// Global maps to store metafields by variant ID and product handle
const firmnessMetafieldsMap = new Map(); // Map<variantId, {text1, text2, text3}>
const foundationMetafieldsMap = new Map(); // Map<variantId, {metafield1, metafield2, metafield3}>
const foundationMetafieldsByProduct = new Map(); // Map<productHandle, Map<variantId, metafields>>
const variantInfoMetafieldsMap = new Map(); // Map<variantId, {title, short, long, feature}>
const variantImageFeaturesMap = new Map(); // Map<variantId, {image, heading, description}>
const variantTechnicalSpecsMap = new Map(); // Map<variantId, {titles, features}>
const variantPerformanceDataMap = new Map(); // Map<variantId, [{performance_heading, performance_heading_value, performance_progress, performance_text}, ...]>

// Helper function to parse rich text JSON and extract text content
function parseRichTextToText(richTextJson) {
  if (!richTextJson) return '';
  
  // If it's already a string, try to parse it
  let parsed;
  if (typeof richTextJson === 'string') {
    // Check if it's Ruby hash format (contains =>)
    if (richTextJson.includes('=>')) {
      // Convert Ruby hash syntax to JSON
      // Replace => with : and handle quotes
      let jsonString = richTextJson
        .replace(/=>/g, ':')
        .replace(/(\w+):/g, '"$1":')  // Add quotes to keys
        .replace(/:\s*"([^"]+)"/g, ': "$1"')  // Ensure values are quoted
        .replace(/:\s*(\d+)/g, ': $1')  // Keep numbers unquoted
        .replace(/:\s*(true|false|null)/g, ': $1');  // Keep booleans/null unquoted
      
      try {
        parsed = JSON.parse(jsonString);
      } catch (e) {
        // If conversion fails, try direct JSON parse
        try {
          parsed = JSON.parse(richTextJson);
        } catch (e2) {
          // If both fail, return as is
          return richTextJson;
        }
      }
    } else {
      // Regular JSON string
      try {
        parsed = JSON.parse(richTextJson);
      } catch (e) {
        // If parsing fails, return as is (might be plain text)
        return richTextJson;
      }
    }
  } else {
    parsed = richTextJson;
  }
  
  // If it's not an object, return as string
  if (typeof parsed !== 'object' || parsed === null) {
    return String(richTextJson);
  }
  
  // Extract text from rich text structure
  function extractText(node) {
    if (!node) return '';
    
    if (node.type === 'text' && node.value) {
      return node.value;
    }
    
    if (node.type === 'list-item' && node.children) {
      const items = node.children.map(extractText).filter(Boolean);
      return items.join(' ');
    }
    
    if (node.children && Array.isArray(node.children)) {
      const texts = node.children.map(extractText).filter(Boolean);
      return texts.join(' ');
    }
    
    return '';
  }
  
  // Extract all list items
  const listItems = [];
  
  function extractListItems(node) {
    if (!node) return;
    
    if (node.type === 'list-item' && node.children) {
      const text = node.children.map(extractText).filter(Boolean).join(' ');
      if (text) {
        listItems.push(text);
      }
    }
    
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(extractListItems);
    }
  }
  
  // Start extraction from root
  if (parsed.type === 'root' && parsed.children) {
    parsed.children.forEach(extractListItems);
  } else {
    extractListItems(parsed);
  }
  
  // Return formatted list (use newlines so firmness-feature-text / meta description can show as bullets and preserve structure)
  if (listItems.length > 0) {
    return listItems.join('\n');
  }
  
  // Fallback: try to extract any text; preserve block breaks by joining block-level children with newline
  function extractTextWithBreaks(node) {
    if (!node) return '';
    if (node.type === 'text' && node.value) return node.value;
    if (node.children && Array.isArray(node.children)) {
      var sep = (node.type === 'paragraph' || node.type === 'list-item' || node.type === 'block') ? '\n' : ' ';
      return node.children.map(extractTextWithBreaks).filter(Boolean).join(sep);
    }
    return '';
  }
  const fallbackText = extractTextWithBreaks(parsed);
  return fallbackText || String(richTextJson);
}

  // Helper function to load variant info data for a specific product handle
  function loadVariantInfoForProduct(productHandle) {
    if (!productHandle) return;
    
    // Find script tag by class and data-product-handle attribute
    const variantInfoScripts = document.querySelectorAll('.variant-info-json');
    
    if (variantInfoScripts && variantInfoScripts.length > 0) {
      // Find script tag matching product handle
      for (let script of variantInfoScripts) {
        if (script.getAttribute('data-product-handle') === productHandle) {
          try {
            const variantInfoData = JSON.parse(script.textContent);
            
            // Parse variant info metafields from JSON (metaobject: title, short, long, feature)
            if (variantInfoData && variantInfoData.variants && Array.isArray(variantInfoData.variants)) {
              variantInfoData.variants.forEach((variantInfo, idx) => {
                if (variantInfo && variantInfo.variantId) {
                  const variantId = variantInfo.variantId.toString();
                  
                  // Helper function to process field values
                  const processField = (fieldValue) => {
                    if (fieldValue === null || fieldValue === undefined || fieldValue === 'null' || fieldValue === 'undefined') {
                      return null;
                    }
                    
                    const str = String(fieldValue).trim();
                    if (str === '' || str === 'null' || str === 'undefined') {
                      return null;
                    }
                    
                    return str;
                  };
                  
                  // For feature, keep as object if it's already an object (rich text JSON)
                  let featureValue = variantInfo.feature;
                  if (featureValue !== null && featureValue !== undefined && featureValue !== 'null' && featureValue !== 'undefined') {
                    // If it's already an object, keep it as is
                    if (typeof featureValue === 'object') {
                      // Already an object, use as is
                    } else if (typeof featureValue === 'string' && featureValue.trim() !== '') {
                      // Try to parse if it's a JSON string
                      try {
                        featureValue = JSON.parse(featureValue);
                      } catch (e) {
                        // If parsing fails, keep as string
                      }
                    } else {
                      featureValue = null;
                    }
                  } else {
                    featureValue = null;
                  }
                  
                  // firmness_feature_text: variant-specific metafield for firmness card
                  let firmnessFeatureValue = variantInfo.firmness_feature_text;
                  if (firmnessFeatureValue !== null && firmnessFeatureValue !== undefined && firmnessFeatureValue !== 'null' && firmnessFeatureValue !== 'undefined') {
                    if (typeof firmnessFeatureValue === 'object') {
                      // keep as is
                    } else if (typeof firmnessFeatureValue === 'string' && firmnessFeatureValue.trim() !== '') {
                      try {
                        firmnessFeatureValue = JSON.parse(firmnessFeatureValue);
                      } catch (e) {
                        // keep as string
                      }
                    } else {
                      firmnessFeatureValue = null;
                    }
                  } else {
                    firmnessFeatureValue = null;
                  }
                  
                  const variantInfoObj = {
                    title: processField(variantInfo.title),
                    short: processField(variantInfo.short),
                    long: processField(variantInfo.long),
                    feature: featureValue,
                    firmness_feature_text: firmnessFeatureValue
                  };
                  
                  // Always store, even if some fields are empty
                  variantInfoMetafieldsMap.set(variantId, variantInfoObj);
                }
              });
            }
            return; // Found and processed, exit
          } catch (e) {
            console.log('error loading variant info: ', e);
          }
        }
      }
    }
  }

  // Function to initialize metafields from data attributes
function initializeMetafieldsFromDataAttributes() {
  // Get all mattress product tabs
  const tabs = document.querySelectorAll('#custom-product-container .tab[data-variant-metafields]');
  // Also check for tabs without the attribute
  const allTabs = document.querySelectorAll('#custom-product-container .tab');
  
  tabs.forEach((tab, tabIndex) => {
    const productHandle = tab.getAttribute('data-product-handle');
    const variantMetafieldsData = tab.getAttribute('data-variant-metafields');
    const foundationMetafieldsData = tab.getAttribute('data-foundation-variant-metafields');
    const variantImageFeaturesData = tab.getAttribute('data-variant-image-features');
    
    // Get variant info from JSON script tag instead of data attribute
    // Find script tag by class and data-product-handle attribute
    let variantInfoData = null;
    const variantInfoScripts = document.querySelectorAll('.variant-info-json');
    
    if (variantInfoScripts && variantInfoScripts.length > 0) {
      // Find script tag matching current product handle
      for (let script of variantInfoScripts) {
        if (script.getAttribute('data-product-handle') === productHandle) {
          try {
            variantInfoData = JSON.parse(script.textContent);
            break; // Found matching script, exit loop
          } catch (e) {
            console.log('error: ', e); // Silent error handling
          }
        }
      }
    }
    
    // Debug the structure by showing what's between each ||
    if (foundationMetafieldsData && foundationMetafieldsData.trim() !== '') {
      const entries = foundationMetafieldsData.split('||');
      entries.forEach((entry, i) => {
        const parts = entry.split(':');
      });
    }
    // Parse firmness metafields
    if (variantMetafieldsData && variantMetafieldsData.trim() !== '') {
      const variantMetafields = variantMetafieldsData.split('||').filter(v => v && v.trim() !== '');
      
      variantMetafields.forEach((variantData, idx) => {
        if (variantData && variantData.trim() !== '') {
          // Firmness format: variantId:field1|field2|field3 (3 fields separated by |)
          const parts = variantData.split(':');
          
          if (parts.length >= 2) {
            const variantId = parts[0].trim();
            const metafields = parts.slice(1).join(':'); // Join in case any field contains ':'
            const fields = metafields.split('|');
            
            // Helper function to decode HTML entities (for rich text metafields)
            // Data attributes automatically escape HTML, so we need to decode them
            const decodeHtml = (html) => {
              if (!html) return null;
              const htmlStr = html.trim();
              if (htmlStr === '' || htmlStr === 'null' || htmlStr === 'undefined') return null;
              
              // Create a temporary element to decode HTML entities
              const txt = document.createElement('textarea');
              txt.innerHTML = htmlStr;
              const decoded = txt.value;
              
              // If decoded value is different, it means there were HTML entities
              // Otherwise, return original (might already be plain text)
              return decoded !== htmlStr ? decoded : htmlStr;
            };
            
            // Store metafields even if some are empty - we'll check individual fields when displaying
            const metafieldObj = {
              firmness_variant_option_text: decodeHtml(fields[0]),
              firmness_variant_option_text_2: decodeHtml(fields[1]),
              firmness_variant_option_text_3: decodeHtml(fields[2])
            };
            
            // Store with variant ID as string (to match lookup) - store even if all are empty for debugging
            firmnessMetafieldsMap.set(variantId, metafieldObj);
            
            // Check if at least one metafield has data
            const hasAnyValue = metafieldObj.firmness_variant_option_text || 
                               metafieldObj.firmness_variant_option_text_2 || 
                               metafieldObj.firmness_variant_option_text_3;
          }
        }
      });
    }
    
    // Parse foundation metafields
    if (foundationMetafieldsData && foundationMetafieldsData.trim() !== '' && productHandle) {
      const variantMetafields = foundationMetafieldsData.split('||').filter(v => v && v.trim() !== '');
      const foundationMap = new Map();
      
      variantMetafields.forEach((variantData, dataIndex) => {
        if (variantData && variantData.trim() !== '') {
          // Foundation format: variantId:field1 (single field, no pipes)
          const parts = variantData.split(':');
          
          if (parts.length >= 2) {
            const variantId = parts[0].trim();
            const metafieldValue = parts.slice(1).join(':').trim(); // Join in case value contains ':'
            
            if (variantId) {
              // Store even if value is empty - we'll use it for mapping
              // The value might be empty, but we still need to map the variant to its position
              const metafieldObj = {
                value: (metafieldValue && metafieldValue !== '' && metafieldValue !== 'null' && metafieldValue !== 'undefined') ? metafieldValue : null,
                position: dataIndex,   // Position in the data (0, 1, 2, ...)
                foundation_product_variant_metafield: (dataIndex === 0) ? ((metafieldValue && metafieldValue !== '' && metafieldValue !== 'null') ? metafieldValue : null) : null,
                foundation_product_variant_metafield_2: (dataIndex === 1) ? ((metafieldValue && metafieldValue !== '' && metafieldValue !== 'null') ? metafieldValue : null) : null,
                foundation_product_variant_metafield_3: (dataIndex === 2) ? ((metafieldValue && metafieldValue !== '' && metafieldValue !== 'null') ? metafieldValue : null) : null
              };
              
              // Store with variant ID as string (to match lookup) - store even if value is empty for mapping
              foundationMap.set(variantId, metafieldObj);
              
              if (metafieldObj.value) {
                const metafieldName = dataIndex === 0 ? 'metafield1' : (dataIndex === 1 ? 'metafield2' : 'metafield3');
              }
            } 
          }
        }
      });
      
      foundationMetafieldsByProduct.set(productHandle, foundationMap);
    }
    
    // Parse variant image and features metafields
    if (variantImageFeaturesData && variantImageFeaturesData.trim() !== '') {
      const variantImageFeatures = variantImageFeaturesData.split('||').filter(v => v && v.trim() !== '');
      
      variantImageFeatures.forEach((variantData) => {
        if (variantData && variantData.trim() !== '') {
          const parts = variantData.split(':');
          
          if (parts.length >= 2) {
            const variantId = parts[0].trim();
            const metafieldJson = parts.slice(1).join(':').trim(); // Join in case JSON contains ':'
            
            if (variantId && metafieldJson && metafieldJson !== '' && metafieldJson !== 'null' && metafieldJson !== 'undefined') {
              try {
                // Decode HTML entities before parsing
                const decodedJson = metafieldJson.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
                const parsedData = JSON.parse(decodedJson);
                // Store with both string and number variant ID for flexibility
                variantImageFeaturesMap.set(variantId, parsedData);
                const variantIdNum = parseInt(variantId);
                if (!isNaN(variantIdNum)) {
                  variantImageFeaturesMap.set(variantIdNum.toString(), parsedData);
                  variantImageFeaturesMap.set(String(variantIdNum), parsedData);
                }
              } catch (e) {
                // Silent error - invalid JSON
              }
            }
          }
        }
      });
    }
    
    // Parse variant technical specifications metafields
    const variantTechnicalSpecsData = tab.getAttribute('data-variant-technical-specs');
    if (variantTechnicalSpecsData && variantTechnicalSpecsData.trim() !== '') {
      const variantTechnicalSpecs = variantTechnicalSpecsData.split('||').filter(v => v && v.trim() !== '');
      
      variantTechnicalSpecs.forEach((variantData) => {
        if (variantData && variantData.trim() !== '') {
          const parts = variantData.split(':');
          
          if (parts.length >= 2) {
            const variantId = parts[0].trim();
            const metafieldJson = parts.slice(1).join(':').trim(); // Join in case JSON contains ':'
            
            if (variantId && metafieldJson && metafieldJson !== '' && metafieldJson !== 'null' && metafieldJson !== 'undefined') {
              try {
                // Decode HTML entities before parsing
                const decodedJson = metafieldJson.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
                const parsedData = JSON.parse(decodedJson);
                // Metafield is list of metaobject: normalize to { titles: [], features: [] }
                let normalized = parsedData;
                if (Array.isArray(parsedData)) {
                  const titlesArr = parsedData.map(item => {
                    let t = item && (item.titles !== undefined ? item.titles : item.title);
                    if (t && typeof t === 'object' && t.value != null) t = t.value;
                    return t != null && t !== 'null' ? String(t).trim() : '';
                  });
                  const featuresArr = parsedData.map(item => {
                    let f = item && (item.features !== undefined ? item.features : item.feature);
                    if (f && typeof f === 'object' && f.value != null) f = f.value;
                    return f != null && f !== 'null' ? String(f).trim() : '';
                  });
                  normalized = { titles: titlesArr, features: featuresArr };
                }
                variantTechnicalSpecsMap.set(variantId, normalized);
                const variantIdNum = parseInt(variantId);
                if (!isNaN(variantIdNum)) {
                  variantTechnicalSpecsMap.set(variantIdNum.toString(), normalized);
                  variantTechnicalSpecsMap.set(String(variantIdNum), normalized);
                }
              } catch (e) {
                // Silent error - invalid JSON
              }
            }
          }
        }
      });
    }
    
    // Parse variant performance data metafields (list of metaobject: performance_heading, performance_heading_value, performance_progress, performance_text)
    const variantPerformanceDataAttr = tab.getAttribute('data-variant-performance-data');
    if (variantPerformanceDataAttr && variantPerformanceDataAttr.trim() !== '') {
      function safePerfStr(v) {
        if (v == null || v === undefined) return '';
        if (typeof v === 'object' && v !== null && 'value' in v) return safePerfStr(v.value);
        return String(v);
      }
      function safePerfNum(v) {
        if (v == null || v === undefined) return 0;
        if (typeof v === 'object' && v !== null && 'value' in v) return safePerfNum(v.value);
        const n = Number(v);
        return isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
      }
      const variantPerformanceEntries = variantPerformanceDataAttr.split('||').filter(v => v && v.trim() !== '');
      variantPerformanceEntries.forEach((variantData) => {
        if (variantData && variantData.trim() !== '') {
          const parts = variantData.split(':');
          if (parts.length >= 2) {
            const variantId = parts[0].trim();
            const metafieldJson = parts.slice(1).join(':').trim();
            if (variantId && metafieldJson && metafieldJson !== '' && metafieldJson !== 'null' && metafieldJson !== 'undefined') {
              try {
                const decodedJson = metafieldJson.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
                const parsedData = JSON.parse(decodedJson);
                let normalized = [];
                if (Array.isArray(parsedData)) {
                  normalized = parsedData.map(item => {
                    return {
                      performance_heading: safePerfStr(item && item.performance_heading),
                      performance_heading_value: safePerfStr(item && item.performance_heading_value),
                      performance_progress: safePerfNum(item && item.performance_progress),
                      performance_text: safePerfStr(item && item.performance_text)
                    };
                  });
                } else if (parsedData && typeof parsedData === 'object') {
                  var prog = parsedData.performance_progress !== undefined && parsedData.performance_progress !== null ? parsedData.performance_progress : (parsedData.progress !== undefined && parsedData.progress !== null ? parsedData.progress : parsedData.progress_value);
                  normalized = [{
                    performance_heading: safePerfStr(parsedData.performance_heading),
                    performance_heading_value: safePerfStr(parsedData.performance_heading_value),
                    performance_progress: prog,
                    performance_text: safePerfStr(parsedData.performance_text)
                  }];
                }
                if (normalized.length > 0) {
                  // If data has comma-separated values in any field, split into one block per value (block 1 = first value, block 2 = second, etc.)
                  const hasCommaInAny = normalized.some(it => {
                    const h = (it.performance_heading || '').toString();
                    const hv = (it.performance_heading_value || '').toString();
                    const t = (it.performance_text || '').toString();
                    const pStr = (it.performance_progress != null ? it.performance_progress : '').toString();
                    return h.indexOf(',') !== -1 || hv.indexOf(',') !== -1 || t.indexOf(',') !== -1 || pStr.indexOf(',') !== -1;
                  });
                  if (hasCommaInAny) {
                    const expanded = [];
                    for (const item of normalized) {
                      const h = (item.performance_heading || '').toString();
                      const hv = (item.performance_heading_value || '').toString();
                      const t = (item.performance_text || '').toString();
                      const headings = h.split(',').map(s => s.trim());
                      const values = hv.split(',').map(s => s.trim());
                      const texts = t.split(',').map(s => s.trim());
                      let progressArr = [];
                      const pStr = (item.performance_progress != null ? item.performance_progress : '').toString();
                      if (pStr.indexOf(',') !== -1) {
                        progressArr = pStr.split(',').map(s => safePerfNum(s.trim()));
                      } else {
                        const singleProgress = safePerfNum(item.performance_progress);
                        const n = Math.max(headings.length, values.length, texts.length, 1);
                        progressArr = Array(n).fill(singleProgress);
                      }
                      const n = Math.max(headings.length, values.length, progressArr.length, texts.length, 1);
                      for (let i = 0; i < n; i++) {
                        expanded.push({
                          performance_heading: headings[i] || '',
                          performance_heading_value: values[i] || '',
                          performance_progress: progressArr[i] !== undefined ? progressArr[i] : 0,
                          performance_text: texts[i] || ''
                        });
                      }
                    }
                    normalized = expanded;
                  }
                }
                if (normalized.length > 0) {
                  variantPerformanceDataMap.set(variantId, normalized);
                  const variantIdNum = parseInt(variantId);
                  if (!isNaN(variantIdNum)) {
                    variantPerformanceDataMap.set(variantIdNum.toString(), normalized);
                    variantPerformanceDataMap.set(String(variantIdNum), normalized);
                  }
                }
              } catch (e) {
                // Silent - invalid JSON
              }
            }
          }
        }
      });
    }
    
    // Parse variant info metafields from JSON (metaobject: title, short, long, feature)
    if (variantInfoData && variantInfoData.variants && Array.isArray(variantInfoData.variants)) {
      variantInfoData.variants.forEach((variantInfo, idx) => {
        if (variantInfo && variantInfo.variantId) {
          const variantId = variantInfo.variantId.toString();
          
          // Helper function to process field values
          const processField = (fieldValue) => {
            if (fieldValue === null || fieldValue === undefined || fieldValue === 'null' || fieldValue === 'undefined') {
              return null;
            }
            
            const str = String(fieldValue).trim();
            if (str === '' || str === 'null' || str === 'undefined') {
              return null;
            }
            
            return str;
          };
          
          // For feature, keep as object if it's already an object (rich text JSON)
          let featureValue = variantInfo.feature;
          if (featureValue !== null && featureValue !== undefined && featureValue !== 'null' && featureValue !== 'undefined') {
            // If it's already an object, keep it as is
            if (typeof featureValue === 'object') {
              // Already an object, use as is
            } else if (typeof featureValue === 'string' && featureValue.trim() !== '') {
              // Try to parse if it's a JSON string
              try {
                featureValue = JSON.parse(featureValue);
              } catch (e) {
                // If parsing fails, keep as string
              }
            } else {
              featureValue = null;
            }
          } else {
            featureValue = null;
          }
          
          // firmness_feature_text: variant-specific metafield for firmness card (same handling as feature)
          let firmnessFeatureValue = variantInfo.firmness_feature_text;
          if (firmnessFeatureValue !== null && firmnessFeatureValue !== undefined && firmnessFeatureValue !== 'null' && firmnessFeatureValue !== 'undefined') {
            if (typeof firmnessFeatureValue === 'object') {
              // keep as is
            } else if (typeof firmnessFeatureValue === 'string' && firmnessFeatureValue.trim() !== '') {
              try {
                firmnessFeatureValue = JSON.parse(firmnessFeatureValue);
              } catch (e) {
                // keep as string
              }
            } else {
              firmnessFeatureValue = null;
            }
          } else {
            firmnessFeatureValue = null;
          }
          
          const variantInfoObj = {
            title: processField(variantInfo.title),
            short: processField(variantInfo.short),
            long: processField(variantInfo.long),
            feature: featureValue,
            firmness_feature_text: firmnessFeatureValue
          };
          
          // Always store, even if some fields are empty
          variantInfoMetafieldsMap.set(variantId, variantInfoObj);
        }
      });
    }
  });

  // Parse variant performance data from ALL tabs (so default + variant change always have data)
  (function parsePerformanceDataFromAllTabs() {
    function safePerfStr(v) {
      if (v == null || v === undefined) return '';
      if (typeof v === 'object' && v !== null && 'value' in v) return safePerfStr(v.value);
      return String(v);
    }
    function safePerfNum(v) {
      if (v == null || v === undefined) return 0;
      if (typeof v === 'object' && v !== null && 'value' in v) return safePerfNum(v.value);
      const n = Number(v);
      return isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
    }
    allTabs.forEach(function(tab) {
      const attr = tab.getAttribute('data-variant-performance-data');
      if (!attr || attr.trim() === '') return;
      const entries = attr.split('||').filter(function(v) { return v && v.trim() !== ''; });
      entries.forEach(function(variantData) {
        if (!variantData || variantData.trim() === '') return;
        const parts = variantData.split(':');
        if (parts.length < 2) return;
        const variantId = parts[0].trim();
        const metafieldJson = parts.slice(1).join(':').trim();
        if (!variantId || !metafieldJson || metafieldJson === '' || metafieldJson === 'null' || metafieldJson === 'undefined') return;
        try {
          const decodedJson = metafieldJson.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
          const parsedData = JSON.parse(decodedJson);
          let normalized = [];
          if (Array.isArray(parsedData)) {
            normalized = parsedData.map(function(item) {
              return {
                performance_heading: safePerfStr(item && item.performance_heading),
                performance_heading_value: safePerfStr(item && item.performance_heading_value),
                performance_progress: safePerfNum(item && item.performance_progress),
                performance_text: safePerfStr(item && item.performance_text)
              };
            });
          } else if (parsedData && typeof parsedData === 'object') {
            var prog = parsedData.performance_progress !== undefined && parsedData.performance_progress !== null ? parsedData.performance_progress : (parsedData.progress !== undefined && parsedData.progress !== null ? parsedData.progress : parsedData.progress_value);
            normalized = [{
              performance_heading: safePerfStr(parsedData.performance_heading),
              performance_heading_value: safePerfStr(parsedData.performance_heading_value),
              performance_progress: prog,
              performance_text: safePerfStr(parsedData.performance_text)
            }];
          }
          if (normalized.length > 0) {
            const hasCommaInAny = normalized.some(function(it) {
              const h = (it.performance_heading || '').toString();
              const hv = (it.performance_heading_value || '').toString();
              const t = (it.performance_text || '').toString();
              const pStr = (it.performance_progress != null ? it.performance_progress : '').toString();
              return h.indexOf(',') !== -1 || hv.indexOf(',') !== -1 || t.indexOf(',') !== -1 || pStr.indexOf(',') !== -1;
            });
            if (hasCommaInAny) {
              const expanded = [];
              for (var idx = 0; idx < normalized.length; idx++) {
                const item = normalized[idx];
                const h = (item.performance_heading || '').toString();
                const hv = (item.performance_heading_value || '').toString();
                const t = (item.performance_text || '').toString();
                const headings = h.split(',').map(function(s) { return s.trim(); });
                const values = hv.split(',').map(function(s) { return s.trim(); });
                const texts = t.split(',').map(function(s) { return s.trim(); });
                var progressArr = [];
                const pStr = (item.performance_progress != null ? item.performance_progress : '').toString();
                if (pStr.indexOf(',') !== -1) {
                  progressArr = pStr.split(',').map(function(s) { return safePerfNum(s.trim()); });
                } else {
                  const singleProgress = safePerfNum(item.performance_progress);
                  const n = Math.max(headings.length, values.length, texts.length, 1);
                  progressArr = Array(n).fill(singleProgress);
                }
                const n = Math.max(headings.length, values.length, progressArr.length, texts.length, 1);
                for (var j = 0; j < n; j++) {
                  expanded.push({
                    performance_heading: headings[j] || '',
                    performance_heading_value: values[j] || '',
                    performance_progress: progressArr[j] !== undefined ? progressArr[j] : 0,
                    performance_text: texts[j] || ''
                  });
                }
              }
              normalized = expanded;
            }
            variantPerformanceDataMap.set(variantId, normalized);
            const variantIdNum = parseInt(variantId, 10);
            if (!isNaN(variantIdNum)) {
              variantPerformanceDataMap.set(variantIdNum.toString(), normalized);
              variantPerformanceDataMap.set(String(variantIdNum), normalized);
            }
          }
        } catch (e) { /* silent */ }
      });
    });
  })();

  // Debug: Show sample of stored metafields
  if (firmnessMetafieldsMap.size > 0) {
    let count = 0;
    for (let [variantId, metafields] of firmnessMetafieldsMap.entries()) {
      if (count < 3) {
        count++;
      } else {
        break;
      }
    }
  }
  
  // Debug: Show foundation metafields
  if (foundationMetafieldsByProduct.size > 0) {
    for (let [productHandle, foundationMap] of foundationMetafieldsByProduct.entries()) {
      let count = 0;
      for (let [variantId, metafields] of foundationMap.entries()) {
        if (count < 2) {
          count++;
        } else {
          break;
        }
      }
    }
  }
  
  // Debug: Show variant info metafields
  if (variantInfoMetafieldsMap.size > 0) {
    let count = 0;
    for (let [variantId, variantInfo] of variantInfoMetafieldsMap.entries()) {
      if (count < 3) {
        count++;
      } else {
        break;
      }
    }
  }
}

document.addEventListener("DOMContentLoaded", function () {
  // Initialize metafields first
  initializeMetafieldsFromDataAttributes();
  // Store current state
  let currentMattressProduct = null;
  let currentFirmnessVariant = null;
  let currentSizeVariant = null;
  let currentFoundationVariant = null;
  let currentFoundationProduct = null;
  let currentFoundationImages = null;
  let currentMattressImages = null;
  let currentFirmnessTitle = '';
  let isFoundationSelected = false;
  /** When foundation has 2 options (Size + Foundation Type), store the selected type so we sync variant by mattress size */
  let selectedFoundationType = null;
  /** When upsell popup is shown, remember buyNow for "continue" action */
  let pendingUpsellBuyNow = false;
  /** Which upsell overlay is open (adjustable or breeze) — used for confirm/continue dataset */
  let activeUpsellOverlay = null;
  /** After foundation upsell, chain state for add-to-cart (foundation upgrade ids + buyNow). */
  let pendingAccessoryChain = null;

  function upsellPopupGetEl(namespace, suffix) {
    return document.getElementById(namespace + '-' + suffix);
  }

  function foundationTriggerMatches(config, foundationType) {
    if (!config || !foundationType) return false;
    const trigger = config.foundationTrigger ? String(config.foundationTrigger).trim().toLowerCase() : '';
    if (!trigger) return false;
    const normalized = String(foundationType).trim().toLowerCase();
    return normalized === trigger || normalized.includes(trigger) || trigger.includes(normalized);
  }
  
  // Store original product data
  
  // Store current active color for title styling
  let currentActiveTab = null;
  
  // Function to update title last word(s) color based on active tab
  function updateTitleLastWordColor() {
    const titleLastWords = document.querySelectorAll('.title-last-word');
    if (!titleLastWords.length) return;
    
    const activeTab = document.querySelector('#custom-product-container .tab.active');
    if (!activeTab) return;
    
    const tabColor = activeTab.getAttribute('data-tab-color');
    if (tabColor) {
      titleLastWords.forEach(function (el) { el.style.color = tabColor; });
      currentActiveTab = activeTab;
      
      // Get tab index (0, 1, 2)
      const tabs = document.querySelectorAll('#custom-product-container .mattress-type-tabs .tab');
      const tabIndex = Array.from(tabs).indexOf(activeTab);
      
      // For tab 2 (index 1), don't apply colors to specific elements - let CSS handle it
      // For tab 1 and 3, apply colors to all pdp-color-enabled
      if (tabIndex !== 1) {
        // Tab 1 or 3 - apply colors to all pdp-color-enabled (background, border, text)
        document.querySelectorAll('.pdp-color-enabled').forEach(el => {
          el.style.backgroundColor = tabColor;
          el.style.borderColor = tabColor;
          // Also apply text color if element has pdp-text-color class
          if (el.classList.contains('pdp-text-color')) {
            el.style.color = tabColor;
          }
        });
        
        // Update all pdp-text-color elements
        document.querySelectorAll('.pdp-text-color').forEach(el => {
          el.style.color = tabColor;
        });
      } else {
        // Tab 2 (index 1) - remove inline styles ONLY from specific elements to let CSS handle colors
        // Target: btn btn-primary pdp-color-enabled and custom-dropdown-selected pdp-color-enabled
        const btnPrimary = document.querySelector('.btn.btn-primary.pdp-color-enabled');
        const dropdownSelected = document.querySelector('.custom-dropdown-selected.pdp-color-enabled');
        
        if (btnPrimary) {
          btnPrimary.style.backgroundColor = '';
          btnPrimary.style.borderColor = '';
          btnPrimary.style.color = '';
          if (!btnPrimary.style.cssText || btnPrimary.style.cssText.trim() === '') {
            btnPrimary.removeAttribute('style');
          }
        }
        
        if (dropdownSelected) {
          dropdownSelected.style.backgroundColor = '';
          dropdownSelected.style.borderColor = '';
          dropdownSelected.style.color = '';
          if (!dropdownSelected.style.cssText || dropdownSelected.style.cssText.trim() === '') {
            dropdownSelected.removeAttribute('style');
          }
        }
        
        // Apply colors to all other pdp-color-enabled elements (not the two specific ones above)
        document.querySelectorAll('.pdp-color-enabled').forEach(el => {
          // Skip the two specific elements
          if (el === btnPrimary || el === dropdownSelected) {
            return;
          }
          el.style.backgroundColor = tabColor;
          el.style.borderColor = tabColor;
          if (el.classList.contains('pdp-text-color')) {
            el.style.color = tabColor;
          }
        });
        
        // Update all pdp-text-color elements
        document.querySelectorAll('.pdp-text-color').forEach(el => {
          // Skip the two specific elements
          if (el === btnPrimary || el === dropdownSelected) {
            return;
          }
          el.style.color = tabColor;
        });
      }
      
      // Always update title last word(s)
      titleLastWords.forEach(function (el) { el.style.color = tabColor; });
      
      // Update all pdp-border-color elements (border)
      document.querySelectorAll('.pdp-border-color').forEach(el => {
        el.style.borderColor = tabColor;
      });
    }
  }
  
  // Function to load and display product images
  function loadProductImages(productImages) {
    const thumbnailContainer = document.getElementById('thumbnail-images-container');
    const mainImage = document.getElementById('main-image');
    const imageLoading = document.getElementById('image-loading');
    
    if (!thumbnailContainer || !mainImage) {
      return;
    }
    
    // Hide loading and show thumbnail container
    if (imageLoading) {
      imageLoading.style.display = 'none';
    }
    
    // Clear existing content
    thumbnailContainer.innerHTML = '';
    
    if (productImages && productImages.length > 0) {
      productImages.forEach((image, index) => {
        const thumbnail = createThumbnail(image, index);
        thumbnailContainer.appendChild(thumbnail);
      });
      
      // Set main image (first image in the array)
      let mainImageUrl = productImages[0].src || productImages[0];
      if (typeof mainImageUrl === 'string') {
        mainImageUrl = mainImageUrl.replace(/_small\.|_medium\.|_large\.|_compact\.|_grande\./g, '_master.');
      }
      mainImage.src = mainImageUrl;
      mainImage.alt = productImages[0].alt || 'Product image';
      mainImage.onerror = function() {
        console.error('Failed to load main image:', mainImageUrl);
        this.src = mainImageUrl; // Fallback to original URL
      };
      
      // Activate first thumbnail
      const firstThumbnail = thumbnailContainer.querySelector('.thumbnail');
      if (firstThumbnail) {
        firstThumbnail.classList.add('active');
      }

      initMobileThumbnailImages();
      
    } else {
      if (imageLoading) {
        imageLoading.textContent = 'No images available';
      }
    }
  }
  
  const MOBILE_THUMBNAIL_MQ = '(max-width: 640px)';
  const MOBILE_THUMBNAIL_VISIBLE_COUNT = 5;

  function initMobileThumbnailImages() {
    const thumbnailContainer = document.getElementById('thumbnail-images-container');
    if (!thumbnailContainer) return;

    const thumbs = thumbnailContainer.querySelectorAll('.thumbnail');
    const isMobile = window.matchMedia(MOBILE_THUMBNAIL_MQ).matches;
    const useSlider = isMobile && thumbs.length > MOBILE_THUMBNAIL_VISIBLE_COUNT;

    thumbnailContainer.classList.toggle('thumbnail-images--slider', useSlider);
    thumbnailContainer.classList.toggle('thumbnail-images--few', isMobile && thumbs.length <= MOBILE_THUMBNAIL_VISIBLE_COUNT);

  }

  let mobileThumbnailResizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(mobileThumbnailResizeTimer);
    mobileThumbnailResizeTimer = setTimeout(initMobileThumbnailImages, 150);
  });

  // Function to create thumbnail element
  function createThumbnail(image, index) {
    const thumbnail = document.createElement('div');
    thumbnail.className = 'thumbnail';
    thumbnail.dataset.index = index;
    
    // Get the proper image URL
    let imageUrl = image.src || image;
    if (typeof imageUrl === 'string') {
      // Convert to master size for main image
      imageUrl = imageUrl.replace(/_small\.|_medium\.|_large\.|_compact\.|_grande\./g, '_master.');
    }
    
    thumbnail.dataset.image = imageUrl;
    
    const img = document.createElement('img');
    // Use medium size for thumbnails
    const thumbUrl = imageUrl.replace('_master.', '_medium.');
    img.src = thumbUrl;
    img.alt = image.alt || 'Product image';
    img.loading = 'lazy';
    img.onerror = function() {
      console.error('Failed to load thumbnail image:', thumbUrl);
      this.src = imageUrl; // Fallback to master
    };
    
    thumbnail.appendChild(img);
    
    // Add click event
    thumbnail.addEventListener('click', function() {
      const allThumbnails = document.querySelectorAll('#custom-product-container .thumbnail');
      allThumbnails.forEach(t => t.classList.remove('active'));
      this.classList.add('active');
      
      const imageUrl = this.getAttribute('data-image');
      const mainImage = document.getElementById('main-image');
      if (imageUrl && mainImage) {
        mainImage.src = imageUrl;
        mainImage.alt = this.querySelector('img').alt;
      }
    });
    
    return thumbnail;
  }
  
  // Helper function to extract alt text from filename or URL path (pattern: 3z-*-*-foundation)
  function extractAltTextFromFilename(src) {
    if (!src) return null;
    
    try {
      // Remove query string first
      const pathOnly = src.split('?')[0];
      const urlParts = pathOnly.split('/');
      const filename = urlParts[urlParts.length - 1];
      
      // Try filename first
      let baseName = filename
        .replace(/\.(webp|jpg|jpeg|png|gif)$/i, '')
        .replace(/_\d+$/, '')
        .replace(/-\d+$/, '');
      
      if (baseName.startsWith('3z') && baseName.includes('-') && baseName.length > 10) {
        return baseName;
      }
      
      // Try any path segment (Shopify sometimes puts name in folder or different segment)
      for (let i = urlParts.length - 1; i >= 0; i--) {
        const segment = urlParts[i].replace(/\.(webp|jpg|jpeg|png|gif)$/i, '').replace(/_\d+$/, '').replace(/-\d+$/, '');
        if (segment.startsWith('3z') && segment.includes('-') && segment.length > 10) {
          return segment;
        }
      }
      
      return null;
    } catch (e) {
      return null;
    }
  }
  
  // Helper function to check if alt text is in new format
  function isNewFormatAltText(alt) {
    if (!alt) return false;
    
    // New format contains pattern like:
    // "3z-firm-breeze-foundation"
    // "3z-max-plush-no-foundation"
    // "3z-max-hybrid-firm-adjustable-foundation"
    // Must start with "3z" and contain firmness + foundation keywords
    const normalized = normalizeTextForMatching(alt);
    
    // Check if it starts with 3z and has the structure
    if (!normalized.startsWith('3z')) return false;
    
    // Check if it contains firmness keywords
    const hasFirmness = /(firm|medium|plush|soft|frim)/i.test(normalized);
    
    // Check if it contains foundation keywords
    const hasFoundation = /(breeze|adjustable|no-foundation|nofoundation)/i.test(normalized);
    
    // Must have both firmness and foundation
    return hasFirmness && hasFoundation;
  }
  
  // Helper function to extract image properties from various structures
  function getImageProperties(image) {
    if (!image) {
      return { src: null, alt: null };
    }
    
    // Handle different image object structures
    let src = null;
    let alt = null;
    
    // Standard Shopify image object (from /products/{handle}.js API)
    if (image.src) {
      src = image.src;
      // Try multiple ways to get alt text
      alt = image.alt || image.alt_text || image.altText || '';
    }
    // Direct URL string
    else if (typeof image === 'string') {
      src = image;
      alt = '';
    }
    // Metafield image with url property
    else if (image.url) {
      src = image.url;
      alt = image.alt || image.alt_text || image.altText || '';
    }
    // Image with image property (nested structure)
    else if (image.image) {
      if (typeof image.image === 'string') {
        src = image.image;
        alt = image.alt || image.alt_text || image.altText || '';
      } else if (image.image.src) {
        src = image.image.src;
        alt = image.image.alt || image.image.alt_text || image.image.altText || image.alt || image.alt_text || image.altText || '';
      } else if (image.image.url) {
        src = image.image.url;
        alt = image.image.alt || image.image.alt_text || image.image.altText || image.alt || image.alt_text || image.altText || '';
      }
    }
    // Media object (Shopify 2.0)
    else if (image.preview_image) {
      if (image.preview_image.src) {
        src = image.preview_image.src;
        alt = image.preview_image.alt || image.preview_image.alt_text || image.preview_image.altText || image.alt || image.alt_text || image.altText || '';
      }
    }
    // Check for alternative property names
    else if (image.featured_image) {
      if (typeof image.featured_image === 'string') {
        src = image.featured_image;
        alt = image.alt || image.alt_text || image.altText || '';
      } else if (image.featured_image.src) {
        src = image.featured_image.src;
        alt = image.featured_image.alt || image.featured_image.alt_text || image.featured_image.altText || image.alt || image.alt_text || image.altText || '';
      }
    }
    
    // If we still don't have src, try to find it in common properties
    if (!src) {
      // Try common Shopify image URL properties
      if (image.original_src) src = image.original_src;
      else if (image.original) src = image.original;
      else if (image.large) src = image.large;
      else if (image.medium) src = image.medium;
      else if (image.small) src = image.small;
    }
    
    // If we still don't have alt, try to find it in any property
    if (!alt && image) {
      // Check all string properties that might contain alt text
      for (const key in image) {
        if (typeof image[key] === 'string' && 
            (key.toLowerCase().includes('alt') || key.toLowerCase().includes('title') || key.toLowerCase().includes('name')) &&
            image[key].length > 0 && 
            image[key].length < 200) { // Reasonable alt text length
          alt = image[key];
          break;
        }
      }
    }
    
    // IMPORTANT: If alt text is in old format, try to extract from filename
    if (src && (!alt || !isNewFormatAltText(alt))) {
      const filenameAlt = extractAltTextFromFilename(src);
      if (filenameAlt && isNewFormatAltText(filenameAlt)) {
        alt = filenameAlt;
      }
    }
    
    return { src, alt };
  }

  
  // Function to extract key product name from variant title
  function extractKeyProductName(variantTitle) {
    if (!variantTitle) return '';
    
    const lowerTitle = variantTitle.toLowerCase();
    
    // Common words to remove
    const removeWords = [
      'foundation', 'base', 'bed', 'frame', 'mattress', 
      'support', 'adjustable', 'standard', 'premium', 'luxury',
      'with', 'without', 'no', 'deluxe', 'basic', 'ergonomic',
      'foundations', 'bases', 'beds', 'frames'
    ];
    
    // Remove common words
    let cleanName = lowerTitle;
    removeWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      cleanName = cleanName.replace(regex, '').trim();
    });
    
    // Remove extra spaces and punctuation
    cleanName = cleanName.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
    
    // If nothing left after cleaning, use the original
    if (cleanName === '' || cleanName.length < 2) {
      cleanName = lowerTitle.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
    }
    
    return cleanName;
  }
  
  // Helper function to normalize text for matching (case insensitive, flexible)
  function normalizeTextForMatching(text) {
    if (!text) return '';
    return text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')           // Replace spaces with hyphens
      .replace(/[^\w-]/g, '')         // Remove special characters except hyphens
      .replace(/-+/g, '-')            // Replace multiple hyphens with single
      .replace(/^-|-$/g, '');          // Remove leading/trailing hyphens
  }
  
  // Helper function to get firmness value from current selection
  function getCurrentFirmnessValue() {
    if (!currentFirmnessVariant || !currentMattressProduct) {
      return null;
    }
    
    const { firmnessIndex } = detectOptionTypes(currentMattressProduct);
    let firmnessValue = null;
    
    if (currentFirmnessVariant.options && currentFirmnessVariant.options[firmnessIndex] !== undefined) {
      firmnessValue = currentFirmnessVariant.options[firmnessIndex];
    } else if (currentFirmnessVariant.option1 && firmnessIndex === 0) {
      firmnessValue = currentFirmnessVariant.option1;
    } else if (currentFirmnessVariant.option2 && firmnessIndex === 1) {
      firmnessValue = currentFirmnessVariant.option2;
    } else if (currentFirmnessVariant.option3 && firmnessIndex === 2) {
      firmnessValue = currentFirmnessVariant.option3;
    }
    
    return firmnessValue;
  }
  
  // Helper function to normalize firmness value (handle special cases like "Pro Firm" -> "firm")
  function normalizeFirmnessValue(firmnessValue) {
    if (!firmnessValue) return '';
    
    let normalized = normalizeTextForMatching(firmnessValue);
    
    // Handle special cases and variations
    if (normalized.includes('pro-firm') || normalized.includes('profirm') || normalized.includes('pro-frim') || normalized.includes('profrim')) {
      normalized = 'firm';
    } else if (normalized.includes('pro-plush') || normalized.includes('proplush')) {
      normalized = 'plush';
    } else if (normalized.includes('firm') || normalized.includes('frim')) {
      // Handle typo "frim" -> "firm"
      normalized = 'firm';
    } else if (normalized.includes('plush')) {
      normalized = 'plush';
    } else if (normalized.includes('medium')) {
      normalized = 'medium';
    }
    
    return normalized;
  }
  
  // Helper function to normalize foundation variant name
  function normalizeFoundationName(foundationTitle) {
    if (!foundationTitle) return '';
    
    let normalized = normalizeTextForMatching(foundationTitle);
    
    // Handle common foundation names
    if (normalized.includes('breeze')) {
      normalized = 'breeze-foundation';
    } else if (normalized.includes('no-foundation') || normalized.includes('nofoundation')) {
      normalized = 'no-foundation';
    } else if (normalized.includes('adjustable')) {
      normalized = 'adjustable-foundation';
    }
    
    return normalized;
  }
  
  // Helper function to determine prefix from mattress product title
  function getMattressPrefix() {
    if (!currentMattressProduct || !currentMattressProduct.title) {
      return '3z';
    }
    
    const title = currentMattressProduct.title.toLowerCase();
    const handle = (currentMattressProduct.handle || '').toLowerCase();
    
    // Check both title and handle for better matching
    const searchText = title + ' ' + handle;
    
    if (searchText.includes('hybrid')) {
      return '3z-max-hybrid';
    } else if (searchText.includes('max')) {
      return '3z-max';
    } else {
      return '3z';
    }
  }
  
  // Function to handle foundation variant image change with ALT TEXT MATCHING
  function handleFoundationVariantImageChange(variantElement, foundationProductData) {
    try {
      // Get foundation variant title (e.g. "Breeze foundation / Twin" or "Breeze foundation")
      let foundationTitle = variantElement.getAttribute('data-variant-title') || '';
      
      // Clean: variant title can be "Foundation Type / Size" (option1 / option2) or "Size / Foundation Type" (old). Use first part as foundation type for alt matching.
      if (foundationTitle.includes('/')) {
        const parts = foundationTitle.split('/').map(p => p.trim());
        // First part = Foundation Type (e.g. "Breeze foundation"), last part = Size (e.g. "Twin"). Alt text matches on foundation type.
        foundationTitle = parts[0] || parts[parts.length - 1] || foundationTitle;
      }
      
      // Get current firmness value
      const firmnessValue = getCurrentFirmnessValue();
      
      if (!firmnessValue) {
        if (currentMattressImages) {
          loadProductImages(currentMattressImages);
          isFoundationSelected = false;
        }
        return;
      }
      
      // If no product data or images, show mattress images as fallback
      if (!foundationProductData || !foundationProductData.images || foundationProductData.images.length === 0) {
        if (currentMattressImages) {
          loadProductImages(currentMattressImages);
          isFoundationSelected = false;
        }
        return;
      }
      
      
      // Normalize values for matching
      const normalizedFirmness = normalizeFirmnessValue(firmnessValue);
      const normalizedFoundation = normalizeFoundationName(foundationTitle);
      const prefix = getMattressPrefix();
      
      const expectedAltText = `${prefix}-${normalizedFirmness}-${normalizedFoundation}`;
      
      // Filter images by matching alt text (case insensitive, flexible)
      const matchedImages = [];
      
      foundationProductData.images.forEach((img, index) => {
        const { src, alt } = getImageProperties(img);
        
        if (!src) {
          return;
        }
        
        // Use alt for matching; if alt is generic ("product image", empty) try filename (structure: 3z-max-medium-breeze-foundation)
        // So one variant's images with alt "product image" but correct filename still match
        let altForMatch = (alt || '').trim();
        if (!altForMatch || !isNewFormatAltText(altForMatch)) {
          const fromFilename = extractAltTextFromFilename(src);
          if (fromFilename) altForMatch = fromFilename;
        }
        const normalizedAlt = normalizeTextForMatching(altForMatch || '');
        
        // Check if alt text matches expected pattern (strict: image alt must contain full expected pattern, not the other way around)
        // Do NOT use expectedAltText.includes(normalizedAlt) - that would match short/generic alts like "3z-max" and show wrong images
        const altMatches = normalizedAlt === expectedAltText || 
                          normalizedAlt.includes(expectedAltText);
        
        // Also check if alt text contains both firmness and foundation keywords (flexible matching)
        const containsFirmness = normalizedAlt.includes(normalizedFirmness) || 
                                 (normalizedFirmness === 'firm' && (normalizedAlt.includes('firm') || normalizedAlt.includes('frim')));
        
        // Check for foundation keywords (handle variations)
        const containsFoundation = normalizedAlt.includes(normalizedFoundation) || 
                                  (normalizedFoundation === 'breeze-foundation' && normalizedAlt.includes('breeze')) ||
                                  (normalizedFoundation === 'no-foundation' && (normalizedAlt.includes('no-foundation') || normalizedAlt.includes('nofoundation'))) ||
                                  (normalizedFoundation === 'adjustable-foundation' && (normalizedAlt.includes('adjustable') || normalizedAlt.includes('adjustable-foundation') || normalizedAlt.includes('adjustable_foundation')));
        
        // Strict prefix check so second product (3z-max) does NOT match 3z or 3z-max-hybrid images
        // Image alt must clearly belong to current mattress type only
        let containsPrefix = false;
        if (prefix === '3z-max-hybrid') {
          containsPrefix = normalizedAlt.startsWith('3z-max-hybrid-') || normalizedAlt === '3z-max-hybrid';
        } else if (prefix === '3z-max') {
          containsPrefix = (normalizedAlt.startsWith('3z-max-') && normalizedAlt.indexOf('3z-max-hybrid') !== 0) || normalizedAlt === '3z-max';
        } else {
          // prefix === '3z' - must not match 3z-max or 3z-max-hybrid
          containsPrefix = (normalizedAlt.startsWith('3z-') && !normalizedAlt.startsWith('3z-max')) || normalizedAlt === '3z';
        }
        
        // Match if: exact match OR (contains firmness AND foundation AND strict prefix)
         const firmExcluded = (normalizedFirmness !== 'firm') && normalizedAlt.includes('firm');

        // Match if: exact match OR (contains firmness AND foundation AND strict prefix), but never if firm-excluded
        if (altMatches || (containsFirmness && containsFoundation && containsPrefix && !firmExcluded)) {
          matchedImages.push({
            src: src,
            alt: alt || foundationTitle || 'Foundation Image'
          });
        }
      });
      
      // If we found matching images, display them
      if (matchedImages.length > 0) {
        loadProductImages(matchedImages);
        isFoundationSelected = true;
      } else {
        // Fallback to mattress images if no match found
        if (currentMattressImages) {
          loadProductImages(currentMattressImages);
          isFoundationSelected = false;
        }
      }
      
    } catch (error) {
      console.error('❌ Error handling foundation variant images:', error);
      if (currentMattressImages) {
        loadProductImages(currentMattressImages);
      }
      isFoundationSelected = false;
    }
  }
  
  // Function to fetch variant metafields for a product
  // NOTE: This function now only uses data attributes - API calls removed due to 403 errors
  async function fetchVariantMetafields(productData) {
    // Metafields are already merged from data attributes via mergeVariantMetafields()
    // This function is kept for compatibility but does nothing
    return productData;
  }
  
  // Function to fetch foundation variant metafields
  // NOTE: This function now only uses data attributes - API calls removed due to 403 errors
  async function fetchFoundationVariantMetafields(foundationProduct) {
    // Metafields are already merged from data attributes via mergeFoundationMetafields()
    // This function is kept for compatibility but does nothing
    return foundationProduct;
  }

  // Function to get foundation product from metafield
  async function getFoundationProductFromMetafield(productHandle) {
    try {
      // Try to get product with metafields using Shopify's AJAX API
      const response = await fetch(`/products/${productHandle}?view=json-metafields`);
      
      if (response.ok) {
        const html = await response.text();
        
        // Try to parse as JSON first
        try {
          const productData = JSON.parse(html);
          
          // Check for foundation_product in metafields
          if (productData.metafields && productData.metafields.custom && productData.metafields.custom.foundation_product) {
            const foundationHandle = productData.metafields.custom.foundation_product;
            return foundationHandle;
          }
          
          // Check in global namespace
          if (productData.metafields && productData.metafields.global && productData.metafields.global.foundation_product) {
            const foundationHandle = productData.metafields.global.foundation_product;
            return foundationHandle;
          }
          
          // Check direct property
          if (productData.metafields && productData.metafields.foundation_product) {
            const foundationHandle = productData.metafields.foundation_product;
            return foundationHandle;
          }
          
        } catch (e) {
          console.log('Response is not JSON, trying to parse as HTML');
        }
        
        // Look for metafield in data attributes or script tags
        const dataAttrRegex = /data-foundation-product=["']([^"']+)["']/i;
        const dataMatch = html.match(dataAttrRegex);
        if (dataMatch && dataMatch[1]) {
          return dataMatch[1];
        }
        
      }
      
      // Try to get product JSON directly
      const jsonResponse = await fetch(`/products/${productHandle}.js`);
      if (jsonResponse.ok) {
        const productData = await jsonResponse.json();
        
        // Check if product has metafields property
        if (productData.metafields) {
          
          // Check custom namespace
          if (productData.metafields.custom && productData.metafields.custom.foundation_product) {
            return productData.metafields.custom.foundation_product;
          }
          
          // Check global namespace
          if (productData.metafields.global && productData.metafields.global.foundation_product) {
            return productData.metafields.global.foundation_product;
          }
          
          // Check direct property
          if (productData.metafields.foundation_product) {
            return productData.metafields.foundation_product;
          }
        }
        
      }
      
      return null;
      
    } catch (error) {
      console.error('Error fetching foundation metafield:', error);
      return null;
    }
  }
  
  // Function to load foundation options for a mattress product
  async function loadFoundationOptions(mattressProductHandle) {
    const foundationContainer = document.getElementById('foundation-options-container');
    
    if (!foundationContainer) {
      return;
    }
    
    // Show loading state
    foundationContainer.innerHTML = '<p class="no-foundation-options">Loading foundation options...</p>';
    
    try {
      // METHOD 1: Try to get foundation product from active tab data attribute FIRST
      const activeTab = document.querySelector('#custom-product-container .tab.active');
      let foundationProductHandle = null;
      
      if (activeTab) {
        const tabFoundationProduct = activeTab.getAttribute('data-foundation-product');
        if (tabFoundationProduct && tabFoundationProduct !== '' && tabFoundationProduct !== 'null') {
          foundationProductHandle = tabFoundationProduct;
        }
      }
      
      // METHOD 2: If not in tab data, try metafield
      if (!foundationProductHandle) {
        foundationProductHandle = await getFoundationProductFromMetafield(mattressProductHandle);
      }
      
      // METHOD 3: Fallback to section settings
      if (!foundationProductHandle || foundationProductHandle === '' || foundationProductHandle === 'null') {
        // eslint-disable-next-line no-unused-vars -- Liquid replaced at build/render
        const defaultFoundationProduct = typeof window.__SECTION_FOUNDATION_PRODUCT__ !== 'undefined' ? window.__SECTION_FOUNDATION_PRODUCT__ : null;
        if (defaultFoundationProduct && defaultFoundationProduct !== '') {
          foundationProductHandle = defaultFoundationProduct;
        }
      }
      
      // If we found a foundation product, load it
      if (foundationProductHandle && foundationProductHandle !== '' && foundationProductHandle !== 'null') {
        await loadFoundationProduct(foundationProductHandle);
      } else {
        foundationContainer.innerHTML = '<p class="no-foundation-options">No foundation options available for this mattress.</p>';
      }
      
    } catch (error) {
      console.error('💥 Error loading foundation options:', error);
      foundationContainer.innerHTML = '<p class="no-foundation-options">Unable to load foundation options.</p>';
    }
  }
  
  // Function to merge foundation metafields from data attributes
  function mergeFoundationMetafields(foundationProduct) {
    // Get active tab to find foundation metafields
    const activeTab = document.querySelector('#custom-product-container .tab.active');
    if (!activeTab || !foundationProduct || !foundationProduct.variants) return foundationProduct;
    
    // Get foundation metafields from data attribute
    const foundationMetafieldsData = activeTab.getAttribute('data-foundation-variant-metafields');
    
    if (foundationMetafieldsData) {
      // Parse metafields data: format is "variantId:field1|field2|field3||variantId2:..."
      const variantMetafields = foundationMetafieldsData.split('||');
      
      // Create a map of variant ID to its index in the foundation product
      const variantIndexMap = {};
      foundationProduct.variants.forEach((v, index) => {
        variantIndexMap[v.id.toString()] = index;
      });
      
      variantMetafields.forEach((variantData) => {
        if (variantData) {
          const [variantId, metafields] = variantData.split(':');
          if (variantId && metafields) {
            const fields = metafields.split('|');
            const variant = foundationProduct.variants.find(v => v.id.toString() === variantId);
            const variantIndex = variantIndexMap[variantId];
            
            if (variant && variantIndex !== undefined) {
              if (!variant.metafields) variant.metafields = {};
              if (!variant.metafields.custom) variant.metafields.custom = {};
              
              // Set only the metafield that corresponds to this variant's index (0, 1, 2)
              // Variant at index 0 gets foundation_product_variant_metafield
              // Variant at index 1 gets foundation_product_variant_metafield_2
              // Variant at index 2 gets foundation_product_variant_metafield_3
              if (variantIndex === 0 && fields[0] && fields[0].trim() !== '') {
                variant.metafields.custom.foundation_product_variant_metafield = fields[0];
              } else if (variantIndex === 1 && fields[1] && fields[1].trim() !== '') {
                variant.metafields.custom.foundation_product_variant_metafield_2 = fields[1];
              } else if (variantIndex === 2 && fields[2] && fields[2].trim() !== '') {
                variant.metafields.custom.foundation_product_variant_metafield_3 = fields[2];
              } else {
                // Fallback: try to find any non-empty metafield for this variant
                const availableField = fields.find(f => f && f.trim() !== '');
                if (availableField) {
                  if (variantIndex === 0) {
                    variant.metafields.custom.foundation_product_variant_metafield = availableField;
                  } else if (variantIndex === 1) {
                    variant.metafields.custom.foundation_product_variant_metafield_2 = availableField;
                  } else if (variantIndex === 2) {
                    variant.metafields.custom.foundation_product_variant_metafield_3 = availableField;
                  }
                }
              }
            }
          }
        }
      });
    }
    
    return foundationProduct;
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  function escapeHtmlText(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function isHideFirstFoundationOptionEnabled() {
    const section = document.getElementById('foundation-section');
    return section && section.dataset.hideFirstFoundationOption === 'true';
  }

  function isPurchasableFoundationInput(input) {
    if (!input || input.disabled) return false;
    const optionEl = input.closest('.foundation-option');
    if (!optionEl) return false;
    if (optionEl.classList.contains('size-unavailable') || optionEl.getAttribute('data-size-mismatch') === 'true') {
      return false;
    }
    const variantId = String(input.value || input.getAttribute('data-variant-id') || '').trim();
    if (!variantId) return false;

    const isAvailable = input.getAttribute('data-variant-available') === 'true';
    const inventoryPolicy = input.getAttribute('data-variant-inventory-policy');
    const inventoryQuantity = input.getAttribute('data-variant-inventory');
    const inventoryManagement = input.getAttribute('data-variant-inventory-management');

    if (isAvailable) return true;
    if (inventoryPolicy === 'continue') return true;
    if (inventoryQuantity && parseInt(inventoryQuantity, 10) > 0) return true;
    if (inventoryManagement === 'null' || inventoryManagement === null || inventoryManagement === '') return true;
    return false;
  }

  function clearFoundationSelectionState() {
    const foundationContainer = document.getElementById('foundation-options-container');
    if (foundationContainer) {
      foundationContainer.querySelectorAll('.foundation-option').forEach(function (o) {
        o.classList.remove('selected');
        const t = o.querySelector('.foundation-title');
        const p = o.querySelector('.foundation-price');
        if (t) t.classList.remove('pdp-text-color');
        if (p) p.classList.remove('pdp-text-color');
        const input = o.querySelector('input[name="foundation"]');
        if (input) input.checked = false;
      });
    }
    selectedFoundationType = null;
    currentFoundationVariant = null;
    isFoundationSelected = false;
  }

  /** Hide first foundation card on storefront; keep it checked (e.g. No foundation). Visible options start unselected. */
  function applyHiddenFirstFoundationOptionMode() {
    if (!isHideFirstFoundationOptionEnabled()) return false;

    const foundationContainer = document.getElementById('foundation-options-container');
    if (!foundationContainer) return false;

    const options = foundationContainer.querySelectorAll('.foundation-option');
    if (!options.length) return false;

    const firstOption = options[0];

    options.forEach(function (opt, idx) {
      opt.classList.remove('selected');
      const title = opt.querySelector('.foundation-title');
      const price = opt.querySelector('.foundation-price');
      if (title) title.classList.remove('pdp-text-color');
      if (price) price.classList.remove('pdp-text-color');

      const input = opt.querySelector('input[name="foundation"]');
      if (input) input.checked = false;

      if (idx === 0) {
        opt.classList.add('foundation-option--storefront-hidden');
        opt.setAttribute('aria-hidden', 'true');
      } else {
        opt.classList.remove('foundation-option--storefront-hidden');
        opt.removeAttribute('aria-hidden');
      }
    });

    firstOption.classList.add('selected');
    const firstInput = firstOption.querySelector('input[name="foundation"]');
    const canSelectFirst = isPurchasableFoundationInput(firstInput);

    if (canSelectFirst && firstInput) {
      firstInput.checked = true;
    } else if (firstInput) {
      firstInput.checked = false;
      firstOption.classList.remove('selected');
    }

    const foundationType = firstOption.getAttribute('data-foundation-type') || '';
    if (foundationType) selectedFoundationType = foundationType;

    const variantId = firstInput ? firstInput.value : '';
    const variantPrice = parseInt(firstOption.getAttribute('data-variant-price'), 10) || 0;
    const isAvailable = firstOption.getAttribute('data-variant-available') === 'true';

    if (canSelectFirst && variantId && variantPrice > 0 && isAvailable) {
      currentFoundationVariant = {
        id: variantId,
        title: firstOption.getAttribute('data-variant-title'),
        price: variantPrice,
        available: true,
        inventoryPolicy: firstOption.getAttribute('data-variant-inventory-policy'),
        inventoryQuantity: firstOption.getAttribute('data-variant-inventory'),
        inventoryManagement: firstOption.getAttribute('data-variant-inventory-management')
      };
      isFoundationSelected = true;
    } else {
      currentFoundationVariant = variantId
        ? {
            id: variantId,
            title: firstOption.getAttribute('data-variant-title'),
            price: variantPrice,
            available: isAvailable,
            inventoryPolicy: firstOption.getAttribute('data-variant-inventory-policy'),
            inventoryQuantity: firstOption.getAttribute('data-variant-inventory'),
            inventoryManagement: firstOption.getAttribute('data-variant-inventory-management')
          }
        : null;
      isFoundationSelected = !!(variantId && isAvailable);
    }

    if (currentFoundationProduct && firstOption) {
      const foundationProduct = currentFoundationProduct;
      if (!foundationProduct.handle) {
        const productHandle = firstOption.getAttribute('data-product-handle');
        if (productHandle) {
          foundationProduct.handle = productHandle;
        }
      }
      handleFoundationVariantImageChange(firstOption, foundationProduct);
    } else if (currentMattressImages) {
      loadProductImages(currentMattressImages);
    }

    updateTotalPrice();
    updateVariantNameDisplay();
    updateButtonStates();
    return true;
  }

// Function to load foundation product - UPDATED with flexible image matching
async function loadFoundationProduct(foundationProductHandle) {
  const foundationContainer = document.getElementById('foundation-options-container');
  
  if (!foundationContainer) return;
  
  selectedFoundationType = null;
  
  try {
    // Fetch foundation product data
    const response = await fetch(`/products/${foundationProductHandle}.js`);
    if (!response.ok) {
      throw new Error(`Failed to fetch foundation product: ${response.status}`);
    }
    
    let foundationProduct = await response.json();
    
    // First try to merge from data attributes (faster, more reliable)
    foundationProduct = mergeFoundationMetafields(foundationProduct);
    
    // Then try to fetch from API as fallback
    foundationProduct = await fetchFoundationVariantMetafields(foundationProduct);
    
    // Ensure handle is set
    if (!foundationProduct.handle) {
      foundationProduct.handle = foundationProductHandle;
    }
    currentFoundationProduct = foundationProduct;
    
    // Debug: Log image info with alt text - FULL STRUCTURE
    if (foundationProduct.images && foundationProduct.images.length > 0) {
      foundationProduct.images.slice(0, 3).forEach((img, i) => {
        const extracted = getImageProperties(img);
      });
    }
    
    if (!foundationProduct.variants || foundationProduct.variants.length === 0) {
      foundationContainer.innerHTML = '<p class="no-foundation-options">No foundation variants available.</p>';
      if (typeof window.rebuildCustomDropdown === 'function') {
        window.rebuildCustomDropdown();
      }
      return;
    }
    
    // Ensure firmness header title is set correctly
    const firmnessTitleElement = document.querySelector('#firmness-section .section-title');
    if (firmnessTitleElement) {
      firmnessTitleElement.textContent = 'Choose your comfort preference.';
      currentFirmnessTitle = 'Choose your comfort preference.';
    }
    
    let foundationHTML = '';
    let hasAvailableVariant = false;
    let firstAvailableVariantIndex = -1;
    
    // Get the active tab to find which mattress product's foundation metafields to use
    const activeTab = document.querySelector('#custom-product-container .tab.active');
    const mattressProductHandle = activeTab ? activeTab.getAttribute('data-product-handle') : null;
    
    // Foundation product with 2 options: Size (hidden) + Foundation Type. Show one option per Foundation Type; size syncs with mattress size.
    const hasTwoOptions = foundationProduct.options && foundationProduct.options.length >= 2;
    // Foundation product with only size variants: hide size, show 3 "foundation type" options (Breeze etc.); variant = mattress size.
    const hasOneOption = foundationProduct.options && foundationProduct.options.length === 1;
    const currentSizeValue = getCurrentMattressSizeValue();
    
    if (hasOneOption) {
      // Size-only foundation: show 3 foundation type options (labels from tab metafields); actual variant = by mattress size
      const metafieldsRaw = activeTab ? activeTab.getAttribute('data-foundation-variant-metafields') : '';
      const typeLabels = [];
      const subtitleMetafieldsOne = [];
      if (metafieldsRaw) {
        const entries = metafieldsRaw.split('||').filter(Boolean);
        for (let i = 0; i < 3 && i < entries.length; i++) {
          const part = entries[i].split(':');
          const label = (part[1] != null ? part[1] : '').trim();
          if (label) typeLabels.push(label);
          subtitleMetafieldsOne.push((part[1] != null ? part[1] : '').toString().trim());
        }
      }
      if (typeLabels.length === 0) {
        typeLabels.push('Foundation 1', 'Foundation 2', 'Foundation 3');
      }
      const resolvedVariant = getFoundationVariantBySize(foundationProduct, currentSizeValue);
      const displayVariant = resolvedVariant || null;
      typeLabels.forEach(function (label, typeIndex) {
        const variant = resolvedVariant;
        const sizeMismatch = !variant;
        if (sizeMismatch) {
          let mismatchSubtitle = (subtitleMetafieldsOne[typeIndex] != null ? subtitleMetafieldsOne[typeIndex] : '').toString().trim();
          if (!mismatchSubtitle && metafieldsRaw && mattressProductHandle && foundationMetafieldsByProduct) {
            const entries = metafieldsRaw.split('||').filter(Boolean);
            if (entries[typeIndex]) {
              const variantId = entries[typeIndex].split(':')[0];
              if (variantId) {
                const foundationMap = foundationMetafieldsByProduct.get(mattressProductHandle);
                const mf = foundationMap ? foundationMap.get(variantId.trim()) : null;
                if (mf) {
                  mismatchSubtitle = (mf.value && mf.value.trim() !== '') ? mf.value.trim() : (mf.foundation_product_variant_metafield || mf.foundation_product_variant_metafield_2 || mf.foundation_product_variant_metafield_3 || '');
                }
              }
            }
          }
          if (!mismatchSubtitle) {
            const fallbackV = foundationProduct.variants[typeIndex] || foundationProduct.variants[0];
            mismatchSubtitle = fallbackV ? (fallbackV.title || '') : '';
          }
          const badgeHtml = '<span class="out-of-stock-badge foundation-size-na-badge">Not Available</span>';
          foundationHTML += `
          <label class="foundation-option foundation-option-size-synced size-unavailable out-of-stock" 
                 data-foundation-type="${(label || '').replace(/"/g, '&quot;')}"
                 data-foundation-subtitle-default="${escapeAttr(mismatchSubtitle)}"
                 data-variant-id=""
                 data-variant-title="${(label || 'Foundation').replace(/"/g, '&quot;')}"
                 data-variant-price="0"
                 data-variant-compare-price=""
                 data-variant-available="false"
                 data-size-mismatch="true"
                 data-variant-inventory="0"
                 data-variant-inventory-policy="deny"
                 data-variant-inventory-management="shopify"
                 data-variant-image=""
                 data-variant-alt="${(label || '') + ' - ' + (foundationProduct.title || '')}"
                 data-product-handle="${foundationProduct.handle}"
                 data-product-title="${foundationProduct.title}">
            ${badgeHtml}
            <input type="radio" name="foundation" value=""
                   data-foundation-type="${(label || '').replace(/"/g, '&quot;')}"
                   data-variant-id="" data-product-id="${foundationProduct.id}" data-product-handle="${foundationProduct.handle}" data-product-title="${(foundationProduct.title || '').replace(/"/g, '&quot;')}"
                   data-price="0" data-variant-title="${(label || 'Foundation').replace(/"/g, '&quot;')}"
                   data-variant-available="false" data-variant-inventory="0"
                   data-variant-inventory-policy="deny" data-variant-inventory-management="shopify"
                   disabled class="foundation-input">
            <span class="foundation-indicator"></span>
            <div class="foundation-content">
              <p class="foundation-title">${(label || 'Foundation').replace(/</g, '&lt;')}</p>
              <p class="foundation-subtitle" style="color: #666;">
                ${escapeHtmlText(mismatchSubtitle)}
              </p>
            </div>
            <p class="foundation-price">—</p>
          </label>
        `;
          return;
        }
        const variantId = variant.id.toString();
        // Option 1 → foundation_product_variant_metafield, option 2 → _2, option 3 → _3
        let foundationMetafieldText = (subtitleMetafieldsOne[typeIndex] != null ? subtitleMetafieldsOne[typeIndex] : '').toString().trim();
        if (!foundationMetafieldText && mattressProductHandle && foundationMetafieldsByProduct) {
          const foundationMap = foundationMetafieldsByProduct.get(mattressProductHandle);
          if (foundationMap) {
            const metafields = foundationMap.get(variantId);
            if (metafields) {
              const v = (metafields.value && metafields.value.trim() !== '') ? metafields.value.trim() : (metafields.foundation_product_variant_metafield || metafields.foundation_product_variant_metafield_2 || metafields.foundation_product_variant_metafield_3 || '');
              if (v) foundationMetafieldText = v;
            }
          }
        }
        let isAvailable = false;
        if (variant.available === true) isAvailable = true;
        else if (variant.inventory_policy === 'continue') isAvailable = true;
        else if (variant.inventory_quantity != null && variant.inventory_quantity > 0) isAvailable = true;
        else if (variant.inventory_management == null && variant.available !== false) isAvailable = true;
        if (isAvailable && firstAvailableVariantIndex === -1) { firstAvailableVariantIndex = typeIndex; hasAvailableVariant = true; }
        const hasFeaturedImage = (displayVariant && displayVariant.featured_image && displayVariant.featured_image.src) || (variant.featured_image && variant.featured_image.src);
        const isFirstAvailable = typeIndex === firstAvailableVariantIndex && isAvailable;
        foundationHTML += `
          <label class="foundation-option foundation-option-size-synced${isFirstAvailable ? ' selected' : ''}${!isAvailable ? ' out-of-stock' : ''}" 
                 data-foundation-type="${(label || '').replace(/"/g, '&quot;')}"
                 data-foundation-subtitle-default="${escapeAttr(foundationMetafieldText)}"
                 data-variant-id="${variant.id}" 
                 data-variant-title="${(label || variant.title || 'Default Title').replace(/"/g, '&quot;')}"
                 data-variant-price="${variant.price || 0}"
                 data-variant-compare-price="${variant.compare_at_price || ''}"
                 data-variant-available="${isAvailable}"
                 data-variant-inventory="${variant.inventory_quantity || 0}"
                 data-variant-inventory-policy="${variant.inventory_policy || 'deny'}"
                 data-variant-inventory-management="${variant.inventory_management || 'shopify'}"
                 data-variant-image="${hasFeaturedImage ? (variant.featured_image && variant.featured_image.src ? variant.featured_image.src : (displayVariant && displayVariant.featured_image && displayVariant.featured_image.src) || '') : ''}"
                 data-variant-alt="${(label || '') + ' - ' + (foundationProduct.title || '')}"
                 data-product-handle="${foundationProduct.handle}"
                 data-product-title="${foundationProduct.title}">
            ${!isAvailable ? '<span class="out-of-stock-badge">Out of Stock</span>' : ''}
            <input type="radio" name="foundation" value="${variant.id}" 
                   data-foundation-type="${(label || '').replace(/"/g, '&quot;')}"
                   data-variant-id="${variant.id}" data-product-id="${foundationProduct.id}" data-product-handle="${foundationProduct.handle}" data-product-title="${(foundationProduct.title || '').replace(/"/g, '&quot;')}"
                   data-price="${variant.price || 0}" data-variant-title="${(label || variant.title || 'Default Title').replace(/"/g, '&quot;')}"
                   data-variant-available="${isAvailable}" data-variant-inventory="${variant.inventory_quantity || 0}"
                   data-variant-inventory-policy="${variant.inventory_policy || 'deny'}" data-variant-inventory-management="${variant.inventory_management || 'shopify'}"
                   ${isFirstAvailable ? 'checked' : ''} class="foundation-input" ${!isAvailable ? 'disabled' : ''}>
            <span class="foundation-indicator"></span>
            <div class="foundation-content">
              <p class="foundation-title ${isFirstAvailable ? 'pdp-text-color' : ''}">${(label || 'Foundation').replace(/</g, '&lt;')}</p>
              <p class="foundation-subtitle" style="color: ${isAvailable ? '#666' : '#ff4444'};">
                ${(isAvailable ? foundationMetafieldText : 'Out of Stock').replace(/</g, '&lt;')}
              </p>
            </div>
            <p class="foundation-price ${isFirstAvailable ? 'pdp-text-color' : ''}">${(variant.price && variant.price > 0) ? '+' + formatMoney(variant.price) : formatMoney(variant.price || 0)}</p>
          </label>
        `;
      });
    } else if (hasTwoOptions) {
      // Option1 = Foundation Type (No foundation, Breeze foundation, Adjustable foundation), Option2 = Size. Show only Foundation Type; size syncs with mattress.
      // Subtitle: option 1 → foundation_product_variant_metafield, option 2 → _2, option 3 → _3 (from tab data-foundation-variant-metafields: first 3 variant values)
      const foundationVariantMetafieldsRaw = activeTab ? activeTab.getAttribute('data-foundation-variant-metafields') : '';
      const subtitleMetafields = [];
      if (foundationVariantMetafieldsRaw) {
        const entries = foundationVariantMetafieldsRaw.split('||').filter(Boolean);
        for (let i = 0; i < 3 && i < entries.length; i++) {
          const part = entries[i].split(':');
          const val = (part[1] != null ? part[1] : '').toString().trim();
          subtitleMetafields.push(val);
        }
      }
      const foundationTypes = [...new Set(foundationProduct.variants.map(function (v) {
        return (v.option1 || (v.options && v.options[0]) || '').toString().trim();
      }).filter(Boolean))];
      foundationTypes.forEach(function (foundationType, typeIndex) {
        const variant = getFoundationVariantBySizeAndType(foundationProduct, currentSizeValue, foundationType);
        const sizeMismatch = !variant;
        if (sizeMismatch) {
          let mismatchSubtitle = (subtitleMetafields[typeIndex] != null ? subtitleMetafields[typeIndex] : '').toString().trim();
          const anyForType = getFirstVariantForFoundationType(foundationProduct, foundationType);
          if (!mismatchSubtitle && anyForType && mattressProductHandle && foundationMetafieldsByProduct) {
            const foundationMap = foundationMetafieldsByProduct.get(mattressProductHandle);
            const mf = foundationMap ? foundationMap.get(anyForType.id.toString()) : null;
            if (mf) {
              mismatchSubtitle = (mf.value && mf.value.trim() !== '') ? mf.value.trim() : (mf.foundation_product_variant_metafield || mf.foundation_product_variant_metafield_2 || mf.foundation_product_variant_metafield_3 || '');
            }
          }
          if (!mismatchSubtitle && anyForType) {
            mismatchSubtitle = anyForType.title || '';
          }
          const badgeHtml = '<span class="out-of-stock-badge foundation-size-na-badge">Not Available</span>';
          foundationHTML += `
          <label class="foundation-option size-unavailable out-of-stock" 
                 data-foundation-type="${(foundationType || '').replace(/"/g, '&quot;')}"
                 data-foundation-subtitle-default="${escapeAttr(mismatchSubtitle)}"
                 data-variant-id=""
                 data-variant-title="${(foundationType || 'Default Title').replace(/"/g, '&quot;')}"
                 data-variant-price="0"
                 data-variant-compare-price=""
                 data-variant-available="false"
                 data-size-mismatch="true"
                 data-variant-inventory="0"
                 data-variant-inventory-policy="deny"
                 data-variant-inventory-management="shopify"
                 data-variant-image=""
                 data-variant-alt="${(foundationType || '') + ' - ' + (foundationProduct.title || '')}"
                 data-product-handle="${foundationProduct.handle}"
                 data-product-title="${foundationProduct.title}">
            ${badgeHtml}
            <input type="radio" name="foundation" value=""
                   data-foundation-type="${(foundationType || '').replace(/"/g, '&quot;')}"
                   data-variant-id="" data-product-id="${foundationProduct.id}" data-product-handle="${foundationProduct.handle}" data-product-title="${(foundationProduct.title || '').replace(/"/g, '&quot;')}"
                   data-price="0" data-variant-title="${(foundationType || 'Default Title').replace(/"/g, '&quot;')}"
                   data-variant-available="false" data-variant-inventory="0"
                   data-variant-inventory-policy="deny" data-variant-inventory-management="shopify"
                   disabled class="foundation-input">
            <span class="foundation-indicator"></span>
            <div class="foundation-content">
              <p class="foundation-title">${(foundationType || 'Default Title').replace(/</g, '&lt;')}</p>
              <p class="foundation-subtitle" style="color: #666;">
                ${escapeHtmlText(mismatchSubtitle)}
              </p>
            </div>
            <p class="foundation-price">—</p>
          </label>
        `;
          return;
        }
        const variantId = variant.id.toString();
        // Option 1 (typeIndex 0) → foundation_product_variant_metafield, option 2 → _2, option 3 → _3
        let foundationMetafieldText = (subtitleMetafields[typeIndex] != null ? subtitleMetafields[typeIndex] : '').toString().trim();
        if (!foundationMetafieldText && foundationMetafieldsByProduct) {
          const foundationMap = mattressProductHandle ? foundationMetafieldsByProduct.get(mattressProductHandle) : null;
          if (foundationMap) {
            const metafields = foundationMap.get(variantId);
            if (metafields) {
              foundationMetafieldText = (metafields.value && metafields.value.trim() !== '') ? metafields.value.trim() : (metafields.foundation_product_variant_metafield || metafields.foundation_product_variant_metafield_2 || metafields.foundation_product_variant_metafield_3 || '');
            }
          }
        }
        if (!foundationMetafieldText || foundationMetafieldText.trim() === '') foundationMetafieldText = variant.title || '';
        let isAvailable = false;
        if (variant.available === true) isAvailable = true;
        else if (variant.inventory_policy === 'continue') isAvailable = true;
        else if (variant.inventory_quantity != null && variant.inventory_quantity > 0) isAvailable = true;
        else if (variant.inventory_management == null && variant.available !== false) isAvailable = true;
        if (isAvailable && firstAvailableVariantIndex === -1) { firstAvailableVariantIndex = typeIndex; hasAvailableVariant = true; }
        const hasFeaturedImage = variant.featured_image && variant.featured_image.src;
        const isFirstAvailable = typeIndex === firstAvailableVariantIndex && isAvailable;
        foundationHTML += `
          <label class="foundation-option${isFirstAvailable ? ' selected' : ''}${!isAvailable ? ' out-of-stock' : ''}" 
                 data-foundation-type="${(foundationType || '').replace(/"/g, '&quot;')}"
                 data-foundation-subtitle-default="${escapeAttr(foundationMetafieldText)}"
                 data-variant-id="${variant.id}" 
                 data-variant-title="${(variant.title || 'Default Title').replace(/"/g, '&quot;')}"
                 data-variant-price="${variant.price || 0}"
                 data-variant-compare-price="${variant.compare_at_price || ''}"
                 data-variant-available="${isAvailable}"
                 data-variant-inventory="${variant.inventory_quantity || 0}"
                 data-variant-inventory-policy="${variant.inventory_policy || 'deny'}"
                 data-variant-inventory-management="${variant.inventory_management || 'shopify'}"
                 data-variant-image="${hasFeaturedImage ? variant.featured_image.src : ''}"
                 data-variant-alt="${(variant.title || '') + ' - ' + (foundationProduct.title || '')}"
                 data-product-handle="${foundationProduct.handle}"
                 data-product-title="${foundationProduct.title}">
            ${!isAvailable ? '<span class="out-of-stock-badge">Out of Stock</span>' : ''}
            <input type="radio" name="foundation" value="${variant.id}" 
                   data-foundation-type="${(foundationType || '').replace(/"/g, '&quot;')}"
                   data-variant-id="${variant.id}" data-product-id="${foundationProduct.id}" data-product-handle="${foundationProduct.handle}" data-product-title="${(foundationProduct.title || '').replace(/"/g, '&quot;')}"
                   data-price="${variant.price || 0}" data-variant-title="${(variant.title || 'Default Title').replace(/"/g, '&quot;')}"
                   data-variant-available="${isAvailable}" data-variant-inventory="${variant.inventory_quantity || 0}"
                   data-variant-inventory-policy="${variant.inventory_policy || 'deny'}" data-variant-inventory-management="${variant.inventory_management || 'shopify'}"
                   ${isFirstAvailable ? 'checked' : ''} class="foundation-input" ${!isAvailable ? 'disabled' : ''}>
            <span class="foundation-indicator"></span>
            <div class="foundation-content">
              <p class="foundation-title ${isFirstAvailable ? 'pdp-text-color' : ''}">${(foundationType || variant.title || 'Default Title').replace(/</g, '&lt;')}</p>
              <p class="foundation-subtitle" style="color: ${isAvailable ? '#666' : '#ff4444'};">
                ${(isAvailable ? foundationMetafieldText : 'Out of Stock').replace(/</g, '&lt;')}
              </p>
            </div>
            <p class="foundation-price ${isFirstAvailable ? 'pdp-text-color' : ''}">${(variant.price && variant.price > 0) ? '+' + formatMoney(variant.price) : formatMoney(variant.price || 0)}</p>
          </label>
        `;
      });
    } else {
      foundationProduct.variants.forEach((variant, index) => {
        // Get foundation variant metafields from global map
        let foundationMetafieldText = '';
        const variantId = variant.id.toString();
        
        // Get the foundation metafields map for the mattress product (not foundation product)
        // The foundation metafields are stored under the mattress product handle
        const foundationMap = mattressProductHandle ? foundationMetafieldsByProduct.get(mattressProductHandle) : null;
        
        if (foundationMap) {
          const metafields = foundationMap.get(variantId);
          if (metafields) {
            // Use the 'value' property first - it contains the actual metafield text for this variant
            // This is more reliable than checking position-specific properties
            foundationMetafieldText = (metafields.value && metafields.value.trim() !== '') ? metafields.value.trim() : '';
            
            // If value is empty, try position-specific properties as fallback
            if (!foundationMetafieldText) {
              if (index === 0) {
                foundationMetafieldText = metafields.foundation_product_variant_metafield || '';
              } else if (index === 1) {
                foundationMetafieldText = metafields.foundation_product_variant_metafield_2 || '';
              } else if (index === 2) {
                foundationMetafieldText = metafields.foundation_product_variant_metafield_3 || '';
              }
            }
          }
        }
        
        // If no metafield found, use variant title as fallback
        if (!foundationMetafieldText || foundationMetafieldText.trim() === '') {
          foundationMetafieldText = variant.title || '';
        }
        
        // Check availability
        let isAvailable = false;
        let availabilityText = 'In Stock';
        
        if (variant.available === true) {
          isAvailable = true;
        } else if (variant.inventory_policy === 'continue') {
          isAvailable = true;
          availabilityText = 'Available for Backorder';
        } else if (variant.inventory_quantity !== null && variant.inventory_quantity > 0) {
          isAvailable = true;
          if (variant.inventory_quantity < 10) {
            availabilityText = `Only ${variant.inventory_quantity} left`;
          }
        } else if (variant.inventory_management === null && variant.available !== false) {
          isAvailable = true;
        }
        
        if (isAvailable && firstAvailableVariantIndex === -1) {
          firstAvailableVariantIndex = index;
          hasAvailableVariant = true;
        }
        
        // Get ALL matching images for this variant using flexible matching
        // Check if variant has a featured image
        const hasFeaturedImage = variant.featured_image && variant.featured_image.src;
        
        foundationHTML += `
          <label class="foundation-option${index === firstAvailableVariantIndex && isAvailable ? ' selected' : ''}${!isAvailable ? ' out-of-stock' : ''}" 
                 data-variant-id="${variant.id}" 
                 data-variant-title="${variant.title || 'Default Title'}"
                 data-variant-price="${variant.price || 0}"
                 data-variant-compare-price="${variant.compare_at_price || ''}"
                 data-variant-available="${isAvailable}"
                 data-variant-inventory="${variant.inventory_quantity || 0}"
                 data-variant-inventory-policy="${variant.inventory_policy || 'deny'}"
                 data-variant-inventory-management="${variant.inventory_management || 'shopify'}"
                 data-variant-image="${hasFeaturedImage ? variant.featured_image.src : ''}"
                 data-variant-alt="${variant.title || ''} - ${foundationProduct.title}"
                 data-product-handle="${foundationProduct.handle}"
                 data-product-title="${foundationProduct.title}">
            ${!isAvailable ? '<span class="out-of-stock-badge">Out of Stock</span>' : ''}
            
            <input type="radio" 
                   name="foundation" 
                   value="${variant.id}" 
                   data-variant-id="${variant.id}" 
                   data-product-id="${foundationProduct.id}" 
                   data-product-handle="${foundationProduct.handle}" 
                   data-product-title="${foundationProduct.title}" 
                   data-product-description="${foundationProduct.description ? foundationProduct.description.replace(/"/g, '&quot;') : ''}" 
                   data-price="${variant.price || 0}" 
                   data-variant-title="${variant.title || 'Default Title'}"
                   data-variant-available="${isAvailable}"
                   data-variant-inventory="${variant.inventory_quantity || 0}"
                   data-variant-inventory-policy="${variant.inventory_policy || 'deny'}"
                   data-variant-inventory-management="${variant.inventory_management || 'shopify'}"
                   ${index === firstAvailableVariantIndex && isAvailable ? 'checked' : ''}
                   class="foundation-input"
                   ${!isAvailable ? 'disabled' : ''}>
            
            <span class="foundation-indicator"></span>
            <div class="foundation-content">
              <p class="foundation-title ${index === firstAvailableVariantIndex && isAvailable ? 'pdp-text-color' : ''}">
                ${variant.title || 'Default Title'}
              </p>
              <p class="foundation-subtitle" style="color: ${isAvailable ? '#666' : '#ff4444'};">
                ${isAvailable ? foundationMetafieldText : 'Out of Stock'}
              </p>
            </div>
            <p class="foundation-price ${index === firstAvailableVariantIndex && isAvailable ? 'pdp-text-color' : ''}">${(variant.price && variant.price > 0) ? '+' + formatMoney(variant.price) : formatMoney(variant.price || 0)}</p>
          </label>
        `;
      });
    }
    
    // Save current scroll position to prevent auto-scroll
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    foundationContainer.innerHTML = foundationHTML;
    
    // Restore scroll position immediately and in next frame to prevent auto-scroll
    window.scrollTo(0, scrollPosition);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
    
    // Add event listeners to foundation options
    const foundationOptions = foundationContainer.querySelectorAll('.foundation-option');
    foundationOptions.forEach(option => {
      option.addEventListener('click', function(e) {
        // Prevent default scroll behavior when clicking foundation options
        e.preventDefault();
        e.stopPropagation();
        const input = this.querySelector('input[type="radio"]');
        const liveAvailable = this.getAttribute('data-variant-available') === 'true';
        if (!liveAvailable || !input || input.disabled || !input.value) {
          return;
        }
        
        if (this.classList.contains('selected')) return;
        
        foundationOptions.forEach(opt => {
          opt.classList.remove('selected');
          const optInput = opt.querySelector('input[type="radio"]');
          if (optInput) optInput.checked = false;
          // Remove pdp-text-color class
          const optTitle = opt.querySelector('.foundation-title');
          const optPrice = opt.querySelector('.foundation-price');
          if (optTitle) optTitle.classList.remove('pdp-text-color');
          if (optPrice) optPrice.classList.remove('pdp-text-color');
        });
        
        this.classList.add('selected');
        if (input) input.checked = true;
        
        // Add pdp-text-color class to selected foundation
        const selectedTitle = this.querySelector('.foundation-title');
        const selectedPrice = this.querySelector('.foundation-price');
        if (selectedTitle) selectedTitle.classList.add('pdp-text-color');
        if (selectedPrice) selectedPrice.classList.add('pdp-text-color');
        
        // Set current foundation variant
        const variantId = input.value;
        const variantPrice = this.getAttribute('data-variant-price');
        const parsedPrice = variantPrice ? parseInt(variantPrice) : 0;
        const foundationTypeVal = this.getAttribute('data-foundation-type');
        if (foundationTypeVal) selectedFoundationType = foundationTypeVal;
        
        currentFoundationVariant = {
          id: variantId,
          title: this.getAttribute('data-variant-title'),
          price: parsedPrice,
          available: liveAvailable,
          inventoryPolicy: this.getAttribute('data-variant-inventory-policy'),
          inventoryQuantity: this.getAttribute('data-variant-inventory'),
          inventoryManagement: this.getAttribute('data-variant-inventory-management')
        };
        
        // Immediately update price when foundation is selected
        updateTotalPrice();
        
        // Update variant name display (to show foundation name)
        updateVariantNameDisplay();
        
        // Store that foundation is now selected
        isFoundationSelected = true;
        
        // Handle foundation variant image changes
        // Ensure foundation product has handle before calling
        if (foundationProduct && !foundationProduct.handle) {
          const productHandle = this.getAttribute('data-product-handle');
          if (productHandle) {
            foundationProduct.handle = productHandle;
          } else if (currentFoundationProduct && currentFoundationProduct.handle) {
            foundationProduct.handle = currentFoundationProduct.handle;
          }
        }
        handleFoundationVariantImageChange(this, foundationProduct);
        
        // Update button states
        updateButtonStates();
      });
    });
    
    if (applyHiddenFirstFoundationOptionMode()) {
      if (typeof window.rebuildCustomDropdown === 'function') {
        window.rebuildCustomDropdown();
      }
      return;
    }

    // Auto-select first available foundation option AND trigger image change
if (firstAvailableVariantIndex !== -1) {
  const firstAvailableFoundation = foundationContainer.querySelector(`.foundation-option:nth-child(${firstAvailableVariantIndex + 1})`);
  if (firstAvailableFoundation) {
    // Get the radio input and foundation option element
    const radioInput = firstAvailableFoundation.querySelector('input[type="radio"]');
    
    if (radioInput && firstAvailableFoundation) {
      // Programmatically check the radio button without triggering scroll
      // Prevent focus to avoid automatic scroll
      const originalTabIndex = radioInput.getAttribute('tabindex');
      radioInput.setAttribute('tabindex', '-1');
      radioInput.checked = true;
      radioInput.blur(); // Remove focus if it was gained
      if (originalTabIndex !== null) {
        radioInput.setAttribute('tabindex', originalTabIndex);
      } else {
        radioInput.removeAttribute('tabindex');
      }
      
      // Manually update the UI state without click event
      foundationOptions.forEach(opt => {
        opt.classList.remove('selected');
        const optInput = opt.querySelector('input[type="radio"]');
        if (optInput) {
          const optTabIndex = optInput.getAttribute('tabindex');
          optInput.setAttribute('tabindex', '-1');
          optInput.checked = false;
          optInput.blur();
          if (optTabIndex !== null) {
            optInput.setAttribute('tabindex', optTabIndex);
          } else {
            optInput.removeAttribute('tabindex');
          }
        }
        const optTitle = opt.querySelector('.foundation-title');
        const optPrice = opt.querySelector('.foundation-price');
        if (optTitle) optTitle.classList.remove('pdp-text-color');
        if (optPrice) optPrice.classList.remove('pdp-text-color');
      });
      
      // Set selected state
      firstAvailableFoundation.classList.add('selected');
      const selectedTitle = firstAvailableFoundation.querySelector('.foundation-title');
      const selectedPrice = firstAvailableFoundation.querySelector('.foundation-price');
      if (selectedTitle) selectedTitle.classList.add('pdp-text-color');
      if (selectedPrice) selectedPrice.classList.add('pdp-text-color');
      
      // Set current foundation variant
      const variantId = radioInput.value;
      const variantPrice = firstAvailableFoundation.getAttribute('data-variant-price');
      const parsedPrice = variantPrice ? parseInt(variantPrice) : 0;
      const isAvailable = firstAvailableFoundation.getAttribute('data-variant-available') === 'true';
      
      currentFoundationVariant = {
        id: variantId,
        title: firstAvailableFoundation.getAttribute('data-variant-title'),
        price: parsedPrice,
        available: isAvailable,
        inventoryPolicy: firstAvailableFoundation.getAttribute('data-variant-inventory-policy'),
        inventoryQuantity: firstAvailableFoundation.getAttribute('data-variant-inventory'),
        inventoryManagement: firstAvailableFoundation.getAttribute('data-variant-inventory-management')
      };
      const ft = firstAvailableFoundation.getAttribute('data-foundation-type');
      if (ft) selectedFoundationType = ft;
      
      // Update price and button states
      updateTotalPrice();
      updateVariantNameDisplay();
      isFoundationSelected = true;
      updateButtonStates();
      
      // IMPORTANT FIX: Call handleFoundationVariantImageChange for the first foundation option
      // This ensures the images load on page load, not just when user clicks
      // Ensure foundation product has handle before calling
      if (foundationProduct && !foundationProduct.handle) {
        const productHandle = firstAvailableFoundation.getAttribute('data-product-handle');
        if (productHandle) {
          foundationProduct.handle = productHandle;
        } else if (currentFoundationProduct && currentFoundationProduct.handle) {
          foundationProduct.handle = currentFoundationProduct.handle;
        }
      }
      handleFoundationVariantImageChange(firstAvailableFoundation, foundationProduct);
    }
  }
} else if (foundationOptions.length > 0) {
  // If no variants are available, select first one but don't enable it (without scroll)
  const firstOption = foundationOptions[0];
  const radioInput = firstOption.querySelector('input[type="radio"]');
  
  if (radioInput) {
    // Prevent focus to avoid automatic scroll
    const originalTabIndex = radioInput.getAttribute('tabindex');
    radioInput.setAttribute('tabindex', '-1');
    radioInput.checked = true;
    radioInput.blur(); // Remove focus if it was gained
    if (originalTabIndex !== null) {
      radioInput.setAttribute('tabindex', originalTabIndex);
    } else {
      radioInput.removeAttribute('tabindex');
    }
    firstOption.classList.add('selected');
  }
}

    if (typeof window.rebuildCustomDropdown === 'function') {
      window.rebuildCustomDropdown();
    }

  } catch (error) {
    console.error('Error loading foundation product:', error);
    foundationContainer.innerHTML = '<p class="no-foundation-options">Unable to load foundation options.</p>';
  }
}
  
  /** When mattress size changes, update each foundation option to the variant matching mattress size (and foundation type if 2 options). Keeps same foundation type selected. */
  function syncFoundationOptionsWithMattressSize() {
    const foundationContainer = document.getElementById('foundation-options-container');
    if (!currentFoundationProduct || !currentFoundationProduct.variants || !foundationContainer) return;
    const hasTwoOptions = currentFoundationProduct.options && currentFoundationProduct.options.length >= 2;
    const hasOneOption = currentFoundationProduct.options && currentFoundationProduct.options.length === 1;
    if (!hasTwoOptions && !hasOneOption) return;
    const currentSizeValue = getCurrentMattressSizeValue();
    const options = foundationContainer.querySelectorAll('.foundation-option[data-foundation-type]');
    let previouslySelectedType = selectedFoundationType;
    let newSelectedVariant = null;
    options.forEach(function (opt) {
      const typeVal = opt.getAttribute('data-foundation-type');
      if (!typeVal) return;
      opt.querySelectorAll('.foundation-size-na-badge').forEach(function (b) {
        b.remove();
      });
      const variant = hasOneOption
        ? getFoundationVariantBySize(currentFoundationProduct, currentSizeValue)
        : getFoundationVariantBySizeAndType(currentFoundationProduct, currentSizeValue, typeVal);
      if (!variant) {
        opt.setAttribute('data-variant-id', '');
        opt.setAttribute('data-variant-title', typeVal);
        opt.setAttribute('data-variant-price', '0');
        opt.setAttribute('data-variant-available', 'false');
        opt.setAttribute('data-size-mismatch', 'true');
        opt.removeAttribute('data-variant-image');
        opt.classList.add('out-of-stock', 'size-unavailable');
        opt.classList.remove('selected');
        const input = opt.querySelector('input[name="foundation"]');
        if (input) {
          input.value = '';
          input.setAttribute('data-variant-id', '');
          input.setAttribute('data-variant-title', typeVal);
          input.setAttribute('data-variant-available', 'false');
          input.setAttribute('data-price', '0');
          input.disabled = true;
          input.checked = false;
        }
        const titleEl = opt.querySelector('.foundation-title');
        if (titleEl) {
          titleEl.textContent = typeVal;
          titleEl.classList.remove('pdp-text-color');
        }
        const priceEl = opt.querySelector('.foundation-price');
        if (priceEl) {
          priceEl.textContent = '—';
          priceEl.classList.remove('pdp-text-color');
        }
        const subEl = opt.querySelector('.foundation-subtitle');
        if (subEl) {
          let subText = opt.getAttribute('data-foundation-subtitle-default');
          if (subText == null || subText === '') {
            if (!hasOneOption) {
              const anyForType = getFirstVariantForFoundationType(currentFoundationProduct, typeVal);
              subText = anyForType ? (anyForType.title || '') : '';
            }
          }
          subEl.textContent = subText || '';
          subEl.style.color = '#666';
        }
        const naBadge = document.createElement('span');
        naBadge.className = 'out-of-stock-badge foundation-size-na-badge';
        naBadge.textContent = 'Not Available';
        opt.insertBefore(naBadge, opt.firstChild);
        opt.querySelectorAll('.out-of-stock-badge:not(.foundation-size-na-badge)').forEach(function (b) {
          b.remove();
        });
        return;
      }
      opt.classList.remove('size-unavailable');
      opt.removeAttribute('data-size-mismatch');
      const variantId = variant.id.toString();
      let isAvailable = false;
      if (variant.available === true) isAvailable = true;
      else if (variant.inventory_policy === 'continue') isAvailable = true;
      else if (variant.inventory_quantity != null && variant.inventory_quantity > 0) isAvailable = true;
      else if (variant.inventory_management == null && variant.available !== false) isAvailable = true;
      opt.setAttribute('data-variant-id', variantId);
      opt.setAttribute('data-variant-title', (hasOneOption ? typeVal : variant.title) || 'Default Title');
      opt.setAttribute('data-variant-price', variant.price || 0);
      opt.setAttribute('data-variant-available', isAvailable);
      opt.setAttribute('data-variant-inventory', variant.inventory_quantity || 0);
      opt.setAttribute('data-variant-inventory-policy', variant.inventory_policy || 'deny');
      opt.setAttribute('data-variant-inventory-management', variant.inventory_management || 'shopify');
      if (variant.featured_image && variant.featured_image.src) {
        opt.setAttribute('data-variant-image', variant.featured_image.src);
      } else {
        opt.removeAttribute('data-variant-image');
      }
      const input = opt.querySelector('input[name="foundation"]');
      if (input) {
        input.value = variantId;
        input.setAttribute('data-variant-id', variantId);
        input.setAttribute('data-variant-title', (hasOneOption ? typeVal : variant.title) || 'Default Title');
        input.setAttribute('data-variant-available', isAvailable);
        input.setAttribute('data-price', variant.price || 0);
        input.disabled = !isAvailable;
      }
      const titleEl = opt.querySelector('.foundation-title');
      if (titleEl) titleEl.textContent = typeVal || variant.title || 'Default Title';
      const priceEl = opt.querySelector('.foundation-price');
      if (priceEl) priceEl.textContent = (variant.price && variant.price > 0) ? '+' + formatMoney(variant.price) : formatMoney(variant.price || 0);
      const subEl = opt.querySelector('.foundation-subtitle');
      if (subEl) {
        subEl.style.color = isAvailable ? '#666' : '#ff4444';
        const preservedSub = opt.getAttribute('data-foundation-subtitle-default');
        if (isAvailable) {
          subEl.textContent =
            preservedSub != null && String(preservedSub).trim() !== ''
              ? preservedSub
              : (variant.title || '');
        } else {
          subEl.textContent = 'Out of Stock';
        }
      }
      opt.classList.toggle('out-of-stock', !isAvailable);
      opt.querySelectorAll('.out-of-stock-badge:not(.foundation-size-na-badge)').forEach(function (b) {
        b.remove();
      });
      if (!isAvailable) {
        const os = document.createElement('span');
        os.className = 'out-of-stock-badge';
        os.textContent = 'Out of Stock';
        opt.insertBefore(os, opt.firstChild);
      }
      if (typeVal === previouslySelectedType && isAvailable) newSelectedVariant = variant;
    });
    if (previouslySelectedType && newSelectedVariant) {
      let selectedOpt = null;
      foundationContainer.querySelectorAll('.foundation-option[data-foundation-type]').forEach(function (o) {
        if (o.getAttribute('data-foundation-type') === previouslySelectedType) selectedOpt = o;
      });
      if (selectedOpt) {
        const radio = selectedOpt.querySelector('input[name="foundation"]');
        if (radio) radio.checked = true;
        foundationContainer.querySelectorAll('.foundation-option').forEach(function (o) {
          o.classList.remove('selected');
          const t = o.querySelector('.foundation-title');
          const p = o.querySelector('.foundation-price');
          if (t) t.classList.remove('pdp-text-color');
          if (p) p.classList.remove('pdp-text-color');
        });
        selectedOpt.classList.add('selected');
        const st = selectedOpt.querySelector('.foundation-title');
        const sp = selectedOpt.querySelector('.foundation-price');
        if (st) st.classList.add('pdp-text-color');
        if (sp) sp.classList.add('pdp-text-color');
        currentFoundationVariant = {
          id: newSelectedVariant.id.toString(),
          title: hasOneOption ? previouslySelectedType : newSelectedVariant.title,
          price: parseInt(newSelectedVariant.price) || 0,
          available: true,
          inventoryPolicy: newSelectedVariant.inventory_policy,
          inventoryQuantity: newSelectedVariant.inventory_quantity,
          inventoryManagement: newSelectedVariant.inventory_management
        };
      }
    } else if (isHideFirstFoundationOptionEnabled()) {
      applyHiddenFirstFoundationOptionMode();
    } else {
      const firstAvailable = foundationContainer.querySelector('.foundation-option:not(.out-of-stock):not(.size-unavailable)');
      if (firstAvailable) {
        const radio = firstAvailable.querySelector('input[name="foundation"]');
        if (radio && !radio.disabled && isPurchasableFoundationInput(radio)) {
          radio.checked = true;
          foundationContainer.querySelectorAll('.foundation-option').forEach(function (o) {
            o.classList.remove('selected');
            const t = o.querySelector('.foundation-title');
            const p = o.querySelector('.foundation-price');
            if (t) t.classList.remove('pdp-text-color');
            if (p) p.classList.remove('pdp-text-color');
          });
          firstAvailable.classList.add('selected');
          const st = firstAvailable.querySelector('.foundation-title');
          const sp = firstAvailable.querySelector('.foundation-price');
          if (st) st.classList.add('pdp-text-color');
          if (sp) sp.classList.add('pdp-text-color');
          const variantId = firstAvailable.getAttribute('data-variant-id');
          if (variantId) {
            selectedFoundationType = firstAvailable.getAttribute('data-foundation-type');
            currentFoundationVariant = {
              id: variantId,
              title: firstAvailable.getAttribute('data-variant-title'),
              price: parseInt(firstAvailable.getAttribute('data-variant-price'), 10) || 0,
              available: firstAvailable.getAttribute('data-variant-available') === 'true',
              inventoryPolicy: firstAvailable.getAttribute('data-variant-inventory-policy'),
              inventoryQuantity: firstAvailable.getAttribute('data-variant-inventory'),
              inventoryManagement: firstAvailable.getAttribute('data-variant-inventory-management')
            };
            isFoundationSelected = true;
          }
        }
      } else {
        clearFoundationSelectionState();
      }
    }
    updateTotalPrice();
    updateVariantNameDisplay();
    updateButtonStates();
  }
  
  // Function to merge metafields from data attributes into product data
  function mergeVariantMetafields(productData, tabElement) {
    if (!tabElement || !productData || !productData.variants) return productData;
    
    // Get metafields from data attribute
    const metafieldsData = tabElement.getAttribute('data-variant-metafields');
    if (metafieldsData) {
      // Parse metafields data: format is "variantId:field1|field2|field3||variantId2:..."
      const variantMetafields = metafieldsData.split('||');
      variantMetafields.forEach((variantData, idx) => {
        if (variantData) {
          const [variantId, metafields] = variantData.split(':');
          if (variantId && metafields) {
            const fields = metafields.split('|');
            const variant = productData.variants.find(v => v.id.toString() === variantId);
            if (variant) {
              if (!variant.metafields) variant.metafields = {};
              if (!variant.metafields.custom) variant.metafields.custom = {};
              
              // Set metafields based on card index (0, 1, 2) - each variant can have all three
              if (fields[0] && fields[0].trim() !== '') {
                variant.metafields.custom.firmness_variant_option_text = fields[0];
              }
              if (fields[1] && fields[1].trim() !== '') {
                variant.metafields.custom.firmness_variant_option_text_2 = fields[1];
              }
              if (fields[2] && fields[2].trim() !== '') {
                variant.metafields.custom.firmness_variant_option_text_3 = fields[2];
              }
            }
          }
        }
      });
    }
    
    return productData;
  }

  // Function to update product display when mattress product changes
  async function updateMattressProduct(productData, tabElement) {
    // Merge metafields from data attributes
    productData = mergeVariantMetafields(productData, tabElement);
    
    // Fetch metafields from custom endpoint
    productData = await fetchVariantMetafields(productData);
    
    // Load variant info data for this product handle
    if (productData.handle) {
      loadVariantInfoForProduct(productData.handle);
    }
    
    currentMattressProduct = productData;
    currentMattressImages = productData.images;
    
    // Update active tab reference
    currentActiveTab = tabElement;
    
    // Update colors based on active tab using existing function
    updateTitleLastWordColor();
    
    // Reset foundation selection
    isFoundationSelected = false;
    currentFoundationVariant = null;
    currentFoundationProduct = null;
    currentFoundationImages = null;
    
    // Load mattress images initially
    if (productData.images && productData.images.length > 0) {
      loadProductImages(productData.images);
    }
    
    // Update title
    updateProductTitle(productData.title);
    
    // Update description - will be updated when variant is selected (from variant info metafield: short)
    // Initial description will be updated when first variant is selected
    
    // Update accessories title
    const accessoriesTitleElement = document.getElementById('accessories-product-title');
    if (accessoriesTitleElement) {
      accessoriesTitleElement.textContent = productData.title;
    }
    
    // Price will be updated by updateButtonStates() which calls updateTotalPrice()
    // Compare-at + savings badge are synced inside updateTotalPrice -> updatePriceSavingsDisplay()
    
    // Show firmness section
    const firmnessSection = document.getElementById('firmness-section');
    if (firmnessSection) {
      firmnessSection.style.display = 'block';
    }
    
    // Load firmness options (variants)
    loadFirmnessOptions(productData);
    
    // Load foundation options for this mattress from metafield
    await loadFoundationOptions(productData.handle);
    
    // Update button states
    updateButtonStates();
  }
  
  
  // Function to update product title with styling (last 1 word if 2 words, last 2 words if 3+)
  function updateProductTitle(newTitle) {
    const titleElement = document.getElementById('dynamic-product-title');
    if (!titleElement) return;
    
    const titleWords = newTitle.trim().split(/\s+/).filter(Boolean);
    const len = titleWords.length;
    const applyColor = len >= 2;
    const colorLastTwo = len >= 3;
    
    let newTitleHTML = '';
    titleWords.forEach((word, index) => {
      const isColored = applyColor && (index === len - 1 || (colorLastTwo && index === len - 2));
      if (isColored) {
        newTitleHTML += `<span class="title-last-word pdp-text-color" data-pdp-color="text">${word}</span>`;
      } else {
        newTitleHTML += word;
      }
      if (index < len - 1) newTitleHTML += ' ';
    });
    
    titleElement.innerHTML = newTitleHTML;
    
    // Apply color from current active tab to all colored words
    if (currentActiveTab) {
      const tabColor = currentActiveTab.getAttribute('data-tab-color');
      const titleLastWords = titleElement.querySelectorAll('.title-last-word');
      if (tabColor && titleLastWords.length) {
        titleLastWords.forEach(function (el) { el.style.color = tabColor; });
      }
    }
  }
  
  // Function to detect which option is firmness and which is size
  function detectOptionTypes(productData) {
    
    if (!productData.options || !Array.isArray(productData.options)) {
      console.warn('No options array found in product data');
      return { firmnessIndex: 0, sizeIndex: 1 };
    }
    
    // Get option names
    const optionNames = productData.options.map(opt => {
      if (typeof opt === 'string') return opt.toLowerCase();
      else if (opt && typeof opt === 'object' && opt.name) return opt.name.toLowerCase();
      else if (opt && typeof opt === 'object' && opt.title) return opt.title.toLowerCase();
      else if (opt && typeof opt === 'number') return opt.toString().toLowerCase();
      else return '';
    });
    
    const sizeKeywords = ['size', 'dimension', 'twin', 'full', 'queen', 'king', 'california', 'xl'];
    const firmnessKeywords = ['firmness', 'firm', 'soft', 'medium', 'plush', 'comfort', 'support', 'feel'];
    
    let firmnessIndex = -1;
    let sizeIndex = -1;
    
    // First, check option names
    optionNames.forEach((name, index) => {
      if (name && sizeKeywords.some(keyword => name.includes(keyword))) {
        sizeIndex = index;
      } else if (name && firmnessKeywords.some(keyword => name.includes(keyword))) {
        firmnessIndex = index;
      }
    });
    
    // If not detected by name, check variant values
    if (productData.variants && productData.variants.length > 0) {
      const sampleVariant = productData.variants[0];
      if (sampleVariant && sampleVariant.options) {
        sampleVariant.options.forEach((value, index) => {
          if (!value) return;
          const valLower = value.toString().toLowerCase();
          
          // Check for size values
          if (valLower.includes('twin') || valLower.includes('queen') || 
              valLower.includes('king') || valLower.includes('full') ||
              valLower.includes('xl') || valLower.includes('california') ||
              valLower.includes('single') || valLower.includes('double')) {
            sizeIndex = index;
          } 
          // Check for firmness values
          else if (valLower.includes('firm') || valLower.includes('soft') || 
                   valLower.includes('medium') || valLower.includes('plush') ||
                   valLower.includes('luxury') || valLower.includes('standard') ||
                   valLower.includes('comfort') || valLower.includes('support')) {
            firmnessIndex = index;
          }
        });
      }
    }
    
    // If we still haven't found them, make educated guesses
    if (firmnessIndex === -1) firmnessIndex = 0;
    if (sizeIndex === -1 && productData.options.length > 1) {
      sizeIndex = 1;
    } else if (sizeIndex === -1) {
      sizeIndex = 0;
    }
    
    // Ensure they're different
    if (firmnessIndex === sizeIndex && productData.options.length > 1) {
      sizeIndex = 1;
    }
    return { firmnessIndex, sizeIndex };
  }
  
  /** Normalize mattress/foundation size labels for comparison (spacing, dashes, case). */
  function normalizeMattressSizeLabel(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/[-–—]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function compactBedSizeKey(value) {
    return normalizeMattressSizeLabel(value).replace(/\s+/g, '');
  }

  function mattressSizesMatch(a, b) {
    const na = normalizeMattressSizeLabel(a);
    const nb = normalizeMattressSizeLabel(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    if (compactBedSizeKey(a) === compactBedSizeKey(b)) return true;

    const aliases = {
      'twin xl': ['twinxl', 'txl', 'twin extra long'],
      'split king': ['splitking', 'sk'],
      'split cal king': ['splitcalking', 'split california king', 'sck'],
      'cal king': ['calking', 'california king', 'ck'],
      'split head queen': ['splitheadqueen', 'shq'],
      'split head king': ['splitheadking', 'shk'],
      'split head cal king': ['splitheadcalking', 'split head california king', 'shck']
    };

    function aliasKeys(label) {
      const normalized = normalizeMattressSizeLabel(label);
      const compact = compactBedSizeKey(label);
      const keys = new Set([normalized, compact]);
      Object.keys(aliases).forEach(function (canonical) {
        const list = aliases[canonical];
        if (normalized === canonical || compact === compactBedSizeKey(canonical) || list.includes(compact)) {
          keys.add(canonical);
          keys.add(compactBedSizeKey(canonical));
          list.forEach(function (alias) {
            keys.add(alias);
          });
        }
      });
      return keys;
    }

    const keysA = aliasKeys(a);
    const keysB = aliasKeys(b);
    for (const key of keysA) {
      if (keysB.has(key)) return true;
    }
    return false;
  }

  /** Returns current mattress size value (e.g. "Twin", "Queen") or empty string if none selected. Used to sync foundation variant. */
  function getCurrentMattressSizeValue() {
    if (currentMattressProduct && currentSizeVariant) {
      const { sizeIndex } = detectOptionTypes(currentMattressProduct);
      if (currentSizeVariant.options && currentSizeVariant.options[sizeIndex] !== undefined) {
        const v = String(currentSizeVariant.options[sizeIndex]).trim();
        if (v) return v;
      }
      if (sizeIndex === 0 && currentSizeVariant.option1) return String(currentSizeVariant.option1).trim();
      if (sizeIndex === 1 && currentSizeVariant.option2) return String(currentSizeVariant.option2).trim();
      if (sizeIndex === 2 && currentSizeVariant.option3) return String(currentSizeVariant.option3).trim();
    }
    const customSelected = document.querySelector('#custom-product-container .custom-dropdown-selected .selected-size');
    if (customSelected && customSelected.textContent) {
      const t = customSelected.textContent.trim();
      const sizeOnly = t.split('|')[0].trim();
      if (sizeOnly) return sizeOnly;
    }
    return '';
  }
  
  /** Foundation product: option1 = Foundation Type, option2 = Size. Returns variant matching (foundation type + mattress size) or null. */
  function getFoundationVariantBySizeAndType(foundationProduct, sizeValue, foundationTypeValue) {
    if (!foundationProduct || !foundationProduct.variants || !foundationTypeValue) return null;
    const sizeVal = (sizeValue || '').toString().trim();
    const typeVal = foundationTypeValue.toString().trim();
    return foundationProduct.variants.find(function (v) {
      const vType = (v.option1 || (v.options && v.options[0]) || '').toString().trim();
      const vSize = (v.option2 || (v.options && v.options[1]) || '').toString().trim();
      return vType === typeVal && (sizeVal === '' || mattressSizesMatch(vSize, sizeVal));
    }) || null;
  }
  
  /** Get first variant for a foundation type (option1 = Foundation Type). For display when no size selected. */
  function getFirstVariantForFoundationType(foundationProduct, foundationTypeValue) {
    if (!foundationProduct || !foundationProduct.variants || !foundationTypeValue) return null;
    const typeVal = foundationTypeValue.toString().trim();
    return foundationProduct.variants.find(function (v) {
      const vType = (v.option1 || (v.options && v.options[0]) || '').toString().trim();
      return vType === typeVal;
    }) || null;
  }
  
  /** Foundation product with only size variants: find variant matching size (option1 / options[0]). */
  function getFoundationVariantBySize(foundationProduct, sizeValue) {
    if (!foundationProduct || !foundationProduct.variants) return null;
    const sizeVal = (sizeValue || '').toString().trim();
    if (!sizeVal) return foundationProduct.variants[0] || null;
    return foundationProduct.variants.find(function (v) {
      const vSize = (v.option1 || (v.options && v.options[0]) || '').toString().trim();
      return mattressSizesMatch(vSize, sizeVal);
    }) || null;
  }
  
  // Function to load firmness options from variants
  function loadFirmnessOptions(productData) {
    // Store productData for image access
    const currentProductData = productData;
    
    const firmnessContainer = document.getElementById('firmness-options-container');
    if (!firmnessContainer) return;
    
    if (!productData.variants || !Array.isArray(productData.variants) || productData.variants.length === 0) {
      firmnessContainer.innerHTML = '<p class="error-message">No variants available</p>';
      return;
    }
    
    firmnessContainer.innerHTML = '';
    
    // Get firmness_option blocks from JSON
    let firmnessOptionBlocks = [];
    try {
      const blocksJson = document.getElementById('firmness-option-blocks');
      if (blocksJson) {
        firmnessOptionBlocks = JSON.parse(blocksJson.textContent);
      }
    } catch (e) {
      console.log('Could not parse firmness option blocks:', e);
    }
    
    const { firmnessIndex, sizeIndex } = detectOptionTypes(productData);
    const firmnessMap = new Map();
    
    // First pass: group variants by firmness
    productData.variants.forEach(variant => {
      let firmnessValue = 'Standard';
      
      if (variant.options && variant.options[firmnessIndex] !== undefined) {
        firmnessValue = variant.options[firmnessIndex];
      } else if (variant.option1 && firmnessIndex === 0) {
        firmnessValue = variant.option1;
      } else if (variant.option2 && firmnessIndex === 1) {
        firmnessValue = variant.option2;
      } else if (variant.option3 && firmnessIndex === 2) {
        firmnessValue = variant.option3;
      } else if (variant.title) {
        const titleParts = variant.title.split(' / ');
        firmnessValue = titleParts[0] || variant.title;
      }
      
      firmnessValue = firmnessValue.toString();
      const firmnessKey = firmnessValue.toLowerCase().replace(/\s+/g, '-');
      
      if (!firmnessMap.has(firmnessKey)) {
        // Try to find matching firmness option block
        let matchingBlock = null;
        for (let block of firmnessOptionBlocks) {
          const blockValue = (block.firmness_value || '').toLowerCase().replace(/\s+/g, '-');
          if (blockValue === firmnessKey || blockValue === firmnessValue.toLowerCase().replace(/\s+/g, '-')) {
            matchingBlock = block;
            break;
          }
        }
        
        firmnessMap.set(firmnessKey, {
          label: firmnessValue,
          value: firmnessKey,
          variants: [],
          variantImage: variant.featured_image || null,
          blockData: matchingBlock
        });
      }
      
      firmnessMap.get(firmnessKey).variants.push(variant);
    });
    
    if (firmnessMap.size === 0) {
      firmnessContainer.innerHTML = '<p class="error-message">No firmness options available</p>';
      return;
    }
    
    // Set firmness header title to "Choose your comfort preference."
    const firmnessTitleTextElement = document.querySelector('#firmness-section .section-title');
    if (firmnessTitleTextElement) {
      firmnessTitleTextElement.textContent = 'Choose your comfort preference.';
      currentFirmnessTitle = 'Choose your comfort preference.';
    } else {
      currentFirmnessTitle = '';
    }
    
    let firstFirmnessVariant = null;
    let cardIndex = 0;
    
    firmnessMap.forEach((firmness, firmnessKey) => {
      const card = document.createElement('div');
      card.className = `firmness-card${cardIndex === 0 ? ' selected' : ''}`;
      card.dataset.firmnessValue = firmness.value;
      card.dataset.firmnessKey = firmnessKey;
      card.dataset.variantIds = firmness.variants.map(v => v.id).join(',');
      
      if (cardIndex === 0) {
        firstFirmnessVariant = firmness.variants[0];
        currentFirmnessVariant = firstFirmnessVariant;
      }
      
      const indicator = document.createElement('span');
      indicator.className = `card-indicator${cardIndex === 0 ? ' active' : ''}`;
      card.appendChild(indicator);
      
      // Add Custom Title - ABOVE IMAGE (from variant info metafield: title)
      const customTitle = document.createElement('p');
      customTitle.className = 'firmness-card-title';
      
      // Get title from variant info metafield (metaobject) - Use FIRST variant in firmness group
      let cardTitle = '';
      
      if (firmness.variants && firmness.variants.length > 0) {
        const firstVariant = firmness.variants[0];
        const variantId = firstVariant.id.toString();
        const variantInfo = variantInfoMetafieldsMap.get(variantId);
        // FLEXIBLE title check - accept any truthy value
        if (variantInfo && variantInfo.title) {
          const titleStr = String(variantInfo.title).trim();
          if (titleStr && titleStr.length > 0 && titleStr !== 'null' && titleStr !== 'undefined') {
            cardTitle = titleStr;
          }
        }
      }
      
      // Fallback to blockData.title if variant info not available
      if (!cardTitle && firmness.blockData && firmness.blockData.title) {
        cardTitle = firmness.blockData.title;
      }
      
      customTitle.textContent = cardTitle;
      card.appendChild(customTitle);
      
      // Add image if variant has one or from block
      const imageDiv = document.createElement('div');
      imageDiv.className = 'firmness-image';
      let imageUrl = null;
      
      // Priority: 1) blockData image, 2) variant featured_image (from variants array), 3) variantImage (stored), 4) product images
      if (firmness.blockData && firmness.blockData.image) {
        imageUrl = firmness.blockData.image;
      } else if (firmness.variants && firmness.variants.length > 0) {
        // Get product images for matching
        const productImages = currentMattressImages || (currentProductData && currentProductData.images) || [];
        // FIRST: Try to match product images with variant IDs (this is how Shopify stores variant images)
        // Shopify stores variant images in product.images array with variant_ids property
        for (let v of firmness.variants) {
          // Check if any product image is associated with this variant
          if (productImages.length > 0) {
            for (let imgIndex = 0; imgIndex < productImages.length; imgIndex++) {
              const img = productImages[imgIndex];
              let imgSrc = null;
              
              // Try multiple ways to get image source
              if (img.src) {
                imgSrc = img.src;
              } else if (img.url) {
                imgSrc = img.url;
              } else if (typeof img === 'string') {
                imgSrc = img;
              } else if (img.original) {
                imgSrc = img.original;
              } else if (img.original_src) {
                imgSrc = img.original_src;
              }
              
              if (!imgSrc) {
                continue;
              }
              
              // Check if image has variant_ids array and includes this variant
              if (img.variant_ids) {
                let variantIdsArray = [];
                
                // Handle different variant_ids formats
                if (Array.isArray(img.variant_ids)) {
                  variantIdsArray = img.variant_ids;
                } else if (typeof img.variant_ids === 'string') {
                  // Try to parse if it's a JSON string
                  try {
                    variantIdsArray = JSON.parse(img.variant_ids);
                  } catch (e) {
                    variantIdsArray = [img.variant_ids];
                  }
                } else {
                  variantIdsArray = [img.variant_ids];
                }
                
                // Convert variant ID to number and string for comparison
                const variantIdNum = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
                const variantIdStr = String(v.id);
                
                // Check all possible ID formats
                if (variantIdsArray.includes(variantIdNum) || 
                    variantIdsArray.includes(v.id) || 
                    variantIdsArray.includes(variantIdStr) ||
                    variantIdsArray.some(id => String(id) === String(v.id)) ||
                    variantIdsArray.some(id => String(id) === String(variantIdNum))) {
                  imageUrl = imgSrc;
                  break;
                }
              }
            }
            if (imageUrl) break;
          }
          
          // SECOND: Check variant.featured_image (if it exists directly on variant)
          if (!imageUrl && v.featured_image) {
            if (v.featured_image.src) {
              imageUrl = v.featured_image.src;
              break;
            } else if (typeof v.featured_image === 'string') {
              imageUrl = v.featured_image;
              break;
            }
          }
          
          // THIRD: Check variant.image as fallback
          if (!imageUrl && v.image) {
            if (v.image.src) {
              imageUrl = v.image.src;
              break;
            } else if (v.image.url) {
              imageUrl = v.image.url;
              break;
            } else if (typeof v.image === 'string') {
              imageUrl = v.image;
              break;
            }
          }
        }
        
        // FOURTH: Fallback to variantImage (stored when creating firmnessMap)
        if (!imageUrl && firmness.variantImage) {
          if (firmness.variantImage.src) {
            imageUrl = firmness.variantImage.src;
          } else if (typeof firmness.variantImage === 'string') {
            imageUrl = firmness.variantImage;
          }
        }
        
        // FIFTH: Last resort - use first product image if no variant-specific image found
        if (!imageUrl && productImages.length > 0) {
          const firstImage = productImages[0];
          imageUrl = firstImage.src || firstImage.url || firstImage.original || firstImage.original_src || (typeof firstImage === 'string' ? firstImage : null);
        }
      }
      
      // Process and validate image URL
      if (imageUrl) {
        // Ensure imageUrl is a string
        if (typeof imageUrl !== 'string') {
          imageUrl = imageUrl.src || imageUrl.url || imageUrl.original || imageUrl.original_src || String(imageUrl);
        }
        
        // Skip if it's a no-image placeholder
        if (imageUrl && (imageUrl.includes('no-image') || imageUrl.includes('no_image'))) {
          imageUrl = null;
        }
      }
      
      // Only create and append image if we have a valid URL
      if (imageUrl && !imageUrl.includes('no-image') && !imageUrl.includes('no_image')) {
        // Process image URL
        if (imageUrl.includes('_medium.') || imageUrl.includes('_small.')) {
          imageUrl = imageUrl.replace('_medium.', '_400x.').replace('_small.', '_400x.');
        } else if (!imageUrl.includes('?')) {
          imageUrl = `${imageUrl}?width=400&height=400&crop=center`;
        }
        
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = firmness.label;
        img.loading = 'lazy';
        img.onerror = function() {
          // Remove the image element and hide the div on error
          if (img.parentNode) {
            img.parentNode.removeChild(img);
          }
          if (imageDiv.parentNode) {
            imageDiv.style.display = 'none';
          }
        };
        imageDiv.appendChild(img);
        card.appendChild(imageDiv);
      }
      
      // Add Variant Name - BELOW IMAGE
      const variantName = document.createElement('p');
      variantName.className = 'firmness-variant-name';
      variantName.textContent = firmness.label;
      card.appendChild(variantName);
      
      // Create div for variant features - BELOW firmness-variant-name
      const variantInfoDiv = document.createElement('div');
      variantInfoDiv.className = 'firmness-variant-info';
      variantInfoDiv.style.marginTop = '2px';
      variantInfoDiv.style.padding = '0 5px';
      
      // Get features from FIRST variant in the firmness group
      // Prefer variant-specific firmness_feature_text metafield so each card shows its own data
      let featureText = '';
      
      if (firmness.variants && firmness.variants.length > 0) {
        const firstVariant = firmness.variants[0];
        const variantId = firstVariant.id.toString();
        const variantInfo = variantInfoMetafieldsMap.get(variantId);
        console.log('variantInfo: ', variantInfo);
        
        var featureSource = (variantInfo && variantInfo.firmness_feature_text != null) ? variantInfo.firmness_feature_text : (variantInfo && variantInfo.feature) ? variantInfo.feature : null;
        if (variantInfo && featureSource) {
          // Parse rich text JSON to extract actual text
          let featureData = featureSource;
          
          // If it's a string that looks like JSON, try to parse it
          if (typeof featureData === 'string') {
            // Check if it looks like a JSON object (starts with { or [)
            if (featureData.trim().startsWith('{') || featureData.trim().startsWith('[')) {
              try {
                featureData = JSON.parse(featureData);
              } catch (e) {
                // If parsing fails, use as is
              }
            }
          }
          
          const parsedFeature = parseRichTextToText(featureData);
          if (parsedFeature && parsedFeature.trim().length > 0 && parsedFeature !== 'null' && parsedFeature !== 'undefined') {
            featureText = parsedFeature.trim();
          }
        }
      }
      
      // Add Features text only (preserve spaces, line breaks, show list bullets)
      if (featureText) {
        const featureElement = document.createElement('div');
        featureElement.className = 'firmness-feature-text';
        var formatted = featureText.replace(/\r\n/g, '\n').trim();
        var lines = formatted.split(/\n/).map(function(s) { return s.trim(); }).filter(function(s) { return s.length > 0; });
        var bodyHtml;
        if (lines.length > 1) {
          bodyHtml = '<ul class="firmness-feature-list">' + lines.map(function(line) {
            var t = line.replace(/^[\s•\-*]+\s*/, '');
            return '<li>' + t.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</li>';
          }).join('') + '</ul>';
        } else {
          bodyHtml = formatted.replace(/\n/g, '<br>').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        }
        featureElement.innerHTML = '<strong>Features:</strong> ' + bodyHtml;
        featureElement.style.fontSize = '12px';
        featureElement.style.margin = '4px 0';
        featureElement.style.color = '#666';
        variantInfoDiv.appendChild(featureElement);
      }
      
      // Append variant info div after variant name (only if features exist)
      if (featureText) {
        card.appendChild(variantInfoDiv);
      }
      
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'firmness';
      radio.value = firmness.value;
      radio.dataset.variantId = firmness.variants[0].id;
      radio.dataset.variantIds = firmness.variants.map(v => v.id).join(',');
      radio.style.display = 'none';
      if (cardIndex === 0) radio.checked = true;
      card.appendChild(radio);
      
      card.addEventListener('click', async function() {
        document.querySelectorAll('.firmness-card').forEach(c => {
          c.classList.remove('selected');
          c.querySelector('.card-indicator').classList.remove('active');
        });
        this.classList.add('selected');
        indicator.classList.add('active');
        
        document.querySelectorAll('.firmness-card input[type="radio"]').forEach(r => r.checked = false);
        const radio = this.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          const variantIds = radio.dataset.variantIds.split(',').map(id => parseInt(id));
          const selectedVariants = productData.variants.filter(v => variantIds.includes(v.id));
          updateSizeOptionsForFirmness(selectedVariants, sizeIndex);
          currentFirmnessVariant = productData.variants.find(v => v.id === parseInt(radio.dataset.variantId));
          
          // Update variant info when firmness changes
          // Priority: 1) Selected size variant (if size is already selected), 2) First variant in firmness group
          let variantIdToUse = null;
          if (currentSizeVariant && selectedVariants.some(v => v.id === currentSizeVariant.id)) {
            // If current size variant is in the selected firmness group, use it
            variantIdToUse = currentSizeVariant.id.toString();
          } else if (selectedVariants && selectedVariants.length > 0) {
            // Otherwise use first variant in the firmness group
            variantIdToUse = selectedVariants[0].id.toString();
          }
          
          if (variantIdToUse) {
            updateVariantInfo(variantIdToUse);
            // Await so images update on first click (no need for second click)
            await updateVariantImageAndFeatures(variantIdToUse);
            updateVariantTechnicalSpecs(variantIdToUse);
            updateVariantPerformanceBlocks(variantIdToUse);
          }
          
          // Update variant name display
          updateVariantNameDisplay();
          
          // If foundation is selected, update foundation images based on new firmness selection
          // Otherwise, show mattress images
          if (isFoundationSelected && currentFoundationProduct) {
            const selectedFoundation = document.querySelector('input[name="foundation"]:checked');
            if (selectedFoundation) {
              const foundationOption = selectedFoundation.closest('.foundation-option');
              if (foundationOption) {
                handleFoundationVariantImageChange(foundationOption, currentFoundationProduct);
              }
            }
          } else if (!isFoundationSelected && currentMattressImages) {
            loadProductImages(currentMattressImages);
          }
          
          // Update button states
          updateButtonStates();
        }
      });
      
      firmnessContainer.appendChild(card);
      cardIndex++;
    });
    
    if (firstFirmnessVariant) {
      const firstFirmness = firmnessMap.values().next().value;
      updateSizeOptionsForFirmness(firstFirmness.variants, sizeIndex);
      
      // Update variant info for first firmness variant
      if (firstFirmness.variants && firstFirmness.variants.length > 0) {
        const firstVariantId = firstFirmness.variants[0].id.toString();
        updateVariantInfo(firstVariantId);
        updateVariantImageAndFeatures(firstVariantId);
        updateVariantTechnicalSpecs(firstVariantId);
        updateVariantPerformanceBlocks(firstVariantId);
      }
      
      updateVariantNameDisplay();
    }
  }
  
  // Function to update size options based on selected firmness
  function updateSizeOptionsForFirmness(variants, sizeIndex = 1) {
    if (!variants || variants.length === 0) {
      loadSizeOptions([]);
      return;
    }
    
    // Sort variants to preserve product page order (by position in original product variants array)
    const sortedVariants = [...variants].sort((a, b) => {
      if (!currentMattressProduct || !currentMattressProduct.variants) {
        return 0; // If no product data, keep original order
      }
      
      // Find index in original product variants array
      const aIndex = currentMattressProduct.variants.findIndex(v => v.id === a.id);
      const bIndex = currentMattressProduct.variants.findIndex(v => v.id === b.id);
      
      // If both found, sort by original position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one found, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // If neither found, keep original order
      return 0;
    });
    
    const sizeOptions = [];
    
    sortedVariants.forEach(variant => {
      let sizeValue = 'Standard';
      
      if (variant.options && variant.options[sizeIndex] !== undefined) {
        sizeValue = variant.options[sizeIndex];
      } else if (variant.option1 && sizeIndex === 0) {
        sizeValue = variant.option1;
      } else if (variant.option2 && sizeIndex === 1) {
        sizeValue = variant.option2;
      } else if (variant.option3 && sizeIndex === 2) {
        sizeValue = variant.option3;
      } else if (variant.title) {
        const titleParts = variant.title.split(' / ');
        if (titleParts.length > 1) {
          sizeValue = titleParts[1];
        } else if (titleParts.length === 1) {
          sizeValue = titleParts[0];
        }
      }
      
      sizeValue = sizeValue.toString();
      
      // IMPROVED availability check
      let isAvailable = false;
      let availabilityText = 'In Stock';
      
      // Check if variant.available is explicitly true
      if (variant.available === true) {
        isAvailable = true;
      }
      // Check inventory policy - if continue, always available for backorder
      else if (variant.inventory_policy === 'continue') {
        isAvailable = true;
        availabilityText = 'Available for Backorder';
      }
      // Check inventory quantity
      else if (variant.inventory_quantity !== null && variant.inventory_quantity > 0) {
        isAvailable = true;
        if (variant.inventory_quantity < 10) {
          availabilityText = `Only ${variant.inventory_quantity} left`;
        }
      }
      // If inventory management is disabled, check if available is not false
      else if (variant.inventory_management === null && variant.available !== false) {
        isAvailable = true;
      }
      
      sizeOptions.push({
        label: sizeValue,
        value: variant.id.toString(),
        variant: variant,
        price: variant.price,
        comparePrice: variant.compare_at_price,
        available: isAvailable,
        availabilityText: availabilityText,
        inventoryQuantity: variant.inventory_quantity,
        inventoryPolicy: variant.inventory_policy,
        inventoryManagement: variant.inventory_management
      });
    });
    
    const preferredSizeLabel = getCurrentMattressSizeValue();
    loadSizeOptions(sizeOptions, preferredSizeLabel);
  }
  
  /** Pick size when firmness changes: same size name if present, else first option in list. */
  function pickSizeOptionAfterFirmnessChange(sizeOptions, preferredSizeLabel) {
    if (!sizeOptions || sizeOptions.length === 0) return null;

    const preferred = (preferredSizeLabel || '').toString().trim();
    if (preferred) {
      const match = sizeOptions.find((so) => mattressSizesMatch(so.label, preferred));
      if (match) return match;
    }

    return sizeOptions[0];
  }

  // Function to load size options with improved availability display
  function loadSizeOptions(sizeOptions, preferredSizeLabel = '') {
    const sizeSelect = document.getElementById('size-select');
    if (!sizeSelect) return;
    
    sizeSelect.innerHTML = '';
    
    if (sizeOptions.length === 0) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'No sizes available';
      option.disabled = true;
      option.selected = true;
      sizeSelect.appendChild(option);
      currentSizeVariant = null;
    } else {
      // Use size options in the order they come from the product page (already sorted by updateSizeOptionsForFirmness)
      const preferred = (preferredSizeLabel || '').toString().trim();
      const selectedSizeOption = preferred
        ? pickSizeOptionAfterFirmnessChange(sizeOptions, preferred)
        : (sizeOptions.find((so) => so.available) || sizeOptions[0]);
      const selectedVariantId = selectedSizeOption ? selectedSizeOption.variant.id : null;
      
      sizeOptions.forEach((sizeOption) => {
        const option = document.createElement('option');
        option.value = sizeOption.variant.id;
        option.dataset.price = formatMoney(sizeOption.price);
        option.dataset.comparePrice = sizeOption.comparePrice ? formatMoney(sizeOption.comparePrice) : '';
        option.dataset.size = sizeOption.label;
        option.dataset.available = sizeOption.available;
        option.dataset.availabilityText = sizeOption.availabilityText;
        option.dataset.inventoryQuantity = sizeOption.inventoryQuantity;
        option.dataset.inventoryPolicy = sizeOption.inventoryPolicy;
        option.dataset.inventoryManagement = sizeOption.inventoryManagement;
        
        let displayText = sizeOption.label;
        const priceText = formatMoney(sizeOption.price);
        
        if (!sizeOption.available) {
          displayText = `${sizeOption.label} - Out of Stock`;
          option.disabled = true;
        } else {
          displayText = `${sizeOption.label} - ${priceText}`;
        }
        
        option.textContent = displayText;
        
        if (selectedVariantId && sizeOption.variant.id === selectedVariantId) {
          option.selected = true;
          currentSizeVariant = sizeOption.variant;
        }
        
        sizeSelect.appendChild(option);
      });

      if (!currentSizeVariant && sizeOptions.length > 0) {
        currentSizeVariant = sizeOptions[0].variant;
        if (sizeSelect.options.length > 0) {
          sizeSelect.options[0].selected = true;
        }
      }
    }
    
    if (typeof window.rebuildCustomDropdown === 'function') {
      // Run immediately so firmness/size change updates images on first click (no 100ms delay)
      window.rebuildCustomDropdown();
      updateProductForSelectedVariant();
      updateButtonStates();
    } else {
      updateProductForSelectedVariant();
      updateButtonStates();
    }
  }
  
  // Function to update total price (main product + foundation)
  function updateTotalPrice() {
    // Use more specific selector to target the correct price element
    const priceElement = document.querySelector('#custom-product-container .price-amount') || 
                         document.querySelector('.price-amount');
    
    if (!priceElement) {
      console.warn('⚠️ Price element not found');
      return;
    }
    
    // Get main product price - try multiple sources
    let mainProductPrice = 0;
    
    // Method 1: From currentSizeVariant
    if (currentSizeVariant && currentSizeVariant.price) {
      mainProductPrice = parseInt(currentSizeVariant.price);
    } 
    // Method 2: From size select dropdown
    else {
      const sizeSelect = document.getElementById('size-select');
      if (sizeSelect && sizeSelect.selectedIndex >= 0) {
        const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
        if (selectedOption) {
          // Try to get price from the variant object if available
          const variantId = selectedOption.value;
          if (currentMattressProduct && currentMattressProduct.variants) {
            const variant = currentMattressProduct.variants.find(v => v.id.toString() === variantId);
            if (variant && variant.price) {
              mainProductPrice = parseInt(variant.price);
            }
          }
          
          // Fallback: Try data-price attribute (formatted price)
          if (mainProductPrice === 0) {
            const priceAttr = selectedOption.getAttribute('data-price');
            if (priceAttr) {
              // Extract numeric value from formatted price string (e.g., "$1,299.00" -> 129900)
              // Remove $ and commas, then parse
              const priceStr = priceAttr.replace(/[$,]/g, '');
              const priceFloat = parseFloat(priceStr);
              if (!isNaN(priceFloat)) {
                mainProductPrice = Math.round(priceFloat * 100); // Convert to cents
              }
            }
          }
        }
      }
    }
    
    // Get foundation price - try multiple sources
    let foundationPrice = 0;
    const selectedFoundation = document.querySelector('input[name="foundation"]:checked');
    if (selectedFoundation) {
      // Method 1: From currentFoundationVariant (most reliable)
      if (currentFoundationVariant && currentFoundationVariant.price !== undefined && currentFoundationVariant.price !== null) {
        foundationPrice = parseInt(currentFoundationVariant.price) || 0;
      }
      // Method 2: From data-variant-price attribute
      else {
        const foundationPriceAttr = selectedFoundation.getAttribute('data-variant-price');
        if (foundationPriceAttr !== null && foundationPriceAttr !== '' && foundationPriceAttr !== 'null') {
          foundationPrice = parseInt(foundationPriceAttr) || 0;
        }
      }
    }
    
    // Calculate total
    const totalPrice = mainProductPrice + foundationPrice;
    
    // Update price display - use both textContent and innerHTML to ensure it updates
    const formattedPrice = formatMoney(totalPrice);
    
    // Try multiple methods to update the price
    if (priceElement) {
      priceElement.textContent = formattedPrice;
      priceElement.innerHTML = formattedPrice;
      
      // Also try to trigger a reflow to ensure the update is visible
      priceElement.style.display = 'none';
      priceElement.offsetHeight; // Trigger reflow
      priceElement.style.display = '';
    }

    updatePriceSavingsDisplay();
  }

  // Show "You save $X (Y% OFF)" from compare-at vs currently displayed sale price
  function updatePriceSavingsDisplay() {
    const savingsEl = document.getElementById('price-savings-badge');
    const comparePriceElement = document.querySelector('#custom-product-container .compare-at-price') ||
      document.querySelector('.compare-at-price');
    if (!savingsEl) return;

    let saleCents = 0;
    let compareCents = 0;

    if (currentSizeVariant) {
      saleCents = parseInt(currentSizeVariant.price, 10) || 0;
      compareCents = parseInt(currentSizeVariant.compare_at_price, 10) || 0;
    }

    let foundationPrice = 0;
    const selectedFoundation = document.querySelector('input[name="foundation"]:checked');
    if (selectedFoundation) {
      if (currentFoundationVariant && currentFoundationVariant.price !== undefined && currentFoundationVariant.price !== null) {
        foundationPrice = parseInt(currentFoundationVariant.price, 10) || 0;
      } else {
        const foundationPriceAttr = selectedFoundation.getAttribute('data-variant-price');
        if (foundationPriceAttr !== null && foundationPriceAttr !== '' && foundationPriceAttr !== 'null') {
          foundationPrice = parseInt(foundationPriceAttr, 10) || 0;
        }
      }
    }

    // Match on-screen sale (mattress + foundation) against mattress compare-at
    const displayedSaleCents = saleCents + foundationPrice;

    if (compareCents > saleCents) {
      if (comparePriceElement) {
        comparePriceElement.textContent = formatMoney(compareCents);
        comparePriceElement.style.display = 'inline';
      }
      if (compareCents > displayedSaleCents) {
        const saveCents = compareCents - displayedSaleCents;
        savingsEl.textContent = 'You save ' + formatMoney(saveCents);
        savingsEl.style.display = 'inline-flex';
      } else {
        savingsEl.textContent = '';
        savingsEl.style.display = 'none';
      }
    } else {
      savingsEl.textContent = '';
      savingsEl.style.display = 'none';
      if (comparePriceElement) {
        comparePriceElement.textContent = '';
        comparePriceElement.style.display = 'none';
      }
    }
  }
  
  function updateButtonStates() {
    const addToCartBtn = document.getElementById('add-to-cart');
    const buyNowBtn = document.getElementById('buy-now');
    
    if (!addToCartBtn || !buyNowBtn) return;
    
    // Update total price first
    updateTotalPrice();
    
    // Check mattress variant availability
    let isMattressAvailable = false;
    if (currentSizeVariant) {
      isMattressAvailable = 
        currentSizeVariant.available === true ||
        currentSizeVariant.inventory_policy === 'continue' ||
        (currentSizeVariant.inventory_quantity !== null && currentSizeVariant.inventory_quantity > 0) ||
        (currentSizeVariant.inventory_management === null && currentSizeVariant.available !== false);
    }
    
    // Foundation is optional — only block checkout when customer chose a purchasable foundation that is unavailable
    let isFoundationAvailable = true;
    if (isFoundationSelected) {
      const selectedFoundation = document.querySelector('input[name="foundation"]:checked');
      if (!isPurchasableFoundationInput(selectedFoundation)) {
        isFoundationAvailable = true;
      } else {
        const isAvailable = selectedFoundation.getAttribute('data-variant-available') === 'true';
        const inventoryPolicy = selectedFoundation.getAttribute('data-variant-inventory-policy');
        const inventoryQuantity = selectedFoundation.getAttribute('data-variant-inventory');
        const inventoryManagement = selectedFoundation.getAttribute('data-variant-inventory-management');

        if (isAvailable === true) {
          isFoundationAvailable = true;
        } else if (inventoryPolicy === 'continue') {
          isFoundationAvailable = true;
        } else if (inventoryQuantity && parseInt(inventoryQuantity, 10) > 0) {
          isFoundationAvailable = true;
        } else if (inventoryManagement === 'null' || inventoryManagement === null) {
          isFoundationAvailable = true;
        } else {
          isFoundationAvailable = false;
        }
      }
    }

    const isAllAvailable = isMattressAvailable && isFoundationAvailable;
    
    if (!isAllAvailable) {
      addToCartBtn.textContent = 'Out of Stock';
      addToCartBtn.disabled = true;
      addToCartBtn.classList.add('out-of-stock');
      
      buyNowBtn.disabled = true;
      buyNowBtn.classList.add('out-of-stock');
    } else {
      addToCartBtn.textContent = 'Add to Cart';
      addToCartBtn.disabled = false;
      addToCartBtn.classList.remove('out-of-stock');
      
      buyNowBtn.disabled = false;
      buyNowBtn.classList.remove('out-of-stock');
    }

    if (document.getElementById('custom-product-container') && document.querySelector('#md-sticky-atc')) {
      updateStickyATC();
    }
  }

  function updateStickyATC() {
    const sticky = document.querySelector('#md-sticky-atc');
    if (!sticky || !currentMattressProduct) return;
    const titleEl = sticky.querySelector('.product__title');
    const imgEl = sticky.querySelector('.product-content img');
    const selectionsEl = document.getElementById('sticky-atc-selections');
    const qtyWrap = sticky.querySelector('.sticky-atc-quantity-wrap');

    if (titleEl) titleEl.textContent = currentMattressProduct.title || '';
    if (imgEl) {
      const mainImg = document.getElementById('main-image');
      if (mainImg && mainImg.src && mainImg.src.trim()) {
        imgEl.src = mainImg.src;
        if (mainImg.alt) imgEl.alt = mainImg.alt;
      } else {
        const selectedFoundation = document.querySelector('input[name="foundation"]:checked');
        const foundationOption = selectedFoundation ? selectedFoundation.closest('.foundation-option') : null;
        const foundationImageUrl = foundationOption ? foundationOption.getAttribute('data-variant-image') : null;
        if (foundationImageUrl && foundationImageUrl.trim()) {
          imgEl.src = foundationImageUrl.trim();
        } else if (currentMattressProduct.featured_image && currentMattressProduct.featured_image.src) {
          imgEl.src = currentMattressProduct.featured_image.src;
        } else if (currentMattressProduct.images && currentMattressProduct.images[0] && currentMattressProduct.images[0].src) {
          imgEl.src = currentMattressProduct.images[0].src;
        }
      }
    }
    // Update sticky price: only set sale price; show compare-at only when it is greater than variant price
    const mainPriceEl = document.querySelector('#custom-product-container .price-amount');
    const mainCompareEl = document.querySelector('#custom-product-container .compare-at-price');
    const priceContainer = sticky.querySelector('.sticky-atc-product-details .price');
    if (priceContainer) {
      const salePriceEl = priceContainer.querySelector('.price-item--sale, .price-item--last');
      const compareWrap = priceContainer.querySelector('.price__sale s.price-item--regular, .price__sale .price-item--regular');
      const saleBadge = priceContainer.querySelector('.price__badge-sale');
      const formattedPrice = (mainPriceEl && mainPriceEl.textContent) ? mainPriceEl.textContent.trim() : (currentSizeVariant ? formatMoney(currentSizeVariant.price) : '');
      if (salePriceEl && formattedPrice) salePriceEl.textContent = formattedPrice;
      var showCompare = false;
      var formattedCompare = '';
      if (currentSizeVariant && currentSizeVariant.compare_at_price && parseInt(currentSizeVariant.compare_at_price, 10) > parseInt(currentSizeVariant.price, 10)) {
        showCompare = true;
        formattedCompare = formatMoney(currentSizeVariant.compare_at_price);
      } else if (mainCompareEl && mainCompareEl.style.display !== 'none' && mainCompareEl.textContent && mainPriceEl && mainPriceEl.textContent) {
        var compareText = mainCompareEl.textContent.trim();
        var priceText = mainPriceEl.textContent.trim();
        if (compareText && compareText !== priceText) {
          showCompare = true;
          formattedCompare = compareText;
        }
      }
      if (compareWrap) {
        if (showCompare && formattedCompare) {
          compareWrap.textContent = formattedCompare;
          if (compareWrap.closest('span')) compareWrap.closest('span').style.display = '';
          priceContainer.classList.add('price--on-sale');
        } else {
          compareWrap.textContent = '';
          if (compareWrap.closest('span')) compareWrap.closest('span').style.display = 'none';
          priceContainer.classList.remove('price--on-sale');
        }
      } else {
        if (showCompare) priceContainer.classList.add('price--on-sale');
        else priceContainer.classList.remove('price--on-sale');
      }
      if (saleBadge) saleBadge.style.display = showCompare ? '' : 'none';
    }

    if (selectionsEl) {
      const variantNameEl = document.getElementById('variant-name-display');
      const foundationNameEl = document.getElementById('foundation-name-display');
      const sizeSelect = document.getElementById('size-select');
      const parts = [];
      if (sizeSelect && sizeSelect.selectedIndex >= 0) {
        const sizeOption = sizeSelect.options[sizeSelect.selectedIndex];
        if (sizeOption && sizeOption.text) parts.push(sizeOption.text.trim());
      }
      if (variantNameEl && variantNameEl.textContent) parts.push(variantNameEl.textContent.trim());
      if (foundationNameEl && foundationNameEl.textContent) {
        const foundationText = foundationNameEl.textContent.trim();
        if (foundationText) parts.push('Foundation: ' + foundationText);
      }
      if (parts.length > 0) {
        selectionsEl.textContent = parts.join(' · ');
        selectionsEl.style.display = '';
      } else {
        selectionsEl.style.display = 'none';
      }
    }
    if (qtyWrap) qtyWrap.style.display = 'none';
  }
  
  // Function to update browser address bar URL
  function updateProductUrlDisplay(productHandle) {
    const baseUrl = window.location.origin;
    const productUrl = `${baseUrl}/products/${productHandle}`;
    
    // Update browser address bar without page reload
    if (window.history && window.history.replaceState) {
      window.history.replaceState({ path: productUrl }, '', productUrl);
    }
  }
  
  // Tab switching for mattress products
  const tabs = document.querySelectorAll("#custom-product-container .tab");
  
  tabs.forEach((tab) => {
    tab.addEventListener("click", async function (e) {
      // Save current scroll position to prevent auto-scroll
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      tabs.forEach((t) => {
        t.classList.remove("active");
        // Reset indicator for inactive tabs
        const indicator = t.querySelector('.tab-indicator');
        if (indicator) {
          indicator.style.backgroundColor = '#d9d9d9';
          indicator.style.borderColor = '#0b1f3a';
        }
        // Reset border to default 1px for inactive tabs
        t.style.borderWidth = '1px';
        t.style.borderStyle = 'solid';
      });
      
      this.classList.add("active");
      
      // Update tab indicator color for active tab (make it checked/active)
      const tabColor = this.getAttribute('data-tab-color');
      const indicator = this.querySelector('.tab-indicator');
      if (indicator && tabColor && tabColor.trim() !== '' && tabColor !== 'null' && tabColor !== '#') {
        indicator.style.backgroundColor = tabColor.trim();
        indicator.style.borderColor = tabColor.trim();
      } else if (indicator) {
        // Fallback to default active color
        indicator.style.backgroundColor = '#0066cc';
        indicator.style.borderColor = '#0066cc';
      }
      
      // Set 2px solid border with tab color for active tab
      if (tabColor && tabColor.trim() !== '' && tabColor !== 'null' && tabColor !== '#') {
        this.style.borderWidth = '2px';
        this.style.borderStyle = 'solid';
        this.style.borderColor = tabColor.trim();
        this.style.color = tabColor.trim();
      }
      
      const productHandle = this.dataset.tab;
      
      // Update browser address bar URL
      if (productHandle) {
        updateProductUrlDisplay(productHandle);
      }
      
      updateTitleLastWordColor();
      
      try {
        // Fetch product with metafields
        const response = await fetch(`/products/${productHandle}.js`);
        if (response.ok) {
          const productData = await response.json();
          // Fetch variant metafields separately if needed
          if (productData.variants && productData.variants.length > 0) {
            await fetchVariantMetafields(productData);
          }
          await updateMattressProduct(productData, this);
          
          if (document.getElementById('custom-product-container') && document.querySelector('#md-sticky-atc')) {
            updateStickyATC();
            setTimeout(function() {
              updateStickyATC();
            }, 450);
          }
          
          // Restore scroll position after product update to prevent auto-scroll
          window.scrollTo(0, scrollPosition);
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPosition);
          });
        } else {
          console.error('Failed to fetch product:', response.status);
        }
      } catch (error) {
        console.error('Error loading product data:', error);
      }
    });
  });
  
  // Handle browser back/forward buttons
  window.addEventListener('popstate', function(event) {
    // If user navigates back/forward, update the active tab based on URL
    const currentPath = window.location.pathname;
    const productHandle = currentPath.replace('/products/', '');
    
    if (productHandle) {
      const matchingTab = document.querySelector(`#custom-product-container .tab[data-tab="${productHandle}"]`);
      if (matchingTab && !matchingTab.classList.contains('active')) {
        matchingTab.click();
      }
    }
  });
  
  // Size selection handler
  const sizeSelect = document.getElementById("size-select");
  if (sizeSelect) {
    sizeSelect.addEventListener("change", function () {
      updateProductForSelectedVariant();
      updateButtonStates();
    });
  }

  // Function to update variant name display
  function updateVariantNameDisplay() {
    const variantNameDisplay = document.getElementById('variant-name-display');
    const foundationNameDisplay = document.getElementById('foundation-name-display');
    
    if (!variantNameDisplay) return;
    
    // Get current firmness variant name
    if (currentFirmnessVariant) {
      const { firmnessIndex } = detectOptionTypes(currentMattressProduct);
      let firmnessValue = 'Standard';
      
      if (currentFirmnessVariant.options && currentFirmnessVariant.options[firmnessIndex] !== undefined) {
        firmnessValue = currentFirmnessVariant.options[firmnessIndex];
      } else if (currentFirmnessVariant.option1 && firmnessIndex === 0) {
        firmnessValue = currentFirmnessVariant.option1;
      } else if (currentFirmnessVariant.option2 && firmnessIndex === 1) {
        firmnessValue = currentFirmnessVariant.option2;
      } else if (currentFirmnessVariant.option3 && firmnessIndex === 2) {
        firmnessValue = currentFirmnessVariant.option3;
      }
      
      variantNameDisplay.textContent = firmnessValue;
      variantNameDisplay.style.display = 'block';
    } else {
      variantNameDisplay.style.display = 'none';
    }
    
    // Get foundation name if selected
    if (foundationNameDisplay) {
      const selectedFoundation = document.querySelector('input[name="foundation"]:checked');
      if (selectedFoundation) {
        const foundationTitle = selectedFoundation.getAttribute('data-variant-title') || '';
        if (foundationTitle) {
          foundationNameDisplay.textContent = `with ${foundationTitle}`;
          foundationNameDisplay.style.display = 'block';
        } else {
          foundationNameDisplay.style.display = 'none';
        }
      } else {
        foundationNameDisplay.style.display = 'none';
      }
    }
  }

  // Function to update variant info (short, long) from variant info metafields
  function updateVariantInfo(variantId) {
    if (!variantId) return;
    const variantIdStr = variantId.toString();
    const variantInfo = variantInfoMetafieldsMap.get(variantIdStr);
    
    // Update firmness card title with variant title
    if (variantInfo && variantInfo.title && variantInfo.title.trim() !== '') {
      const firmnessCardTitle = document.querySelector('.firmness-card.selected .firmness-card-title');
      if (firmnessCardTitle) {
        firmnessCardTitle.textContent = variantInfo.title.trim();
      }
    }
    
    // Update product description with short text
    const descriptionElement = document.querySelector('.product-description');
    if (descriptionElement && variantInfo && variantInfo.short && variantInfo.short.trim() !== '') {
      descriptionElement.innerHTML = variantInfo.short.trim();
    } else if (descriptionElement && (!variantInfo || !variantInfo.short)) {
      // Fallback to product description if no variant info
      if (currentMattressProduct && currentMattressProduct.description) {
        const strippedDescription = stripHtml(currentMattressProduct.description || '');
        const truncatedDescription = truncateWords(strippedDescription, 30);
        descriptionElement.textContent = truncatedDescription;
      }
    }
    
    // Update meta variant description with long text (preserve spaces and line breaks)
    const metaVariantDescription = document.getElementById('meta-varaint-description');
    if (metaVariantDescription && variantInfo && variantInfo.long && variantInfo.long.trim() !== '') {
      var longText = variantInfo.long.trim();
      if (longText.indexOf('<') === -1 || longText.indexOf('>') === -1) {
        longText = longText.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
      }
      metaVariantDescription.innerHTML = longText;
    } else if (metaVariantDescription) {
      metaVariantDescription.innerHTML = '';
    }
  }
  
  // Function to update variant image and features section
  async function updateVariantImageAndFeatures(variantId) {
    if (!variantId) return;
    
    // Check if section exists on page
    // Try multiple selectors for image element
    let imageElement = document.getElementById('variant-image-data');

    if (!imageElement) {
      imageElement = document.querySelector('[data-attribute="variant-image-data"]');
    }
    if (!imageElement) {
      imageElement = document.querySelector('[id="variant-image-data"]');
    }
    // Also try finding by wrapper class
    if (!imageElement) {
      const wrapper = document.querySelector('.custom-image-feature__image-wrapper');
      if (wrapper) {
        imageElement = wrapper;
      }
    }
    const headingElements = document.querySelectorAll('#variant-data-heading');
    const descriptionElements = document.querySelectorAll('#variant-data-description');
    
    if (!imageElement && headingElements.length === 0 && descriptionElements.length === 0) {
      // Section not present on page, exit
      return;
    }
    
    try {
      // Get variant metafield data from map (loaded from Liquid template)
      const variantIdStr = variantId.toString();
      let variantMetafieldData = variantImageFeaturesMap.get(variantIdStr);
    
      
      // Try alternative variant ID formats
      if (!variantMetafieldData) {
        const variantIdNum = parseInt(variantIdStr);
        if (!isNaN(variantIdNum)) {
          variantMetafieldData = variantImageFeaturesMap.get(variantIdNum.toString()) || 
                                 variantImageFeaturesMap.get(String(variantIdNum));
        }
      }
      
      // If not found in map, try to get from current variant object
      if (!variantMetafieldData && currentMattressProduct && currentMattressProduct.variants) {
        const variant = currentMattressProduct.variants.find(v => 
          v.id.toString() === variantIdStr || 
          v.id === parseInt(variantIdStr) ||
          String(v.id) === variantIdStr
        );
        if (variant && variant.metafields && variant.metafields.custom && variant.metafields.custom.variant_image_and_features) {
          variantMetafieldData = variant.metafields.custom.variant_image_and_features;
          // Handle metaobject - get value if it exists
          if (variantMetafieldData && typeof variantMetafieldData === 'object' && variantMetafieldData.value) {
            variantMetafieldData = variantMetafieldData.value;
          }
        }
      }
      
      // Validate variant data - check if it has actual content
      let hasVariantData = false;
      if (variantMetafieldData) {
        // Check if image exists and is valid - try both "image" and "Image" (case-insensitive)
        const imageField = variantMetafieldData.image || variantMetafieldData.Image;
        const hasImage = imageField && 
                        imageField !== null && 
                        imageField !== 'null' &&
                        imageField !== '';
        
        // Check if heading exists and is valid
        let headingData = variantMetafieldData.heading;
        if (headingData && typeof headingData === 'object' && headingData.value) {
          headingData = headingData.value;
        }
        const hasHeading = headingData && 
                          headingData !== null && 
                          headingData !== 'null' &&
                          (Array.isArray(headingData) ? headingData.length > 0 : String(headingData).trim() !== '');
        
        // Check if description exists and is valid
        let descData = variantMetafieldData.description;
        if (descData && typeof descData === 'object' && descData.value) {
          descData = descData.value;
        }
        const hasDescription = descData && 
                              descData !== null && 
                              descData !== 'null' &&
                              (Array.isArray(descData) ? descData.length > 0 : String(descData).trim() !== '');
        
        hasVariantData = hasImage || hasHeading || hasDescription;
      }
      
      // If no valid variant data, set to null to use customizer data
      if (!hasVariantData) {
        variantMetafieldData = null;
      }
      
      // Update image if variant has image data
      if (imageElement) {
        // Hide all customizer images and placeholder first
        const customizerImages = imageElement.querySelectorAll('img:not([data-variant-image])');
        const placeholder = imageElement.querySelector('.custom-image-feature__image-placeholder');
        
        customizerImages.forEach(img => {
          img.style.cssText = 'display: none !important; visibility: hidden !important;';
        });
        if (placeholder) {
          placeholder.style.cssText = 'display: none !important; visibility: hidden !important;';
        }
        
        // Check if variant has valid image data - try both "image" and "Image" (case-insensitive)
        let imageField = null;
        if (variantMetafieldData) {
          // Try lowercase first, then uppercase
          imageField = variantMetafieldData.image || variantMetafieldData.Image;
          
          // Also check all keys case-insensitively
          if (!imageField) {
            for (const key in variantMetafieldData) {
              if (key.toLowerCase() === 'image') {
                imageField = variantMetafieldData[key];
                break;
              }
            }
          }
        }
        
        if (imageField && 
            imageField !== null && 
            imageField !== 'null' &&
            imageField !== '' &&
            imageField !== undefined) {
          let imgSrc = '';
          
          // Handle different image formats - check all possible structures
          const imageData = imageField;
          
          // First, try direct string
          if (typeof imageData === 'string' && imageData.trim() !== '' && imageData !== 'null' && !imageData.includes('gid://')) {
            imgSrc = imageData.trim();
          }
          // If object, try various properties
          else if (imageData && typeof imageData === 'object' && imageData !== null) {
            // Try direct properties first (src, url)
            if (imageData.src && typeof imageData.src === 'string' && imageData.src.trim() !== '' && !imageData.src.includes('gid://')) {
              imgSrc = imageData.src.trim();
            } else if (imageData.url && typeof imageData.url === 'string' && imageData.url.trim() !== '' && !imageData.url.includes('gid://')) {
              imgSrc = imageData.url.trim();
            } 
            // Try preview property (Shopify media format) - check first as it's common
            else if (imageData.preview) {
              if (imageData.preview.src && typeof imageData.preview.src === 'string' && imageData.preview.src.trim() !== '' && !imageData.preview.src.includes('gid://')) {
                imgSrc = imageData.preview.src.trim();
              } else if (imageData.preview.url && typeof imageData.preview.url === 'string' && imageData.preview.url.trim() !== '' && !imageData.preview.url.includes('gid://')) {
                imgSrc = imageData.preview.url.trim();
              }
            }
            // Try value property (for metaobject references)
            else if (imageData.value) {
              const imgValue = imageData.value;
              if (typeof imgValue === 'string' && imgValue.trim() !== '' && imgValue !== 'null' && !imgValue.includes('gid://')) {
                imgSrc = imgValue.trim();
              } else if (imgValue && typeof imgValue === 'object' && imgValue !== null) {
                // Nested object - try src, url, or preview
                if (imgValue.src && typeof imgValue.src === 'string' && imgValue.src.trim() !== '' && !imgValue.src.includes('gid://')) {
                  imgSrc = imgValue.src.trim();
                } else if (imgValue.url && typeof imgValue.url === 'string' && imgValue.url.trim() !== '' && !imgValue.url.includes('gid://')) {
                  imgSrc = imgValue.url.trim();
                } else if (imgValue.preview && imgValue.preview.src) {
                  // Try preview.src for Shopify media objects
                  imgSrc = imgValue.preview.src;
                }
              }
            }
          }
          
          // Validate image URL - must be valid HTTP/HTTPS URL, not gid://
          // Also check if it's a Shopify CDN URL (cdn.shopify.com)
          const isValidUrl = imgSrc && 
                            imgSrc.trim() !== '' && 
                            !imgSrc.includes('gid://') && 
                            imgSrc !== 'null' && 
                            (imgSrc.startsWith('http') || 
                             imgSrc.startsWith('//') || 
                             imgSrc.startsWith('/') ||
                             imgSrc.includes('cdn.shopify.com'));
          
          if (isValidUrl) {
            // Check if variant image already exists
            let variantImg = imageElement.querySelector('img[data-variant-image]');
            if (variantImg) {
              variantImg.src = imgSrc;
              // Create proper srcset for responsive images
              const baseUrl = imgSrc.split('?')[0]; // Remove query params for srcset
              variantImg.srcset = `${baseUrl}?width=350 350w, ${baseUrl}?width=750 750w, ${baseUrl}?width=1100 1100w, ${imgSrc} ${imgSrc.includes('width=') ? '' : '1100w'}`;
              variantImg.style.cssText = 'display: block !important; visibility: visible !important; width: 100% !important; height: auto !important;';
            } else {
              // Create variant image if doesn't exist
              variantImg = document.createElement('img');
              variantImg.src = imgSrc;
              const baseUrl = imgSrc.split('?')[0];
              variantImg.srcset = `${baseUrl}?width=350 350w, ${baseUrl}?width=750 750w, ${baseUrl}?width=1100 1100w, ${imgSrc} ${imgSrc.includes('width=') ? '' : '1100w'}`;
              variantImg.className = 'custom-image-feature__image';
              variantImg.loading = 'lazy';
              variantImg.setAttribute('data-variant-image', 'true');
              variantImg.alt = 'Variant image';
              variantImg.style.cssText = 'display: block !important; visibility: visible !important; width: 100% !important; height: auto !important;';
              imageElement.appendChild(variantImg);
            }
            // Ensure customizer images and placeholder are hidden
            customizerImages.forEach(img => {
              img.style.cssText = 'display: none !important; visibility: hidden !important;';
            });
            if (placeholder) {
              placeholder.style.cssText = 'display: none !important; visibility: hidden !important;';
            }
          } else {
            // No valid variant image - show customizer images or placeholder
            const variantImg = imageElement.querySelector('img[data-variant-image]');
            if (variantImg) {
              variantImg.style.cssText = 'display: none !important; visibility: hidden !important;';
            }
            // Show customizer image if exists, otherwise show placeholder
            if (customizerImages.length > 0) {
              customizerImages.forEach(img => {
                img.style.cssText = 'display: block !important; visibility: visible !important;';
              });
              if (placeholder) {
                placeholder.style.cssText = 'display: none !important; visibility: hidden !important;';
              }
            } else if (placeholder) {
              placeholder.style.cssText = 'display: block !important; visibility: visible !important;';
            }
          }
        } else {
          // No variant image data - show customizer images or placeholder
          const variantImg = imageElement.querySelector('img[data-variant-image]');
          if (variantImg) {
            variantImg.style.cssText = 'display: none !important; visibility: hidden !important;';
          }
          // Show customizer image if exists, otherwise show placeholder
          if (customizerImages.length > 0) {
            customizerImages.forEach(img => {
              img.style.cssText = 'display: block !important; visibility: visible !important;';
            });
            if (placeholder) {
              placeholder.style.cssText = 'display: none !important; visibility: hidden !important;';
            }
          } else if (placeholder) {
            placeholder.style.cssText = 'display: block !important; visibility: visible !important;';
          }
        }
      }
      
      // Update heading and description if variant has data (handle list format)
      const blocksContainer = document.querySelector('.custom-image-feature__blocks-container');
      if (blocksContainer) {
        if (variantMetafieldData) {
          let headingData = variantMetafieldData.heading;
          let descData = variantMetafieldData.description;
          
          // Handle metaobject values
          if (headingData && typeof headingData === 'object' && headingData.value) {
            headingData = headingData.value;
          }
          if (descData && typeof descData === 'object' && descData.value) {
            descData = descData.value;
          }
          
          // Convert to arrays if needed
          let headingArray = [];
          let descArray = [];
          
          if (Array.isArray(headingData)) {
            headingArray = headingData;
          } else if (headingData && typeof headingData === 'string' && headingData.trim() !== '') {
            headingArray = [headingData.trim()];
          }
          
          if (Array.isArray(descData)) {
            descArray = descData;
          } else if (descData && typeof descData === 'string' && descData.trim() !== '') {
            descArray = [descData.trim()];
          }
          
          // Determine max length - use metafield data length, not customizer blocks
          const maxLength = Math.max(headingArray.length, descArray.length);
          
          // Hide all existing blocks first (but don't remove them yet)
          const existingBlocks = blocksContainer.querySelectorAll('.custom-image-feature__block');
          existingBlocks.forEach(block => {
            block.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; margin: 0 !important; padding: 0 !important;';
          });
          
          // Create or update blocks for each data pair - ensure loop runs completely
          if (maxLength > 0) {
            for (let i = 0; i < maxLength; i++) {
              const headingText = headingArray[i] ? String(headingArray[i]).trim() : '';
              const descText = descArray[i] ? String(descArray[i]).trim() : '';
              
              // Skip if both heading and description are empty
              if (!headingText && !descText) {
                // Still hide the block if it exists
                if (existingBlocks[i]) {
                  existingBlocks[i].style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; margin: 0 !important; padding: 0 !important;';
                }
                continue;
              }
              
              // Get or create block element
              let blockElement = existingBlocks[i];
              if (!blockElement) {
                // Create new block if needed
                blockElement = document.createElement('div');
                blockElement.className = 'custom-image-feature__block pdp-border-color';
                blockElement.setAttribute('data-variant-block', 'true');
                const blockInner = document.createElement('div');
                blockInner.className = 'custom-image-feature__block-inner';
                blockElement.appendChild(blockInner);
                blocksContainer.appendChild(blockElement);
              }
              
              // Show block properly - remove all inline styles but keep classes
              blockElement.style.cssText = '';
              blockElement.removeAttribute('style');
              // Ensure classes are present
              if (!blockElement.classList.contains('custom-image-feature__block')) {
                blockElement.classList.add('custom-image-feature__block');
              }
              if (!blockElement.classList.contains('pdp-border-color')) {
                blockElement.classList.add('pdp-border-color');
              }
              
              let blockInner = blockElement.querySelector('.custom-image-feature__block-inner');
              if (!blockInner) {
                blockInner = document.createElement('div');
                blockInner.className = 'custom-image-feature__block-inner';
                blockElement.appendChild(blockInner);
              }
              
              // Update or create heading
              if (headingText) {
                let headingEl = blockInner.querySelector('.custom-image-feature__block-heading');
                if (!headingEl) {
                  headingEl = document.createElement('p');
                  headingEl.className = 'custom-image-feature__block-heading pdp-text-color';
                  blockInner.insertBefore(headingEl, blockInner.firstChild);
                }
                headingEl.textContent = headingText;
                headingEl.setAttribute('data-variant-heading', 'true');
                headingEl.style.cssText = '';
                headingEl.removeAttribute('style');
                // Ensure classes are present
                if (!headingEl.classList.contains('custom-image-feature__block-heading')) {
                  headingEl.classList.add('custom-image-feature__block-heading');
                }
                if (!headingEl.classList.contains('pdp-text-color')) {
                  headingEl.classList.add('pdp-text-color');
                }
                // Re-apply current PDP text color (we cleared style above, so pdp-text-color would show accent otherwise)
                if (headingEl.classList.contains('pdp-text-color')) {
                  const activeTab = document.querySelector('.mattress-type-tabs .tab.active');
                  const tabColor = activeTab && activeTab.getAttribute('data-tab-color');
                  if (tabColor && tabColor.trim() !== '' && tabColor !== 'null') {
                    headingEl.style.color = tabColor.trim();
                    headingEl.style.setProperty('--pdp-text-color', tabColor.trim());
                  }
                }
              } else {
                // Hide heading if empty
                const headingEl = blockInner.querySelector('.custom-image-feature__block-heading');
                if (headingEl) {
                  if (headingEl.hasAttribute('data-variant-heading')) {
                    headingEl.style.cssText = 'display: none !important;';
                  }
                }
              }
              
              // Update or create description
              if (descText) {
                let descEl = blockInner.querySelector('.custom-image-feature__block-content');
                if (!descEl) {
                  descEl = document.createElement('div');
                  descEl.className = 'custom-image-feature__block-content';
                  blockInner.appendChild(descEl);
                }
                descEl.innerHTML = descText;
                descEl.setAttribute('data-variant-content', 'true');
                descEl.style.cssText = '';
                descEl.removeAttribute('style');
              } else {
                // Hide description if empty
                const descEl = blockInner.querySelector('.custom-image-feature__block-content');
                if (descEl) {
                  if (descEl.hasAttribute('data-variant-content')) {
                    descEl.style.cssText = 'display: none !important;';
                  }
                }
              }
            }
          }
        } else {
          // No variant data or invalid data - show customizer blocks
          const existingBlocks = blocksContainer.querySelectorAll('.custom-image-feature__block');
          existingBlocks.forEach(block => {
            block.style.cssText = '';
            block.removeAttribute('style');
          });
          
          // Hide any variant-created blocks
          const variantBlocks = blocksContainer.querySelectorAll('.custom-image-feature__block[data-variant-block]');
          variantBlocks.forEach(block => {
            block.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; margin: 0 !important; padding: 0 !important;';
          });
        }
      }
      
    } catch (e) {
      // Silent error - fallback to customizer data
    }
  }

  // Function to update variant technical specifications section
  function updateVariantTechnicalSpecs(variantId) {
    if (!variantId) return;
    
    // Check if section exists on page (use class - section has .custom-text-feature__features)
    const featuresContainer = document.querySelector('.custom-text-feature__features');
    if (!featuresContainer) {
      return;
    }
    const titleElements = featuresContainer.querySelectorAll('.custom-text-feature__feature-label');
    const featureElements = featuresContainer.querySelectorAll('.custom-text-feature__feature-value');
    
    if (titleElements.length === 0 && featureElements.length === 0) {
      return;
    }
    
    try {
      // Get variant metafield data from map (loaded from Liquid template)
      const variantIdStr = variantId.toString();
      let variantSpecsData = variantTechnicalSpecsMap.get(variantIdStr);
      
      // Try alternative variant ID formats
      if (!variantSpecsData) {
        const variantIdNum = parseInt(variantIdStr);
        if (!isNaN(variantIdNum)) {
          variantSpecsData = variantTechnicalSpecsMap.get(variantIdNum.toString()) || 
                            variantTechnicalSpecsMap.get(String(variantIdNum));
        }
      }
      
      // If not found in map, try to get from current variant object
      if (!variantSpecsData && currentMattressProduct && currentMattressProduct.variants) {
        const variant = currentMattressProduct.variants.find(v => 
          v.id.toString() === variantIdStr || 
          v.id === parseInt(variantIdStr) ||
          String(v.id) === variantIdStr
        );
        if (variant && variant.metafields && variant.metafields.custom && variant.metafields.custom.technical_specifications_variants) {
          variantSpecsData = variant.metafields.custom.technical_specifications_variants;
          // Handle metaobject - get value if it exists
          if (variantSpecsData && typeof variantSpecsData === 'object' && variantSpecsData.value) {
            variantSpecsData = variantSpecsData.value;
          }
        }
      }
      
      // Validate variant data - check if it has actual content (support titles/headings)
      let hasVariantData = false;
      if (variantSpecsData) {
        let titlesData = variantSpecsData.titles || variantSpecsData.headings;
        if (titlesData && typeof titlesData === 'object' && titlesData.value) {
          titlesData = titlesData.value;
        }
        const hasTitles = titlesData && 
                         titlesData !== null && 
                         titlesData !== 'null' &&
                         (Array.isArray(titlesData) ? titlesData.length > 0 : String(titlesData).trim() !== '');
        
        let featuresData = variantSpecsData.features || variantSpecsData.feature;
        if (featuresData && typeof featuresData === 'object' && featuresData.value) {
          featuresData = featuresData.value;
        }
        const hasFeatures = featuresData && 
                           featuresData !== null && 
                           featuresData !== 'null' &&
                           (Array.isArray(featuresData) ? featuresData.length > 0 : String(featuresData).trim() !== '');
        
        hasVariantData = hasTitles || hasFeatures;
      }
      
      // If no valid variant data, set to null to use customizer data
      if (!hasVariantData) {
        variantSpecsData = null;
      }
      
      // Get all existing feature items (featuresContainer already set above)
      const existingItems = featuresContainer.querySelectorAll('.custom-text-feature__feature-item');
      
      // Store original customizer data if not already stored (store per item)
      existingItems.forEach((item, idx) => {
        const titleEl = item.querySelector('.custom-text-feature__feature-label');
        const featureEl = item.querySelector('.custom-text-feature__feature-value');
        
        if (titleEl && !titleEl.hasAttribute('data-customizer-title') && titleEl.textContent && titleEl.textContent.trim() !== '') {
          titleEl.setAttribute('data-customizer-title', titleEl.textContent);
        }
        if (featureEl && !featureEl.hasAttribute('data-customizer-feature') && featureEl.textContent && featureEl.textContent.trim() !== '') {
          featureEl.setAttribute('data-customizer-feature', featureEl.textContent);
        }
      });
      
      if (variantSpecsData) {
        // Get titles and features arrays (support titles/headings and features/feature)
        let titlesData = variantSpecsData.titles || variantSpecsData.headings;
        let featuresData = variantSpecsData.features || variantSpecsData.feature;
        
        // Handle metaobject values
        if (titlesData && typeof titlesData === 'object' && titlesData.value) {
          titlesData = titlesData.value;
        }
        if (featuresData && typeof featuresData === 'object' && featuresData.value) {
          featuresData = featuresData.value;
        }
        
        // Convert to arrays – one entry per row (title 1, feature 1 | title 2, feature 2 | ...)
        let titlesArray = [];
        let featuresArray = [];
        
        if (Array.isArray(titlesData)) {
          titlesArray = titlesData.map(t => t == null || t === 'null' || t === undefined ? '' : String(t).trim());
        } else if (titlesData && typeof titlesData === 'string' && titlesData !== 'null') {
          titlesArray = [titlesData.trim()];
        }
        
        if (Array.isArray(featuresData)) {
          featuresArray = featuresData.map(f => f == null || f === 'null' || f === undefined ? '' : String(f).trim());
        } else if (featuresData && typeof featuresData === 'string' && featuresData !== 'null') {
          featuresArray = [featuresData.trim()];
        }
        
        // When ALL titles/features came as one comma-separated string, split into one per row
        if (titlesArray.length === 1 && titlesArray[0] && titlesArray[0].indexOf(',') !== -1) {
          titlesArray = titlesArray[0].split(',').map(s => s.trim()).filter(Boolean);
        }
        if (featuresArray.length === 1 && featuresArray[0] && featuresArray[0].indexOf(',') !== -1) {
          featuresArray = featuresArray[0].split(',').map(s => s.trim()).filter(Boolean);
        }
        
        const maxLength = Math.max(titlesArray.length, featuresArray.length);
        while (titlesArray.length < maxLength) titlesArray.push('');
        while (featuresArray.length < maxLength) featuresArray.push('');
        
        // Update or show existing rows; hide only extra rows. Don't hide all first – keeps same design.
        if (maxLength > 0) {
          for (let i = 0; i < maxLength; i++) {
            // Keep comma-separated values as-is (e.g. "Value A, Value B")
            const titleText = titlesArray[i] != null ? String(titlesArray[i]) : '';
            const featureText = featuresArray[i] != null ? String(featuresArray[i]) : '';
            
            // Get or create feature item – same structure as Liquid
            let itemElement = existingItems[i];
            if (!itemElement) {
              itemElement = document.createElement('div');
              itemElement.className = 'custom-text-feature__feature-item' + (i < maxLength - 1 ? ' custom-text-feature__feature-item--with-divider' : '');
              itemElement.setAttribute('data-variant-item', 'true');
              const itemContent = document.createElement('div');
              itemContent.className = 'custom-text-feature__feature-content';
              const labelSpan = document.createElement('span');
              labelSpan.className = 'custom-text-feature__feature-label';
              const valueSpan = document.createElement('span');
              valueSpan.className = 'custom-text-feature__feature-value';
              itemContent.appendChild(labelSpan);
              itemContent.appendChild(valueSpan);
              itemElement.appendChild(itemContent);
              featuresContainer.appendChild(itemElement);
            }
            
            itemElement.removeAttribute('style');
            if (i < maxLength - 1) {
              itemElement.classList.add('custom-text-feature__feature-item--with-divider');
            } else {
              itemElement.classList.remove('custom-text-feature__feature-item--with-divider');
            }
            
            const itemContent = itemElement.querySelector('.custom-text-feature__feature-content');
            const titleEl = itemContent ? itemContent.querySelector('.custom-text-feature__feature-label') : null;
            const featureEl = itemContent ? itemContent.querySelector('.custom-text-feature__feature-value') : null;
            if (titleEl) {
              titleEl.removeAttribute('style');
              titleEl.textContent = titleText;
              titleEl.setAttribute('data-variant-title', 'true');
              titleEl.style.display = titleText ? '' : 'none';
            }
            if (featureEl) {
              featureEl.removeAttribute('style');
              featureEl.textContent = featureText;
              featureEl.setAttribute('data-variant-feature', 'true');
              featureEl.style.display = featureText ? '' : 'none';
            }
          }
          // Hide extra rows (index >= maxLength)
          for (let i = maxLength; i < existingItems.length; i++) {
            existingItems[i].style.display = 'none';
          }
        }
      } else {
        // No variant data or invalid data - show customizer data
        existingItems.forEach((item, idx) => {
          item.removeAttribute('style');
          item.style.display = '';
          const titleEl = item.querySelector('.custom-text-feature__feature-label');
          if (titleEl && titleEl.hasAttribute('data-customizer-title')) {
            titleEl.textContent = titleEl.getAttribute('data-customizer-title');
            titleEl.removeAttribute('data-variant-title');
            titleEl.removeAttribute('style');
            titleEl.style.display = '';
          }
          const featureEl = item.querySelector('.custom-text-feature__feature-value');
          if (featureEl && featureEl.hasAttribute('data-customizer-feature')) {
            featureEl.textContent = featureEl.getAttribute('data-customizer-feature');
            featureEl.removeAttribute('data-variant-feature');
            featureEl.removeAttribute('style');
            featureEl.style.display = '';
          }
        });
        
        // Hide any variant-created items
        const variantItems = featuresContainer.querySelectorAll('.custom-text-feature__feature-item[data-variant-item]');
        variantItems.forEach(item => {
          item.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important; margin: 0 !important; padding: 0 !important;';
        });
      }
      
    } catch (e) {
      // Silent error - fallback to customizer data
      console.error('Error updating variant technical specs:', e);
    }
  }

  // Function to update variant performance blocks section (custom.variant_performance_data)
  function updateVariantPerformanceBlocks(variantId) {
    if (!variantId) return;
    function perfDisplayStr(v) {
      if (v == null || v === undefined) return '';
      if (typeof v === 'object' && v !== null && 'value' in v) return perfDisplayStr(v.value);
      return String(v);
    }
    function perfDisplayNum(v) {
      if (v == null || v === undefined) return 0;
      if (typeof v === 'object' && v !== null) {
        if ('value' in v) return perfDisplayNum(v.value);
        if (Array.isArray(v) && v.length > 0) return perfDisplayNum(v[0]);
      }
      const s = String(v).trim();
      if (s === '') return 0;
      const n = Number(s);
      return isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
    }
    function applyProgressWidth(el, widthVal) {
      if (!el || !widthVal) return;
      el.setAttribute('data-customizer-progress', widthVal);
      el.style.animation = 'none';
      el.style.setProperty('--progress-width', widthVal, 'important');
      el.style.setProperty('width', widthVal, 'important');
    }
    function getProgressFromItem(item) {
      if (!item) return 0;
      var raw = item.performance_progress !== undefined && item.performance_progress !== null ? item.performance_progress : (item.progress !== undefined && item.progress !== null ? item.progress : (item.progress_value !== undefined && item.progress_value !== null ? item.progress_value : item.performance_progress_value));
      return perfDisplayNum(raw);
    }
    const variantIdStr = variantId.toString();
    let variantData = variantPerformanceDataMap.get(variantIdStr);
    if (!variantData) {
      const variantIdNum = parseInt(variantIdStr, 10);
      if (!isNaN(variantIdNum)) {
        variantData = variantPerformanceDataMap.get(variantIdNum.toString()) || variantPerformanceDataMap.get(String(variantIdNum));
      }
    }
    // Fallback: parse from tab that matches CURRENT product (so SlumberCool Max variant data comes from SlumberCool Max tab)
    if (!variantData) {
      let tabToRead = null;
      if (currentMattressProduct && currentMattressProduct.handle) {
        tabToRead = document.querySelector('.mattress-type-tabs .tab[data-product-handle="' + currentMattressProduct.handle + '"]') || document.querySelector('.mattress-type-tabs .tab[data-tab="' + currentMattressProduct.handle + '"]');
      }
      if (!tabToRead) {
        tabToRead = document.querySelector('.mattress-type-tabs .tab.active') || document.querySelector('.mattress-type-tabs .tab');
      }
      if (tabToRead) {
        const attr = tabToRead.getAttribute('data-variant-performance-data');
        if (attr && attr.trim() !== '') {
          const entries = attr.split('||').filter(function(v) { return v && v.trim() !== ''; });
          for (let e = 0; e < entries.length; e++) {
            const parts = entries[e].split(':');
            if (parts.length < 2) continue;
            const vid = parts[0].trim();
            if (vid !== variantIdStr && (isNaN(parseInt(vid, 10)) || parseInt(vid, 10) !== parseInt(variantIdStr, 10))) continue;
            const metafieldJson = parts.slice(1).join(':').trim();
            if (!metafieldJson || metafieldJson === 'null' || metafieldJson === 'undefined' || metafieldJson === '[]') continue;
            try {
              const decodedJson = metafieldJson.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
              const parsedData = JSON.parse(decodedJson);
              let normalized = [];
              if (Array.isArray(parsedData)) {
                normalized = parsedData.map(function(item) {
                  return {
                    performance_heading: perfDisplayStr(item && item.performance_heading),
                    performance_heading_value: perfDisplayStr(item && item.performance_heading_value),
                    performance_progress: perfDisplayNum(item && item.performance_progress),
                    performance_text: perfDisplayStr(item && item.performance_text)
                  };
                });
              } else if (parsedData && typeof parsedData === 'object') {
                var prog = parsedData.performance_progress !== undefined && parsedData.performance_progress !== null ? parsedData.performance_progress : (parsedData.progress !== undefined && parsedData.progress !== null ? parsedData.progress : parsedData.progress_value);
                normalized = [{
                  performance_heading: perfDisplayStr(parsedData.performance_heading),
                  performance_heading_value: perfDisplayStr(parsedData.performance_heading_value),
                  performance_progress: prog,
                  performance_text: perfDisplayStr(parsedData.performance_text)
                }];
              }
              if (normalized.length > 0) {
                const hasCommaInAny = normalized.some(function(it) {
                  const h = (it.performance_heading || '').toString();
                  const hv = (it.performance_heading_value || '').toString();
                  const t = (it.performance_text || '').toString();
                  const pStr = (it.performance_progress != null ? it.performance_progress : '').toString();
                  return h.indexOf(',') !== -1 || hv.indexOf(',') !== -1 || t.indexOf(',') !== -1 || pStr.indexOf(',') !== -1;
                });
                if (hasCommaInAny) {
                  const expanded = [];
                  for (let i = 0; i < normalized.length; i++) {
                    const item = normalized[i];
                    const h = (item.performance_heading || '').toString();
                    const hv = (item.performance_heading_value || '').toString();
                    const t = (item.performance_text || '').toString();
                    const headings = h.split(',').map(function(s) { return s.trim(); });
                    const values = hv.split(',').map(function(s) { return s.trim(); });
                    const texts = t.split(',').map(function(s) { return s.trim(); });
                    let progressArr = [];
                    const pStr = (item.performance_progress != null ? item.performance_progress : '').toString();
                    if (pStr.indexOf(',') !== -1) {
                      progressArr = pStr.split(',').map(function(s) { return perfDisplayNum(s.trim()); });
                    } else {
                      const singleProgress = perfDisplayNum(item.performance_progress);
                      const n = Math.max(headings.length, values.length, texts.length, 1);
                      progressArr = Array(n).fill(singleProgress);
                    }
                    const n = Math.max(headings.length, values.length, progressArr.length, texts.length, 1);
                    for (let j = 0; j < n; j++) {
                      expanded.push({
                        performance_heading: headings[j] || '',
                        performance_heading_value: values[j] || '',
                        performance_progress: progressArr[j] !== undefined ? progressArr[j] : 0,
                        performance_text: texts[j] || ''
                      });
                    }
                  }
                  normalized = expanded;
                }
              }
              if (normalized.length > 0) {
                variantPerformanceDataMap.set(variantIdStr, normalized);
                const numId = parseInt(variantIdStr, 10);
                if (!isNaN(numId)) {
                  variantPerformanceDataMap.set(numId.toString(), normalized);
                  variantPerformanceDataMap.set(String(numId), normalized);
                }
                variantData = normalized;
                break;
              }
            } catch (err) { /* silent */ }
          }
        }
      }
    }
    const performanceSections = document.querySelectorAll('.performance-blocks');
    if (!performanceSections || performanceSections.length === 0) return;
    performanceSections.forEach((section) => {
      const grid = section.querySelector('.performance-blocks__grid');
      if (!grid) return;
      const blocks = grid.querySelectorAll('.performance-blocks__block');
      const maxBlocks = Math.max(blocks.length, variantData && variantData.length ? variantData.length : 0);
      for (let i = 0; i < maxBlocks; i++) {
        const item = variantData && variantData[i] ? variantData[i] : null;
        let blockEl = blocks[i];
        if (!blockEl && grid) {
          blockEl = document.createElement('div');
          blockEl.className = 'performance-blocks__block';
          blockEl.setAttribute('data-variant-performance-block', 'true');
          const sectionId = section.getAttribute('data-section-id') || section.id || ('sec_' + i);
          const blockId = 'variant_' + sectionId + '_' + i;
          const inner = document.createElement('div');
          inner.className = 'performance-blocks__block-inner';
          const firstBlock = section.querySelector('.performance-blocks__block');
          const firstInner = firstBlock ? firstBlock.querySelector('.performance-blocks__block-inner') : null;
          if (firstInner) {
            inner.setAttribute('style', firstInner.getAttribute('style') || '');
          }
          const content = document.createElement('div');
          content.className = 'performance-blocks__block-content';
          const headingWrapper = document.createElement('div');
          headingWrapper.className = 'performance-blocks__block-heading-wrapper';
          const headingP = document.createElement('p');
          headingP.className = 'performance-blocks__block-heading pdp-text-color';
          headingP.id = 'performance_heading_' + blockId;
          const headingSpan = document.createElement('span');
          headingSpan.className = 'performance-blocks__block-heading-text pdp-text-color';
          headingSpan.id = 'performance_heading_value_' + blockId;
          headingWrapper.appendChild(headingP);
          headingWrapper.appendChild(headingSpan);
          content.appendChild(headingWrapper);
          const progressContainer = document.createElement('div');
          progressContainer.className = 'performance-blocks__progress-container';
          const progressBar = document.createElement('div');
          progressBar.className = 'performance-blocks__progress-bar';
          const progressFill = document.createElement('div');
          progressFill.className = 'performance-blocks__progress-fill pdp-color-enabled';
          progressFill.id = 'performance_progress_' + blockId;
          progressFill.setAttribute('data-section', 'pdp-color-change');
          const firstFill = firstBlock ? firstBlock.querySelector('.performance-blocks__progress-fill') : null;
          if (firstFill && firstFill.style.background) progressFill.style.background = firstFill.style.background;
          var progressPct = getProgressFromItem(item);
          var pctVal = progressPct > 0 ? (progressPct + '%') : (firstFill && firstFill.getAttribute('data-customizer-progress')) ? firstFill.getAttribute('data-customizer-progress') : '0%';
          progressFill.setAttribute('data-customizer-progress', pctVal);
          applyProgressWidth(progressFill, pctVal);
          progressBar.appendChild(progressFill);
          progressContainer.appendChild(progressBar);
          content.appendChild(progressContainer);
          const textDiv = document.createElement('div');
          textDiv.className = 'performance-blocks__block-text';
          textDiv.id = 'performance_text_' + blockId;
          content.appendChild(textDiv);
          inner.appendChild(content);
          blockEl.appendChild(inner);
          grid.appendChild(blockEl);
        }
        if (!blockEl) continue;
        const headingEl = blockEl.querySelector('.performance-blocks__block-heading');
        const headingValueEl = blockEl.querySelector('.performance-blocks__block-heading-text');
        const progressFillEl = blockEl.querySelector('.performance-blocks__progress-fill');
        const progressContainerEl = blockEl.querySelector('.performance-blocks__progress-container');
        const textEl = blockEl.querySelector('.performance-blocks__block-text');
        if (item) {
          if (headingEl) {
            if (!headingEl.hasAttribute('data-customizer-heading') && headingEl.textContent && headingEl.textContent.trim() !== '') {
              headingEl.setAttribute('data-customizer-heading', headingEl.textContent);
            }
            const val = perfDisplayStr(item.performance_heading);
            headingEl.textContent = val;
            headingEl.style.display = val ? '' : 'none';
          }
          if (headingValueEl) {
            if (!headingValueEl.hasAttribute('data-customizer-heading-value') && headingValueEl.textContent && headingValueEl.textContent.trim() !== '') {
              headingValueEl.setAttribute('data-customizer-heading-value', headingValueEl.textContent);
            }
            const val = perfDisplayStr(item.performance_heading_value);
            headingValueEl.textContent = val;
            headingValueEl.style.display = val ? '' : 'none';
          }
          const progressVal = getProgressFromItem(item);
          if (progressFillEl) {
            if (!progressFillEl.hasAttribute('data-customizer-progress')) {
              const w = progressFillEl.style.width || '';
              if (w) progressFillEl.setAttribute('data-customizer-progress', w);
            }
            var widthVal = progressVal > 0 ? (progressVal + '%') : (progressFillEl.getAttribute('data-customizer-progress') || progressFillEl.style.width || '0%');
            applyProgressWidth(progressFillEl, widthVal);
          }
          if (progressContainerEl) {
            progressContainerEl.style.display = '';
          }
          if (textEl) {
            if (!textEl.hasAttribute('data-customizer-text') && textEl.innerHTML && textEl.innerHTML.trim() !== '') {
              textEl.setAttribute('data-customizer-text', textEl.innerHTML);
            }
            const val = perfDisplayStr(item.performance_text);
            textEl.innerHTML = val;
            textEl.style.display = val ? '' : 'none';
          }
          blockEl.removeAttribute('style');
          blockEl.style.display = '';
        } else {
          if (blockEl.hasAttribute('data-variant-performance-block')) {
            blockEl.style.display = 'none';
          } else {
            if (headingEl && !headingEl.hasAttribute('data-customizer-heading') && headingEl.textContent && headingEl.textContent.trim() !== '') {
              headingEl.setAttribute('data-customizer-heading', headingEl.textContent);
            }
            if (headingValueEl && !headingValueEl.hasAttribute('data-customizer-heading-value') && headingValueEl.textContent && headingValueEl.textContent.trim() !== '') {
              headingValueEl.setAttribute('data-customizer-heading-value', headingValueEl.textContent);
            }
            if (progressFillEl && !progressFillEl.hasAttribute('data-customizer-progress') && progressFillEl.style.width) {
              progressFillEl.setAttribute('data-customizer-progress', progressFillEl.style.width);
            }
            if (textEl && !textEl.hasAttribute('data-customizer-text') && textEl.innerHTML && textEl.innerHTML.trim() !== '') {
              textEl.setAttribute('data-customizer-text', textEl.innerHTML);
            }
            if (headingEl && headingEl.hasAttribute('data-customizer-heading')) {
              headingEl.textContent = headingEl.getAttribute('data-customizer-heading');
              headingEl.removeAttribute('style');
            }
            if (headingValueEl && headingValueEl.hasAttribute('data-customizer-heading-value')) {
              headingValueEl.textContent = headingValueEl.getAttribute('data-customizer-heading-value');
              headingValueEl.removeAttribute('style');
            }
            if (progressFillEl && progressFillEl.hasAttribute('data-customizer-progress')) {
              const w = progressFillEl.getAttribute('data-customizer-progress');
              applyProgressWidth(progressFillEl, w);
            }
            if (progressContainerEl) progressContainerEl.style.display = '';
            if (textEl && textEl.hasAttribute('data-customizer-text')) {
              textEl.innerHTML = textEl.getAttribute('data-customizer-text');
              textEl.removeAttribute('style');
            }
            blockEl.removeAttribute('style');
            blockEl.style.display = '';
          }
        }
      }
      for (let i = maxBlocks; i < blocks.length; i++) {
        const blockEl = blocks[i];
        if (blockEl && blockEl.hasAttribute('data-variant-performance-block')) {
          blockEl.style.display = 'none';
        } else {
          const headingEl = blockEl.querySelector('.performance-blocks__block-heading');
          const headingValueEl = blockEl.querySelector('.performance-blocks__block-heading-text');
          const progressFillEl = blockEl.querySelector('.performance-blocks__progress-fill');
          const progressContainerEl = blockEl.querySelector('.performance-blocks__progress-container');
          const textEl = blockEl.querySelector('.performance-blocks__block-text');
          if (headingEl && headingEl.hasAttribute('data-customizer-heading')) {
            headingEl.textContent = headingEl.getAttribute('data-customizer-heading');
            headingEl.removeAttribute('style');
          }
          if (headingValueEl && headingValueEl.hasAttribute('data-customizer-heading-value')) {
            headingValueEl.textContent = headingValueEl.getAttribute('data-customizer-heading-value');
            headingValueEl.removeAttribute('style');
          }
            if (progressFillEl && progressFillEl.hasAttribute('data-customizer-progress')) {
              const w = progressFillEl.getAttribute('data-customizer-progress');
              applyProgressWidth(progressFillEl, w);
            }
            if (progressContainerEl) progressContainerEl.style.display = '';
            if (textEl && textEl.hasAttribute('data-customizer-text')) {
              textEl.innerHTML = textEl.getAttribute('data-customizer-text');
              textEl.removeAttribute('style');
            }
            blockEl.removeAttribute('style');
            blockEl.style.display = '';
        }
      }
      document.querySelectorAll('.performance-blocks__progress-fill[data-customizer-progress]').forEach(function(el) {
        applyProgressWidth(el, el.getAttribute('data-customizer-progress'));
      });
    });
  }

  // First load: show default selected variant's performance blocks (from active tab's first variant)
  setTimeout(function() {
    const activeTab = document.querySelector('.mattress-type-tabs .tab.active') || document.querySelector('.mattress-type-tabs .tab');
    if (!activeTab) return;
    try {
      const variantsStr = activeTab.getAttribute('data-product-variants');
      if (variantsStr) {
        const decoded = variantsStr.replace(/&quot;/g, '"').replace(/&#39;/g, "'");
        const variants = JSON.parse(decoded);
        if (variants && variants.length > 0) {
          updateVariantPerformanceBlocks(variants[0].id);
        }
      }
    } catch (e) {}
  }, 0);

  // Function to update product for selected variant
  function updateProductForSelectedVariant() {
    const sizeSelect = document.getElementById('size-select');
    if (!sizeSelect) return;
    
    const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
    if (!selectedOption) return;
    
    const variantId = selectedOption.value;
    if (currentMattressProduct && currentMattressProduct.variants) {
      const foundVariant = currentMattressProduct.variants.find(v => v.id.toString() === variantId);
      if (foundVariant) {
        currentSizeVariant = foundVariant;
      }
    }
    
    // Update variant info (short and long text)
    updateVariantInfo(variantId);
    
    // Update variant image and features section
    updateVariantImageAndFeatures(variantId);
    
    // Update variant technical specifications section
    updateVariantTechnicalSpecs(variantId);
    
    // Update variant performance blocks section
    updateVariantPerformanceBlocks(variantId);
    
    // Sync foundation options to current mattress size (foundation size hidden, linked to mattress size)
    syncFoundationOptionsWithMattressSize();

    if (typeof syncBeddingOfferWithMattressSize === 'function') {
      syncBeddingOfferWithMattressSize();
    }
    
    // Update variant name display
    updateVariantNameDisplay();
    
    // Update total price (includes foundation if selected)
    // Also syncs compare-at + savings badge via updatePriceSavingsDisplay()
    updateTotalPrice();
  }

  // Add to Cart functionality
  const addToCartBtn = document.getElementById("add-to-cart");
  if (addToCartBtn) {
    addToCartBtn.addEventListener("click", async function () {
      await handleAddToCart(false, false);
    });
  }

  // Buy Now functionality
  const buyNowBtn = document.getElementById("buy-now");
  if (buyNowBtn) {
    buyNowBtn.addEventListener("click", async function () {
      await handleAddToCart(true, false);
    });
  }

  window.stickyATCCustomAddToCart = async function() {
    const stickyBtn = document.querySelector('#md-sticky-atc [name="add"]');
    const stickySpinner = stickyBtn ? stickyBtn.querySelector('.loading__spinner') : null;
    try {
      if (stickyBtn) {
        stickyBtn.disabled = true;
        if (stickySpinner) stickySpinner.classList.remove('hidden');
      }
      await handleAddToCart(false, false);
    } finally {
      if (stickyBtn) {
        stickyBtn.disabled = false;
        if (stickySpinner) stickySpinner.classList.add('hidden');
      }
    }
  };

  // Upsell popups (adjustable + breeze): same confirm/continue behavior, different DOM namespace
  function wireFoundationUpsellPopup(namespace) {
    const overlay = upsellPopupGetEl(namespace, 'overlay');
    const upsellConfirm = upsellPopupGetEl(namespace, 'confirm');
    const upsellContinue = upsellPopupGetEl(namespace, 'continue');
    const upsellClose = upsellPopupGetEl(namespace, 'close');
    if (!overlay) return;
    async function upsellContinueAction() {
      const target = activeUpsellOverlay || overlay;
      const noUpgradeVariantId = target.dataset.noUpgradeVariantId ? target.dataset.noUpgradeVariantId : null;
      const noUpgradeVariantTitle = target.dataset.noUpgradeVariantTitle ? target.dataset.noUpgradeVariantTitle : '';
      target.style.display = 'none';
      target.setAttribute('aria-hidden', 'true');
      activeUpsellOverlay = null;
      await handleAddToCart(pendingUpsellBuyNow, true, null, null, noUpgradeVariantId || null, noUpgradeVariantTitle || '');
    }
    if (upsellConfirm) {
      upsellConfirm.addEventListener('click', async function () {
        const target = activeUpsellOverlay || overlay;
        target.style.display = 'none';
        target.setAttribute('aria-hidden', 'true');
        activeUpsellOverlay = null;
        const buyNow = !!pendingUpsellBuyNow;
        const upgradeVid = target.dataset.upgradeVariantId || null;
        const upgradeTitle = target.dataset.upgradeVariantTitle || '';
        await handleAddToCart(buyNow, true, upgradeVid, upgradeTitle);
      });
    }
    if (upsellContinue) {
      upsellContinue.addEventListener('click', async function () {
        await upsellContinueAction();
      });
    }
    if (upsellClose) {
      upsellClose.addEventListener('click', async function () {
        await upsellContinueAction();
      });
    }
  }
  wireFoundationUpsellPopup('upsell-popup');
  wireFoundationUpsellPopup('breeze-upsell-popup');

  function getAccessoryBundleUpsellConfig() {
    const el = document.getElementById('accessory-bundle-upsell-popup-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function normalizeAccessoryStoragePart(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 96);
  }

  /** Session key so changing mattress / foundation / size / bundle product resets memory. */
  function getAccessoryBundleStorageKey(abCfg) {
    if (typeof sessionStorage === 'undefined') return null;
    if (!abCfg || !abCfg.upgradeProductHandle) return null;
    const ph = currentMattressProduct && currentMattressProduct.handle ? String(currentMattressProduct.handle) : '';
    if (!ph) return null;
    const foundationInput = document.querySelector('input[name="foundation"]:checked');
    const ft = ((foundationInput && foundationInput.getAttribute('data-foundation-type')) || selectedFoundationType || '').trim();
    const sz = getCurrentMattressSizeValue().trim();
    const uh = String(abCfg.upgradeProductHandle || '').trim();
    return (
      'scAbb:' +
      normalizeAccessoryStoragePart(ph) +
      ':' +
      normalizeAccessoryStoragePart(ft) +
      ':' +
      normalizeAccessoryStoragePart(sz) +
      ':' +
      normalizeAccessoryStoragePart(uh)
    );
  }

  function readStoredAccessoryBundleChoice(key) {
    if (!key || typeof sessionStorage === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeStoredAccessoryBundleChoice(key, obj) {
    if (!key || typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(key, JSON.stringify(obj));
    } catch (e) {}
  }

  /** Forget accessory-bundle popup choices when the cart has no items (new purchase flow). */
  async function clearAccessoryBundleSessionStorageIfCartEmpty() {
    if (typeof sessionStorage === 'undefined') return;
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      const count =
        cart && (cart.item_count != null ? Number(cart.item_count) : cart.items && cart.items.length ? cart.items.length : 0);
      if (count > 0) return;
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && (k.indexOf('scAbb:') === 0 || k.indexOf('scAbbCartDecline:') === 0)) keys.push(k);
      }
      keys.forEach(function (k) {
        try {
          sessionStorage.removeItem(k);
        } catch (e) {}
      });
    } catch (e) {}
  }

  function accessoryBundleFoundationMatches(config, foundationType) {
    if (!config || config.foundationTriggerLines == null) return false;
    const lines = String(config.foundationTriggerLines)
      .split(/\r?\n/)
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
    if (!lines.length) return false;
    const ft = (foundationType || '').toString().trim();
    if (!ft) {
      return lines.some(function (line) {
        return foundationTriggerMatches({ foundationTrigger: line }, 'no foundation');
      });
    }
    return lines.some(function (line) {
      return foundationTriggerMatches({ foundationTrigger: line }, ft);
    });
  }

  function resolveAccessoryVariantIdForItem(accessoryItem) {
    const defaultVariantId = accessoryItem.getAttribute('data-accessory-variant-id');
    const variantsJson = accessoryItem.getAttribute('data-accessory-variants');
    const currentSizeValue = getCurrentMattressSizeValue();
    if (!variantsJson || !currentSizeValue || currentSizeValue === '') {
      return defaultVariantId;
    }
    try {
      const variants = JSON.parse(variantsJson);
      const found = variants.find(function (v) {
        const o1 = (v.o1 || '').toString().trim();
        const o2 = (v.o2 || '').toString().trim();
        const o3 = (v.o3 || '').toString().trim();
        return bedSizeLabelsEqual(o1, currentSizeValue) || bedSizeLabelsEqual(o2, currentSizeValue) || bedSizeLabelsEqual(o3, currentSizeValue);
      });
      return found ? String(found.id) : defaultVariantId;
    } catch (e) {
      return defaultVariantId;
    }
  }

  const beddingOfferProductCache = new Map();

  function resolveBeddingVariantFromElement(el) {
    if (!el) return null;
    const defaultId =
      el.getAttribute('data-bedding-variant-id') || el.getAttribute('data-bedding-header-variant-id');
    const variantsJson =
      el.getAttribute('data-bedding-variants') || el.getAttribute('data-bedding-header-variants');
    const currentSizeValue = getCurrentMattressSizeValue();
    if (!variantsJson) {
      return defaultId ? { id: defaultId } : null;
    }
    try {
      const variants = JSON.parse(variantsJson);
      if (!currentSizeValue) {
        const fallback = variants.find(function (v) {
          return defaultId && String(v.id) === String(defaultId);
        });
        return fallback || (defaultId ? { id: defaultId } : null);
      }
      const found = variants.find(function (v) {
        const o1 = (v.o1 || '').toString().trim();
        const o2 = (v.o2 || '').toString().trim();
        const o3 = (v.o3 || '').toString().trim();
        return (
          bedSizeLabelsEqual(o1, currentSizeValue) ||
          bedSizeLabelsEqual(o2, currentSizeValue) ||
          bedSizeLabelsEqual(o3, currentSizeValue)
        );
      });
      if (found) return found;
      const defaultEntry = variants.find(function (v) {
        return defaultId && String(v.id) === String(defaultId);
      });
      return defaultEntry || (defaultId ? { id: defaultId } : null);
    } catch (e) {
      return defaultId ? { id: defaultId } : null;
    }
  }

  function parseDisplayPriceToCents(priceStr) {
    if (!priceStr || String(priceStr).trim() === '') return 0;
    const num = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return 0;
    return Math.round(num * 100);
  }

  function getBeddingRowRegularPriceCents(rowEl, resolved) {
    if (resolved && resolved.price != null && resolved.price !== '') {
      return parseInt(resolved.price, 10) || 0;
    }
    return parseDisplayPriceToCents(rowEl.getAttribute('data-fallback-price') || '');
  }

  function applyBeddingHeaderCompareFromRowSum(compareEl, sumCents) {
    if (!compareEl) return;
    if (sumCents > 0) {
      compareEl.textContent = formatMoney(sumCents);
      compareEl.style.display = '';
    } else {
      compareEl.style.display = 'none';
    }
  }

  function getBeddingWorthMessageTemplate(worthRow) {
    const input = document.getElementById('bedding-offer-toggle-input');
    const isOn = !!(input && input.checked);
    if (isOn) {
      return (
        worthRow.getAttribute('data-worth-template-on') ||
        worthRow.getAttribute('data-worth-template-off') ||
        ''
      );
    }
    return worthRow.getAttribute('data-worth-template-off') || '';
  }

  function updateBeddingBundleWorthMessage(sumCents) {
    const worthRow = document.getElementById('bedding-offer-worth-row');
    const worthText = document.getElementById('bedding-offer-worth-text');
    if (!worthRow || !worthText) return;
    worthRow.dataset.worthSumCents = String(sumCents);
    const template = getBeddingWorthMessageTemplate(worthRow);
    const worth = formatMoney(sumCents > 0 ? sumCents : 0);
    worthText.textContent = template.replace(/\{worth\}/gi, worth);
  }

  function syncBeddingWorthMessageFromToggle() {
    const worthRow = document.getElementById('bedding-offer-worth-row');
    if (!worthRow) return;
    const sumCents = parseInt(worthRow.dataset.worthSumCents, 10) || 0;
    updateBeddingBundleWorthMessage(sumCents);
  }

  window.syncBeddingWorthMessageFromToggle = syncBeddingWorthMessageFromToggle;

  function applyBeddingPriceToElements(compareEl, priceEl, variantData, fallbackCompare, fallbackPrice) {
    if (!priceEl) return;
    if (variantData && variantData.price != null && variantData.price !== '') {
      const priceCents = parseInt(variantData.price, 10);
      const compareCents =
        variantData.compare_at_price != null ? parseInt(variantData.compare_at_price, 10) : 0;
      priceEl.textContent = formatMoney(priceCents);
      if (compareEl) {
        if (compareCents > priceCents) {
          compareEl.textContent = formatMoney(compareCents);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      return;
    }
    if (fallbackPrice) {
      priceEl.textContent = fallbackPrice;
    }
    if (compareEl) {
      if (fallbackCompare) {
        compareEl.textContent = fallbackCompare;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  async function fetchBeddingOfferProduct(handle) {
    if (!handle) return null;
    if (beddingOfferProductCache.has(handle)) {
      return beddingOfferProductCache.get(handle);
    }
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) return null;
      const product = await res.json();
      beddingOfferProductCache.set(handle, product);
      return product;
    } catch (e) {
      return null;
    }
  }

  /** Product-level image (same for all variants) for bedding display rows. */
  function getBeddingProductImageSrc(product) {
    if (!product) return '';
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image.src) return product.featured_image.src;
    }
    if (product.images && product.images.length) {
      const first = product.images[0];
      if (typeof first === 'string') return first;
      return first.src || first.url || '';
    }
    return '';
  }

  function ensureBeddingRowImageElement(rowEl) {
    let img = rowEl.querySelector('.bedding-offer-product-image');
    if (img) return img;
    const imageDiv = rowEl.querySelector('.bedding-offer-image');
    if (!imageDiv) return null;
    img = document.createElement('img');
    img.className = 'bedding-offer-product-image';
    img.width = 80;
    img.height = 80;
    img.loading = 'lazy';
    imageDiv.appendChild(img);
    return img;
  }

  function updateBeddingRowTitle(rowEl, productTitle) {
    const titleText = rowEl.querySelector('.bedding-offer-product-title-text');
    if (!titleText) return;
    const name =
      (productTitle && String(productTitle).trim()) ||
      rowEl.getAttribute('data-bedding-product-title') ||
      '';
    if (name) {
      titleText.textContent = name;
      return;
    }
    const fallbackTitle = rowEl.getAttribute('data-fallback-title');
    if (fallbackTitle) {
      titleText.textContent = fallbackTitle;
    }
  }

  async function resolveBeddingVariantForRow(rowEl) {
    const fromAttr = resolveBeddingVariantFromElement(rowEl);
    if (fromAttr && fromAttr.price != null) return fromAttr;
    const handle = rowEl.getAttribute('data-bedding-display-product');
    if (!handle) return fromAttr;
    const product = await fetchBeddingOfferProduct(handle);
    if (!product || !product.variants || !product.variants.length) return fromAttr;
    const v = getVariantBySize(product, getCurrentMattressSizeValue());
    if (!v) return fromAttr;
    return {
      id: v.id,
      price: v.price,
      compare_at_price: v.compare_at_price != null ? v.compare_at_price : 0
    };
  }

  async function updateBeddingRowDisplay(rowEl, handle) {
    if (!rowEl || !handle) return;

    const product = await fetchBeddingOfferProduct(handle);
    if (product && product.title) {
      rowEl.setAttribute('data-bedding-product-title', product.title);
      updateBeddingRowTitle(rowEl, product.title);
    } else {
      updateBeddingRowTitle(rowEl);
    }

    if (rowEl.getAttribute('data-bedding-custom-image') === '1') return;

    let src = rowEl.getAttribute('data-bedding-product-image') || '';
    if (!src && product) {
      src = getBeddingProductImageSrc(product);
      if (src) rowEl.setAttribute('data-bedding-product-image', src);
    }

    const img = ensureBeddingRowImageElement(rowEl);
    if (img && src) {
      img.src = src;
      img.alt =
        (product && product.title) || rowEl.getAttribute('data-bedding-product-title') || '';
    }
  }

  async function syncBeddingOfferWithMattressSize() {
    const headerPricing = document.getElementById('bedding-offer-header-pricing');
    const headerCompareEl = document.getElementById('bedding-offer-header-compare');
    const headerPriceEl = document.getElementById('bedding-offer-header-price');
    const sumRowsInHeaderCompare =
      headerPricing &&
      headerPricing.getAttribute('data-sum-row-prices-in-header-compare') === '1';

    const rows = document.querySelectorAll('.bedding-offer-display-row');
    let rowRegularPriceSumCents = 0;

    for (let i = 0; i < rows.length; i++) {
      const rowEl = rows[i];
      const handle = rowEl.getAttribute('data-bedding-display-product');
      const compareEl = rowEl.querySelector('.bedding-offer-compare-at-price');
      const priceEl = rowEl.querySelector('.bedding-offer-regular-price');
      const resolved = await resolveBeddingVariantForRow(rowEl);
      applyBeddingPriceToElements(
        compareEl,
        priceEl,
        resolved,
        rowEl.getAttribute('data-fallback-compare') || '',
        rowEl.getAttribute('data-fallback-price') || ''
      );
      rowRegularPriceSumCents += getBeddingRowRegularPriceCents(rowEl, resolved);
      if (handle) {
        await updateBeddingRowDisplay(rowEl, handle);
      } else {
        updateBeddingRowTitle(rowEl);
      }
    }

    if (headerPricing) {
      const headerResolved = resolveBeddingVariantFromElement(headerPricing);
      const fallbackCompare = headerPricing.getAttribute('data-fallback-compare') || '';
      const fallbackPrice = headerPricing.getAttribute('data-fallback-price') || '';

      let headerPriceCents = 0;
      if (headerResolved && headerResolved.price != null && headerResolved.price !== '') {
        headerPriceCents = parseInt(headerResolved.price, 10) || 0;
      } else {
        headerPriceCents = parseDisplayPriceToCents(fallbackPrice);
      }

      if (headerPriceEl) {
        if (headerResolved && headerResolved.price != null && headerResolved.price !== '') {
          headerPriceEl.textContent = formatMoney(headerPriceCents);
        } else if (fallbackPrice) {
          headerPriceEl.textContent = fallbackPrice;
        }
      }

      if (sumRowsInHeaderCompare) {
        applyBeddingHeaderCompareFromRowSum(headerCompareEl, rowRegularPriceSumCents);
      } else {
        applyBeddingPriceToElements(
          headerCompareEl,
          headerPriceEl,
          headerResolved,
          fallbackCompare,
          fallbackPrice
        );
      }
    }

    updateBeddingBundleWorthMessage(rowRegularPriceSumCents);
  }

  function isBeddingOfferToggleActive() {
    const input = document.getElementById('bedding-offer-toggle-input');
    return !!(input && input.checked);
  }

  /** Display row titles (bedding-offer-product-title-text) for cart parent line. */
  function collectBeddingOfferDisplayTitles() {
    const titles = [];
    document.querySelectorAll('.bedding-offer-display-row').forEach(function (row) {
      const el = row.querySelector('.bedding-offer-product-title-text');
      const text = el && el.textContent ? el.textContent.trim() : '';
      if (text) titles.push(text);
    });
    return titles;
  }

  function resolveBeddingHeaderCartVariantId() {
    const headerPricing = document.getElementById('bedding-offer-header-pricing');
    if (!headerPricing || !headerPricing.getAttribute('data-bedding-header-product')) return null;
    const resolved = resolveBeddingVariantFromElement(headerPricing);
    return resolved && resolved.id != null ? String(resolved.id) : null;
  }

  window.syncBeddingOfferWithMattressSize = syncBeddingOfferWithMattressSize;

  /**
   * Accessory bundle upgrade popup (after foundation upsell when applicable).
   * @returns {Promise<boolean>}
   */
  async function showAccessoryBundleUpsellPopup() {
    const configEl = document.getElementById('accessory-bundle-upsell-popup-config');
    const overlay = document.getElementById('accessory-bundle-upsell-popup-overlay');
    if (!configEl || !overlay) return false;
    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return false;
    }
    const upgradeHandle = config.upgradeProductHandle;
    if (!upgradeHandle) return false;
    const currentSizeValue = getCurrentMattressSizeValue();
    const accessoryItems = document.querySelectorAll('.accessory-item[data-accessory-product]');
    if (!accessoryItems.length) return false;

    try {
      let includedCents = 0;
      for (let i = 0; i < accessoryItems.length; i++) {
        const item = accessoryItems[i];
        const handle = item.getAttribute('data-accessory-product');
        const vid = resolveAccessoryVariantIdForItem(item);
        if (!handle || !vid) continue;
        try {
          const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
          if (!res.ok) continue;
          const prod = await res.json();
          const v = prod.variants && prod.variants.find(function (x) {
            return String(x.id) === String(vid);
          });
          if (v && v.price != null) includedCents += parseInt(v.price, 10) || 0;
        } catch (e) {}
      }

      const upRes = await fetch('/products/' + encodeURIComponent(upgradeHandle) + '.js');
      if (!upRes.ok) return false;
      const upgradeProduct = await upRes.json();
      const upgradeVariant = getVariantBySize(upgradeProduct, currentSizeValue) || (upgradeProduct.variants && upgradeProduct.variants[0]);
      if (!upgradeVariant) return false;
      const upgradeCents = parseInt(upgradeVariant.price, 10) || 0;
      const diffCents = Math.max(0, upgradeCents - includedCents);
      const priceFormatted = formatMoneyFromCents(diffCents);

      const curLabEl = document.getElementById('abb-pro-current-label');
      if (curLabEl && config.comparisonCurrentLabel) curLabEl.textContent = config.comparisonCurrentLabel;
      const upLabEl = document.getElementById('abb-pro-upgrade-label');
      if (upLabEl && config.comparisonUpgradeLabel) upLabEl.textContent = config.comparisonUpgradeLabel;
      const curTagEl = document.getElementById('abb-pro-current-tagline');
      if (curTagEl) {
        if (config.currentCardTagline && String(config.currentCardTagline).trim() !== '') {
          curTagEl.textContent = String(config.currentCardTagline).trim();
        } else {
          curTagEl.textContent = '';
        }
      }
      const upTagEl = document.getElementById('abb-pro-upgrade-tagline');
      if (upTagEl) {
        if (config.upgradeCardTagline && String(config.upgradeCardTagline).trim() !== '') {
          upTagEl.textContent = String(config.upgradeCardTagline).trim();
        } else {
          upTagEl.textContent = '';
        }
      }

      const diffMainEl = document.getElementById('accessory-bundle-upsell-popup-diff');
      if (diffMainEl && config.subheadTemplate) {
        diffMainEl.textContent = String(config.subheadTemplate).replace(/\{\{price\}\}|\$X/g, priceFormatted);
      }
      const compareAtRaw = config.compareAtDifference ? String(config.compareAtDifference).trim() : '';
      const compareTopEl = document.getElementById('accessory-bundle-upsell-popup-compare-at');
      if (compareTopEl) {
        compareTopEl.textContent = compareAtRaw ? compareAtRaw : '';
      }

      const subheadEl = upsellPopupGetEl('accessory-bundle-upsell-popup', 'subhead');
      const priceEl = upsellPopupGetEl('accessory-bundle-upsell-popup', 'price');
      if (priceEl) priceEl.textContent = priceFormatted;
      if (subheadEl && config.subheadTemplate) {
        subheadEl.textContent = String(config.subheadTemplate).replace(/\{\{price\}\}|\$X/g, priceFormatted);
      }
      let includedBundleAlt = 'Included accessories';
      if (config.currentCardTitle && String(config.currentCardTitle).trim() !== '') {
        includedBundleAlt = String(config.currentCardTitle).trim();
      } else {
        const titles = [];
        accessoryItems.forEach(function (node) {
          const dt = node.getAttribute('data-accessory-display-title') || '';
          const vt = node.getAttribute('data-accessory-variant-title') || '';
          const t = (dt && dt.trim()) ? dt.trim() : vt;
          if (t) titles.push(t);
        });
        if (titles.length) includedBundleAlt = titles.join(', ');
      }

      const upgradeBundleAlt =
        config.upgradeCardTitle && String(config.upgradeCardTitle).trim() !== ''
          ? String(config.upgradeCardTitle).trim()
          : upgradeVariant.title || upgradeProduct.title || 'Upgrade bundle';

      const currentImgEl = upsellPopupGetEl('accessory-bundle-upsell-popup', 'current-image');
      if (currentImgEl) {
        const customSrc = config.currentImageUrl || null;
        if (customSrc) {
          currentImgEl.src = customSrc;
          currentImgEl.alt = includedBundleAlt;
          currentImgEl.style.display = '';
        } else {
          const firstImg = accessoryItems[0] && accessoryItems[0].querySelector('.accessory-product-image');
          if (firstImg && firstImg.src) {
            currentImgEl.src = firstImg.src;
            currentImgEl.alt = includedBundleAlt;
            currentImgEl.style.display = '';
          } else {
            currentImgEl.style.display = 'none';
          }
        }
      }

      const upgradeImgEl = upsellPopupGetEl('accessory-bundle-upsell-popup', 'upgrade-image');
      if (upgradeImgEl && config.showUpgradeImage !== false) {
        let imgSrc = config.upgradeImageUrl || null;
        if (!imgSrc && upgradeVariant.featured_image) {
          imgSrc = upgradeVariant.featured_image.src || (typeof upgradeVariant.featured_image === 'string' ? upgradeVariant.featured_image : null);
        }
        if (!imgSrc && upgradeProduct.featured_image) {
          imgSrc = upgradeProduct.featured_image.src || upgradeProduct.featured_image;
        }
        if (imgSrc) {
          upgradeImgEl.src = imgSrc;
          upgradeImgEl.alt = upgradeBundleAlt;
          upgradeImgEl.style.display = '';
        } else {
          upgradeImgEl.style.display = 'none';
        }
      }

      overlay.dataset.upgradeBundleVariantId = String(upgradeVariant.id);
      overlay.style.display = 'flex';
      overlay.setAttribute('aria-hidden', 'false');
      return true;
    } catch (e) {
      console.error('Accessory bundle upsell popup error:', e);
      return false;
    }
  }

  function wireAccessoryBundleUpsellPopup() {
    const overlay = document.getElementById('accessory-bundle-upsell-popup-overlay');
    const confirmBtn = document.getElementById('accessory-bundle-upsell-popup-confirm');
    const continueBtn = document.getElementById('accessory-bundle-upsell-popup-continue');
    const closeBtn = document.getElementById('accessory-bundle-upsell-popup-close');
    if (!overlay) return;

    async function closeAndContinue() {
      overlay.style.display = 'none';
      overlay.setAttribute('aria-hidden', 'true');
      const abCfg = getAccessoryBundleUpsellConfig();
      if (abCfg && abCfg.rememberChoice !== false) {
        const sk = getAccessoryBundleStorageKey(abCfg);
        if (sk) writeStoredAccessoryBundleChoice(sk, { mode: 'decline' });
      }
      const chain = pendingAccessoryChain;
      pendingAccessoryChain = null;
      if (!chain) return;
      await handleAddToCart(
        chain.buyNow,
        true,
        chain.upgradeVariantId || null,
        chain.upgradeVariantTitle || '',
        chain.noUpgradeVariantId || null,
        chain.noUpgradeVariantTitle || '',
        true,
        false,
        null
      );
    }

    async function closeAndUpgrade() {
      const bundleVid = overlay.dataset.upgradeBundleVariantId || null;
      overlay.style.display = 'none';
      overlay.setAttribute('aria-hidden', 'true');
      const abCfg = getAccessoryBundleUpsellConfig();
      if (abCfg && abCfg.rememberChoice !== false) {
        const sk = getAccessoryBundleStorageKey(abCfg);
        if (sk) writeStoredAccessoryBundleChoice(sk, { mode: 'upgrade', variantId: bundleVid ? String(bundleVid) : undefined });
      }
      const chain = pendingAccessoryChain;
      pendingAccessoryChain = null;
      if (!chain) return;
      await handleAddToCart(
        chain.buyNow,
        true,
        chain.upgradeVariantId || null,
        chain.upgradeVariantTitle || '',
        chain.noUpgradeVariantId || null,
        chain.noUpgradeVariantTitle || '',
        true,
        true,
        bundleVid
      );
    }

    if (confirmBtn) confirmBtn.addEventListener('click', closeAndUpgrade);
    if (continueBtn) continueBtn.addEventListener('click', closeAndContinue);
    if (closeBtn) closeBtn.addEventListener('click', closeAndContinue);
  }
  wireAccessoryBundleUpsellPopup();
  void clearAccessoryBundleSessionStorageIfCartEmpty();

  function normalizeBedSizeLabel(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/[-–—]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function compactBedSizeKey(value) {
    return normalizeBedSizeLabel(value).replace(/\s+/g, '');
  }

  function bedSizeLabelsEqual(a, b) {
    const na = normalizeBedSizeLabel(a);
    const nb = normalizeBedSizeLabel(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    return compactBedSizeKey(a) === compactBedSizeKey(b);
  }

  function getSizeLikeOptionIndexes(product) {
    const indexes = [];
    if (product && product.options && product.options.length) {
      product.options.forEach(function (name, idx) {
        const n = String(name || '').toLowerCase();
        if (n.includes('size') || n.includes('dimension') || n.includes('width') || n.includes('mattress')) {
          indexes.push(idx);
        }
      });
    }
    return indexes;
  }

  function variantOptionValuesAll(variant) {
    const out = [];
    if (variant && variant.options && Array.isArray(variant.options)) {
      variant.options.forEach(function (o) {
        if (o != null && String(o).trim() !== '') out.push(String(o).trim());
      });
    }
    if (out.length) return out;
    if (variant.option1) out.push(String(variant.option1).trim());
    if (variant.option2) out.push(String(variant.option2).trim());
    if (variant.option3) out.push(String(variant.option3).trim());
    return out;
  }

  function isVariantSellableForUpsell(v) {
    if (!v) return false;
    if (v.available === true) return true;
    if (v.inventory_policy === 'continue') return true;
    if (v.inventory_management == null && v.available !== false) return true;
    const qty = v.inventory_quantity;
    if (qty != null && parseInt(qty, 10) > 0) return true;
    return false;
  }

  function firstSellableOrFirstVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    const avail = product.variants.find(isVariantSellableForUpsell);
    return avail || product.variants[0];
  }

  /**
   * Match upgrade / no-upgrade product variant to mattress size.
   * Checks every variant option (not only option1), prefers option names containing "Size",
   * case/whitespace tolerant. If matched variant is unavailable, returns product.variants[0].
   * If no match, returns first sellable variant or first variant.
   */
  function getVariantBySize(product, sizeValue) {
    if (!product || !product.variants || !product.variants.length) return null;
    const wantRaw = (sizeValue || '').toString().trim();
    if (!wantRaw) return firstSellableOrFirstVariant(product);

    const preferredIdxs = getSizeLikeOptionIndexes(product);
    const optLen = (product.options && product.options.length) || 0;
    const tryIndexes = preferredIdxs.length
      ? preferredIdxs
      : [0, 1, 2].filter(function (i) { return i < optLen; });

    function valueAtVariantIndex(variant, ix) {
      if (variant.options && variant.options[ix] != null) return String(variant.options[ix]).trim();
      if (ix === 0) return variant.option1 ? String(variant.option1).trim() : '';
      if (ix === 1) return variant.option2 ? String(variant.option2).trim() : '';
      if (ix === 2) return variant.option3 ? String(variant.option3).trim() : '';
      return '';
    }

    function variantMatchesSize(variant) {
      if (tryIndexes.length) {
        for (let ti = 0; ti < tryIndexes.length; ti++) {
          const val = valueAtVariantIndex(variant, tryIndexes[ti]);
          if (val && bedSizeLabelsEqual(val, wantRaw)) return true;
        }
      }
      const allVals = variantOptionValuesAll(variant);
      for (let ai = 0; ai < allVals.length; ai++) {
        if (bedSizeLabelsEqual(allVals[ai], wantRaw)) return true;
      }
      return false;
    }

    const matches = product.variants.filter(variantMatchesSize);
    if (!matches.length) return firstSellableOrFirstVariant(product);

    const sellableMatch = matches.find(isVariantSellableForUpsell);
    if (sellableMatch) return sellableMatch;
    return product.variants[0] || matches[0];
  }

  /**
   * Show foundation upsell popup (adjustable or breeze namespace).
   * @param {boolean} buyNow
   * @param {string} namespace - "upsell-popup" | "breeze-upsell-popup"
   * @returns {Promise<boolean>} true if overlay was shown
   */
  async function showFoundationUpsellPopup(buyNow, namespace) {
    const configEl = upsellPopupGetEl(namespace, 'config');
    const overlay = upsellPopupGetEl(namespace, 'overlay');
    if (!configEl || !overlay) return false;
    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return false;
    }
    const upgradeHandle = config.upgradeProductHandle;
    if (!upgradeHandle) return false;
    const currentSizeValue = getCurrentMattressSizeValue();
    const foundationInput = document.querySelector('input[name="foundation"]:checked');
    const currentFoundationType = (foundationInput && foundationInput.getAttribute('data-foundation-type')) ? String(foundationInput.getAttribute('data-foundation-type')).trim() : '';
    const foundationOptionEl = foundationInput ? (foundationInput.closest && foundationInput.closest('.foundation-option')) : null;
    const foundationHandle = foundationInput ? (foundationInput.getAttribute('data-product-handle') || (foundationOptionEl && foundationOptionEl.getAttribute('data-product-handle'))) : null;
    const foundationVariantId = foundationInput ? (foundationInput.value || foundationInput.getAttribute('data-variant-id')) : null;

    try {
      var includedPriceCents = 0;
      if (foundationHandle && foundationVariantId) {
        try {
          const foundationRes = await fetch('/products/' + foundationHandle + '.js');
          if (foundationRes.ok) {
            const foundationProduct = await foundationRes.json();
            const foundVariant = foundationProduct.variants && foundationProduct.variants.find(function (v) { return String(v.id) === String(foundationVariantId); });
            if (foundVariant && foundVariant.price != null) includedPriceCents = parseInt(foundVariant.price, 10) || 0;
          }
        } catch (e) {}
      }
      if (includedPriceCents === 0 && foundationInput) {
        var attrPrice = parseFloat(String(foundationInput.getAttribute('data-variant-price') || '0').replace(/[^0-9.]/g, '')) || 0;
        if (attrPrice > 0 && attrPrice < 100000) includedPriceCents = Math.round(attrPrice * 100);
        else includedPriceCents = Math.round(attrPrice);
      }

      const res = await fetch('/products/' + upgradeHandle + '.js');
      if (!res.ok) return false;
      const upgradeProduct = await res.json();
      const upgradeVariant = getVariantBySize(upgradeProduct, currentSizeValue) || upgradeProduct.variants[0];
      if (!upgradeVariant) return false;

      var upgradePriceCents = parseInt(upgradeVariant.price, 10) || 0;

      var differenceCents = Math.max(0, upgradePriceCents - includedPriceCents);

      const priceFormatted = formatMoneyFromCents(differenceCents);
      const priceWithPlus = '+' + priceFormatted;

      const subheadEl = upsellPopupGetEl(namespace, 'subhead');
      const priceEl = upsellPopupGetEl(namespace, 'price');
      if (priceEl) priceEl.textContent = priceFormatted;
      if (subheadEl && config.subheadTemplate) {
        subheadEl.textContent = config.subheadTemplate.replace(/\{\{price\}\}|\$X/g, priceFormatted);
      }

      var priceCardEl = upsellPopupGetEl(namespace, 'price-card');
      if (priceCardEl) priceCardEl.textContent = priceWithPlus;

      var compareAtRaw = (config && config.compareAtDifference) ? String(config.compareAtDifference).trim() : '';
      var compareAtEl = upsellPopupGetEl(namespace, 'compare-at');
      if (compareAtEl) {
        compareAtEl.textContent = compareAtRaw ? compareAtRaw + ' ' : '';
        compareAtEl.style.display = compareAtRaw ? '' : 'none';
      }

      var currentNameEl = upsellPopupGetEl(namespace, 'current-name');
      if (currentNameEl) {
        if (config.currentCardTitle && String(config.currentCardTitle).trim() !== '') {
          currentNameEl.textContent = String(config.currentCardTitle).trim();
        } else if (foundationInput) {
          var currentLabel = foundationInput.getAttribute('data-foundation-type') || (currentFoundationVariant && currentFoundationVariant.title) || '';
          currentNameEl.textContent = currentLabel || '—';
        }
      }
      var upgradeNameEl = upsellPopupGetEl(namespace, 'upgrade-name');
      if (upgradeNameEl) {
        if (config.upgradeCardTitle && String(config.upgradeCardTitle).trim() !== '') {
          upgradeNameEl.textContent = String(config.upgradeCardTitle).trim();
        } else {
          upgradeNameEl.textContent = upgradeVariant.title || upgradeProduct.title || '—';
        }
      }

      var currentImgEl = upsellPopupGetEl(namespace, 'current-image');
      if (currentImgEl) {
        var currentImgSrc = (config && config.currentImageUrl) ? config.currentImageUrl : null;
        if (currentImgSrc) {
          currentImgEl.src = currentImgSrc;
          currentImgEl.alt = (currentNameEl && currentNameEl.textContent) ? currentNameEl.textContent : 'Current';
          currentImgEl.style.display = '';
        } else {
          currentImgEl.style.display = 'none';
        }
      }

      var upgradeImgEl = upsellPopupGetEl(namespace, 'upgrade-image');
      if (upgradeImgEl && config.showUpgradeImage !== false) {
        var imgSrc = (config && config.upgradeImageUrl) ? config.upgradeImageUrl : (upgradeVariant.featured_image && (upgradeVariant.featured_image.src || (typeof upgradeVariant.featured_image === 'string' ? upgradeVariant.featured_image : null))) || (upgradeProduct.featured_image && (upgradeProduct.featured_image.src || (typeof upgradeProduct.featured_image === 'string' ? upgradeProduct.featured_image : null))) || (upgradeProduct.images && upgradeProduct.images[0] && (upgradeProduct.images[0].src || (typeof upgradeProduct.images[0] === 'string' ? upgradeProduct.images[0] : null)));
        if (imgSrc) {
          upgradeImgEl.src = imgSrc;
          upgradeImgEl.alt = upgradeProduct.title || '';
          upgradeImgEl.style.display = '';
        } else {
          upgradeImgEl.style.display = 'none';
        }
      }

      var sizeForCart = currentSizeValue || (upgradeVariant.options && upgradeVariant.options[0]) || upgradeVariant.title || '';
      var fullUpgradeTitleForCart = currentFoundationType ? (currentFoundationType + (sizeForCart ? ' / ' + sizeForCart : '')) : (upgradeVariant.options && upgradeVariant.options.length ? upgradeVariant.options.join(' / ') : (upgradeVariant.title || upgradeProduct.title || ''));

      overlay.dataset.upgradeVariantId = String(upgradeVariant.id);
      overlay.dataset.upgradeVariantTitle = fullUpgradeTitleForCart;
      overlay.dataset.noUpgradeVariantId = '';
      overlay.dataset.noUpgradeVariantTitle = '';
      var noUpgradeHandle = (config && config.noUpgradeProductHandle) ? String(config.noUpgradeProductHandle).trim() : '';
      if (noUpgradeHandle && noUpgradeHandle !== '' && noUpgradeHandle !== 'null') {
        try {
          const noUpgradeRes = await fetch('/products/' + noUpgradeHandle + '.js');
          if (noUpgradeRes.ok) {
            const noUpgradeProduct = await noUpgradeRes.json();
            const noUpgradeVariant = getVariantBySize(noUpgradeProduct, currentSizeValue) || (noUpgradeProduct.variants && noUpgradeProduct.variants[0]) || null;
            if (noUpgradeVariant) {
              overlay.dataset.noUpgradeVariantId = String(noUpgradeVariant.id);
              overlay.dataset.noUpgradeVariantTitle = noUpgradeVariant.title || (noUpgradeVariant.options && noUpgradeVariant.options.join(' / ')) || noUpgradeProduct.title || '';
              if (currentNameEl) {
                currentNameEl.textContent = noUpgradeProduct.title + (noUpgradeVariant.title && noUpgradeVariant.title !== 'Default Title' ? ' - ' + noUpgradeVariant.title : '');
              }
            }
          }
        } catch (e) {}
      }
      if (!overlay.dataset.noUpgradeVariantId && currentNameEl && (!config.currentCardTitle || String(config.currentCardTitle).trim() === '') && foundationInput) {
        var currentLabel = foundationInput.getAttribute('data-foundation-type') || (currentFoundationVariant && currentFoundationVariant.title) || '';
        currentNameEl.textContent = currentLabel || '—';
      }

      activeUpsellOverlay = overlay;
      overlay.style.display = 'flex';
      overlay.setAttribute('aria-hidden', 'false');
      return true;
    } catch (e) {
      console.error('Upsell popup error:', e);
      return false;
    }
  }

  async function handleAddToCart(
    buyNow = false,
    skipUpsellPopup = false,
    upgradeVariantId = null,
    upgradeVariantTitle = '',
    noUpgradeVariantId = null,
    noUpgradeVariantTitle = '',
    skipAccessoryBundlePopup = false,
    accessoryBundleUseUpgrade = false,
    accessoryBundleUpgradeVariantId = null
  ) {
    try {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';

      if (!skipUpsellPopup) {
        const foundationInput = document.querySelector('input[name="foundation"]:checked');
        const foundationType = (foundationInput && foundationInput.getAttribute('data-foundation-type')) || selectedFoundationType || '';
        if (foundationInput && foundationType) {
          const adjustableConfigEl = upsellPopupGetEl('upsell-popup', 'config');
          const adjustableOverlay = upsellPopupGetEl('upsell-popup', 'overlay');
          if (adjustableConfigEl && adjustableOverlay) {
            let adjConfig;
            try {
              adjConfig = JSON.parse(adjustableConfigEl.textContent);
            } catch (e) {}
            if (adjConfig && adjConfig.upgradeProductHandle && foundationTriggerMatches(adjConfig, foundationType)) {
              pendingUpsellBuyNow = buyNow;
              const shown = await showFoundationUpsellPopup(buyNow, 'upsell-popup');
              if (shown) return;
            }
          }
          const breezeConfigEl = upsellPopupGetEl('breeze-upsell-popup', 'config');
          const breezeOverlay = upsellPopupGetEl('breeze-upsell-popup', 'overlay');
          if (breezeConfigEl && breezeOverlay) {
            let breezeConfig;
            try {
              breezeConfig = JSON.parse(breezeConfigEl.textContent);
            } catch (e) {}
            if (breezeConfig && breezeConfig.upgradeProductHandle && foundationTriggerMatches(breezeConfig, foundationType)) {
              pendingUpsellBuyNow = buyNow;
              const shown = await showFoundationUpsellPopup(buyNow, 'breeze-upsell-popup');
              if (shown) return;
            }
          }
        }
      }

      /* Accessory bundle popup runs only after adjustable + breeze foundation checks above (when not skipUpsellPopup). */
      if (!skipAccessoryBundlePopup) {
        await clearAccessoryBundleSessionStorageIfCartEmpty();
        const abConfigEl = document.getElementById('accessory-bundle-upsell-popup-config');
        const abOverlay = document.getElementById('accessory-bundle-upsell-popup-overlay');
        const foundationInputAb = document.querySelector('input[name="foundation"]:checked');
        const foundationTypeAb = (foundationInputAb && foundationInputAb.getAttribute('data-foundation-type')) || selectedFoundationType || '';
        const hasAccessoryRows = document.querySelectorAll('.accessory-item[data-accessory-product]').length > 0;
        if (abConfigEl && abOverlay && hasAccessoryRows) {
          let abCfg;
          try {
            abCfg = JSON.parse(abConfigEl.textContent);
          } catch (e) {}
          if (abCfg && abCfg.upgradeProductHandle && accessoryBundleFoundationMatches(abCfg, foundationTypeAb)) {
            const rememberAb = abCfg.rememberChoice !== false;
            const storageKeyAb = rememberAb ? getAccessoryBundleStorageKey(abCfg) : null;
            const storedAb = readStoredAccessoryBundleChoice(storageKeyAb);
            if (storedAb && storedAb.mode === 'upgrade') {
              const rememberedBundleVid =
                storedAb.variantId != null && String(storedAb.variantId).trim() !== ''
                  ? String(storedAb.variantId).trim()
                  : null;
              await handleAddToCart(
                buyNow,
                true,
                upgradeVariantId || null,
                upgradeVariantTitle || '',
                noUpgradeVariantId || null,
                noUpgradeVariantTitle || '',
                true,
                true,
                rememberedBundleVid
              );
              return;
            }
            if (storedAb && storedAb.mode === 'decline') {
              /* fall through — add default accessories, no popup */
            } else {
              pendingAccessoryChain = {
                buyNow: buyNow,
                upgradeVariantId: upgradeVariantId || null,
                upgradeVariantTitle: upgradeVariantTitle || '',
                noUpgradeVariantId: noUpgradeVariantId || null,
                noUpgradeVariantTitle: noUpgradeVariantTitle || ''
              };
              pendingUpsellBuyNow = buyNow;
              const shownAb = await showAccessoryBundleUpsellPopup();
              if (shownAb) return;
            }
          }
        }
      }

      const items = [];
      const bundleGroupId = 'scbg-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);

      if (currentSizeVariant) {
        // Check availability with improved logic
        let isAvailable = false;
        
        if (currentSizeVariant.available === true) {
          isAvailable = true;
        } else if (currentSizeVariant.inventory_policy === 'continue') {
          isAvailable = true;
        } else if (currentSizeVariant.inventory_quantity !== null && currentSizeVariant.inventory_quantity > 0) {
          isAvailable = true;
        } else if (currentSizeVariant.inventory_management === null && currentSizeVariant.available !== false) {
          isAvailable = true;
        }
        
        if (!isAvailable) {
          throw new Error('Selected mattress variant is out of stock');
        }
        
        // Get correct size and firmness values
        const { firmnessIndex, sizeIndex } = detectOptionTypes(currentMattressProduct);
        let sizeValue = 'Standard';
        let firmnessValue = 'Standard';
        
        if (currentSizeVariant.options) {
          if (currentSizeVariant.options[sizeIndex] !== undefined) {
            sizeValue = currentSizeVariant.options[sizeIndex];
          }
          if (currentSizeVariant.options[firmnessIndex] !== undefined) {
            firmnessValue = currentSizeVariant.options[firmnessIndex];
          }
        }
        
        // Add properties to cart for detailed display
        const properties = {};
        
        // Add Foundation Type if foundation is selected - show in main product details (or upgrade title when confirming upsell)
        if (upgradeVariantId && upgradeVariantTitle) {
          properties['Foundation Type'] = upgradeVariantTitle;
        } else if (currentFoundationVariant && currentFoundationVariant.title) {
          properties['Foundation Type'] = currentFoundationVariant.title;
        } else {
          const foundationInput = document.querySelector('input[name="foundation"]:checked');
          if (foundationInput) {
            const foundationVariantTitle = foundationInput.getAttribute('data-variant-title') || '';
            if (foundationVariantTitle) {
              properties['Foundation Type'] = foundationVariantTitle;
            }
          }
        }

        const foundationInputForCart = document.querySelector('input[name="foundation"]:checked');
        const foundationTypeForCart =
          foundationInputForCart && foundationInputForCart.getAttribute('data-foundation-type')
            ? String(foundationInputForCart.getAttribute('data-foundation-type')).trim()
            : '';
        if (foundationTypeForCart) {
          properties['_foundation_type'] = foundationTypeForCart;
        }

        let willIncludeAccessoryBundle = false;
        if (!accessoryBundleUseUpgrade) {
          const accItemsCheck = document.querySelectorAll('.accessory-item[data-accessory-product]');
          for (let abi = 0; abi < accItemsCheck.length; abi++) {
            const accItem = accItemsCheck[abi];
            const ph = accItem.getAttribute('data-accessory-product');
            const vid = resolveAccessoryVariantIdForItem(accItem);
            if (ph && ph !== '' && ph !== 'null' && vid) {
              willIncludeAccessoryBundle = true;
              break;
            }
          }
        }
        if (willIncludeAccessoryBundle) {
          properties['_accessory_bundle_included'] = '1';
        }

        properties['_bundle_group_id'] = bundleGroupId;
        properties['_bundle_parent'] = '1';
        properties['_mattress_size'] = sizeValue;
        if (accessoryBundleUseUpgrade) {
          properties['_accessory_upgraded'] = '1';
        }
        
        items.push({
          id: currentSizeVariant.id,
          quantity: 1,
          properties: properties
        });
      } else {
        throw new Error('No size selected');
      }

      // Add foundation to cart if selected (even if price is $0) — skip when adding upgrade variant instead
      let foundationInput = document.querySelector('input[name="foundation"]:checked');
      if (!isPurchasableFoundationInput(foundationInput)) {
        foundationInput = null;
      }
      if (!foundationInput) {
        const foundationContainer = document.getElementById('foundation-options-container');
        if (foundationContainer) {
          foundationInput = foundationContainer.querySelector(
            '.foundation-option:not(.out-of-stock):not(.size-unavailable) input[name="foundation"]:not([disabled])'
          );
        }
      }
      if (upgradeVariantId) {
        const mainProductNameForFoundation = currentMattressProduct ? currentMattressProduct.title : 'Main Product';
        const upgradeFoundationProperties = { 'Linked to': mainProductNameForFoundation, _bundle_group_id: bundleGroupId, _bundle_locked: '1' };
        items.push({
          id: parseInt(upgradeVariantId, 10),
          quantity: 1,
          properties: upgradeFoundationProperties
        });
      } else if (noUpgradeVariantId) {
        const mainProductNameForFoundation = currentMattressProduct ? currentMattressProduct.title : 'Main Product';
        const noUpgradeProperties = { 'Linked to': mainProductNameForFoundation, _bundle_group_id: bundleGroupId, _bundle_locked: '1' };
        if (noUpgradeVariantTitle) noUpgradeProperties['Variant'] = noUpgradeVariantTitle;
        items.push({
          id: parseInt(noUpgradeVariantId, 10),
          quantity: 1,
          properties: noUpgradeProperties
        });
      } else if (foundationInput && foundationInput.value && isPurchasableFoundationInput(foundationInput)) {
        // data-variant-price may be formatted (e.g. "$79.00") so strip non-numeric chars first.
        const foundationPriceAttr = foundationInput.getAttribute('data-variant-price');
        let foundationPrice = parseFloat(String(foundationPriceAttr || '0').replace(/[^0-9.]/g, '')) || 0;
        // Fallback: sometimes foundation options are rendered dynamically and attribute may be missing.
        if ((!foundationPriceAttr || String(foundationPriceAttr).trim() === '') && foundationInput.closest) {
          const optionEl = foundationInput.closest('.foundation-option') || foundationInput.closest('[class*="foundation-option"]');
          const priceEl = optionEl ? optionEl.querySelector('.foundation-price') : null;
          if (priceEl) {
            const priceText = (priceEl.textContent || '').trim();
            const fallbackPrice = parseFloat(priceText.replace(/[^0-9.]/g, '')) || 0;
            foundationPrice = fallbackPrice;
          }
        }
        if (foundationPrice <= 0) {
          // Do not add foundation to cart when variant price is 0
        } else {
          const isAvailable = foundationInput.getAttribute('data-variant-available') === 'true';
          const inventoryPolicy = foundationInput.getAttribute('data-variant-inventory-policy');
          const inventoryQuantity = foundationInput.getAttribute('data-variant-inventory');
          const inventoryManagement = foundationInput.getAttribute('data-variant-inventory-management');
          
          let isFoundationAvailable = false;
          if (isAvailable === true) {
            isFoundationAvailable = true;
          } else if (inventoryPolicy === 'continue') {
            isFoundationAvailable = true;
          } else if (inventoryQuantity && parseInt(inventoryQuantity) > 0) {
            isFoundationAvailable = true;
          } else if (inventoryManagement === 'null' || inventoryManagement === null) {
            isFoundationAvailable = true;
          }
          
          if (!isFoundationAvailable) {
            throw new Error('Selected foundation variant is out of stock');
          }
          
          const foundationVariantTitle = foundationInput.getAttribute('data-variant-title') || '';
          const mainProductName = currentMattressProduct ? currentMattressProduct.title : 'Main Product';
          const foundationProperties = { 'Linked to': mainProductName, _bundle_group_id: bundleGroupId, _bundle_locked: '1' };
          
          items.push({
            id: parseInt(foundationInput.value),
            quantity: 1,
            properties: foundationProperties
          });
        }
      }

      if (isBeddingOfferToggleActive()) {
        const beddingHeaderPricing = document.getElementById('bedding-offer-header-pricing');
        const beddingHeaderHandle =
          beddingHeaderPricing && beddingHeaderPricing.getAttribute('data-bedding-header-product');
        const beddingCartVid = resolveBeddingHeaderCartVariantId();
        if (beddingHeaderHandle) {
          if (!beddingCartVid) {
            throw new Error(
              'Could not match the bedding offer to your mattress size. Try another size or refresh the page.'
            );
          }
          let beddingProduct = beddingOfferProductCache.get(beddingHeaderHandle);
          if (!beddingProduct) {
            try {
              const beddingRes = await fetch(
                '/products/' + encodeURIComponent(String(beddingHeaderHandle)) + '.js'
              );
              if (beddingRes.ok) {
                beddingProduct = await beddingRes.json();
                beddingOfferProductCache.set(beddingHeaderHandle, beddingProduct);
              }
            } catch (e) {}
          }
          const beddingVariant =
            beddingProduct && beddingProduct.variants
              ? beddingProduct.variants.find(function (v) {
                  return String(v.id) === String(beddingCartVid);
                })
              : null;
          if (!beddingVariant || !isVariantSellableForUpsell(beddingVariant)) {
            throw new Error('Selected bedding offer is out of stock for this size.');
          }
          const beddingMainName = currentMattressProduct ? currentMattressProduct.title : 'Main Product';
          const beddingDisplayTitles = collectBeddingOfferDisplayTitles();
          const beddingCartProperties = {
            'Linked to': beddingMainName,
            _bundle_group_id: bundleGroupId,
            _bundle_locked: '1'
          };
          if (beddingDisplayTitles.length) {
            beddingCartProperties['Bedding Offer Items'] = beddingDisplayTitles.join('|||');
          }
          items.push({
            id: parseInt(beddingCartVid, 10),
            quantity: 1,
            properties: beddingCartProperties
          });
        }
      }

      // Add accessories to cart; resolve variant by main size dropdown (hidden – no separate size selector)
      const accessoryItems = document.querySelectorAll('.accessory-item[data-accessory-product]');
      const accessoryProductsMap = new Map();
      const mainProductName = currentMattressProduct ? currentMattressProduct.title : 'Main Product';
      const currentSizeValue = getCurrentMattressSizeValue();

      let resolvedBundleUpgradeVariantId = accessoryBundleUpgradeVariantId;
      if (accessoryBundleUseUpgrade && !resolvedBundleUpgradeVariantId) {
        const abCfgResolve = getAccessoryBundleUpsellConfig();
        const uh = abCfgResolve && abCfgResolve.upgradeProductHandle;
        if (uh) {
          try {
            const r = await fetch('/products/' + encodeURIComponent(String(uh)) + '.js');
            if (r.ok) {
              const p = await r.json();
              const vv = getVariantBySize(p, getCurrentMattressSizeValue());
              if (vv && vv.id != null) resolvedBundleUpgradeVariantId = String(vv.id);
            }
          } catch (e) {}
        }
      }

      if (accessoryBundleUseUpgrade && resolvedBundleUpgradeVariantId) {
        const bundleProps = {
          'Linked to': mainProductName,
          _bundle_group_id: bundleGroupId,
          _bundle_locked: '1',
          Bundle: 'Upgraded accessory bundle'
        };
        items.push({
          id: parseInt(resolvedBundleUpgradeVariantId, 10),
          quantity: 1,
          properties: bundleProps
        });
      } else if (accessoryBundleUseUpgrade) {
        throw new Error(
          'Could not match the upgraded accessory bundle to your mattress size. Try another size or refresh the page.'
        );
      } else {

      accessoryItems.forEach(accessoryItem => {
        const productHandle = accessoryItem.getAttribute('data-accessory-product');
        const productId = accessoryItem.getAttribute('data-accessory-product-id');
        const variantId = resolveAccessoryVariantIdForItem(accessoryItem);
        const variantTitle = accessoryItem.getAttribute('data-accessory-variant-title') || '';
        const variantOptions = accessoryItem.getAttribute('data-accessory-variant-options') || '';
        
        if (productHandle && productHandle !== '' && productHandle !== 'null' && variantId) {
          if (!accessoryProductsMap.has(productHandle)) {
            accessoryProductsMap.set(productHandle, {
              productId: productId,
              productHandle: productHandle,
              firstVariantId: variantId,
              variantDetails: []
            });
          }
          
          const productData = accessoryProductsMap.get(productHandle);
          productData.firstVariantId = variantId;
          let variantInfo = variantTitle;
          if (variantOptions && variantOptions !== '') {
            variantInfo += ` (${variantOptions})`;
          }
          productData.variantDetails.push(variantInfo);
        }
      });
      
      // Add each accessory product once with variant names only
      accessoryProductsMap.forEach((productData, productHandle) => {
        const accessoryProperties = {};
        
        // Collect variant names: use customizer display title if set, else variant title
        const variantNames = [];
        accessoryItems.forEach(accessoryItem => {
          const itemProductHandle = accessoryItem.getAttribute('data-accessory-product');
          if (itemProductHandle === productHandle) {
            const displayTitle = accessoryItem.getAttribute('data-accessory-display-title') || '';
            const variantTitle = accessoryItem.getAttribute('data-accessory-variant-title') || '';
            const title = (displayTitle && displayTitle.trim()) ? displayTitle.trim() : variantTitle;
            if (title) {
              variantNames.push(title);
            }
          }
        });
        
        // Add variant names as single property
        if (variantNames.length > 0) {
          accessoryProperties['Included Items'] = variantNames.join(', ');
        }
        
        // Add link to main product (like foundation)
        accessoryProperties['Linked to'] = mainProductName;
        accessoryProperties['_bundle_group_id'] = bundleGroupId;
        accessoryProperties['_bundle_locked'] = '1';
        
        items.push({
          id: parseInt(productData.firstVariantId),
          quantity: 1,
          properties: accessoryProperties
        });
      });

      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      if (buyNow) {
        window.location.href = '/checkout';
      } else {
        window.location.href = '/cart';
      }

    } catch (err) {
      console.error('Cart add error:', err);
      alert('Error adding item to cart: ' + err.message);
    } finally {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = 'Add to Cart';
    }
  }

  // Helper functions
  function stripHtml(html) {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  function truncateWords(text, wordCount) {
    const words = text.trim().split(/\s+/);
    if (words.length <= wordCount) return text;
    return words.slice(0, wordCount).join(' ') + '...';
  }

  function formatMoney(cents) {
    if (cents == null || cents === '') return '$0.00';
    let num = cents;
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/[^0-9.-]/g, ''));
    }
    if (isNaN(num)) return '$0.00';

    const dollars = Number(num) / 100;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(dollars);
  }

  /** Format a value that is always in cents (e.g. price difference) as currency. */
  function formatMoneyFromCents(cents) {
    if (cents == null || isNaN(cents)) return '$0.00';
    const num = typeof cents === 'string' ? parseFloat(cents.replace(/[^0-9.-]/g, '')) : Number(cents);
    if (isNaN(num)) return '$0.00';
    const dollars = num / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.max(0, dollars));
  }

  // Initialize custom dropdown
  function initCustomDropdown() {
    const nativeSelect = document.getElementById('size-select');
    const customDropdown = document.querySelector('#custom-product-container .custom-dropdown');
    const customSelected = document.querySelector('#custom-product-container .custom-dropdown-selected');
    const customOptions = document.querySelector('#custom-product-container .custom-dropdown-options');
    
    if (!nativeSelect || !customDropdown || !customSelected || !customOptions) {
      console.error('Custom dropdown elements not found');
      return;
    }
    
    const selectedSize = customSelected.querySelector('.selected-size');
    const selectedPrice = customSelected.querySelector('.selected-price');

    function buildCustomOptions() {
      customOptions.innerHTML = '';
      
      if (nativeSelect.options.length === 0) {
        if (selectedSize) selectedSize.textContent = 'Select Size';
        if (selectedPrice) selectedPrice.textContent = '';
        return;
      }
      
      Array.from(nativeSelect.options).forEach((option, index) => {
        const optionDiv = document.createElement('div');
        optionDiv.className = 'custom-dropdown-option';
        optionDiv.dataset.value = option.value;
        optionDiv.dataset.index = index;
        
        const isAvailable = option.dataset.available === 'true';
        const size = option.dataset.size || option.text.split(' - ')[0].trim();
        const price = option.dataset.price || '';
        const availabilityText = option.dataset.availabilityText || '';
        const effectiveAvailable = isAvailable;

        let displaySize = size;
        let displayPrice = price;
        
        if (!isAvailable) {
          optionDiv.classList.add('disabled', 'out-of-stock');
          displayPrice = 'Out of Stock';
        } else if (availabilityText && availabilityText !== 'In Stock') {
          displayPrice = availabilityText;
        }

        const sizeSpan = document.createElement('span');
        sizeSpan.className = 'option-size';
        // Add pipe separator between size and price
        if (effectiveAvailable && displayPrice && displayPrice !== 'Out of Stock' && displayPrice !== availabilityText) {
          sizeSpan.textContent = `${displaySize} | ${displayPrice}`;
        } else {
          sizeSpan.textContent = displaySize;
        }
        
        const priceSpan = document.createElement('span');
        priceSpan.className = `option-price ${!effectiveAvailable ? 'out-of-stock' : ''}`;
        // Price is now in sizeSpan, so hide priceSpan or show empty
        if (!isAvailable || (availabilityText && availabilityText !== 'In Stock')) {
          priceSpan.textContent = displayPrice;
        } else {
          priceSpan.textContent = '';
        }

        const rowMain = document.createElement('div');
        rowMain.className = 'custom-dropdown-option-main';
        rowMain.appendChild(sizeSpan);
        optionDiv.appendChild(rowMain);
        optionDiv.appendChild(priceSpan);

        if (effectiveAvailable) {
          optionDiv.addEventListener('click', () => {
            selectOption(index);
          });
        } else {
          optionDiv.style.opacity = '0.5';
          optionDiv.style.cursor = 'not-allowed';
        }

        customOptions.appendChild(optionDiv);
      });

      const selIdx = nativeSelect.selectedIndex;
      const selOpt = nativeSelect.options[selIdx];
      if (selOpt && selOpt.disabled) {
        for (let i = 0; i < nativeSelect.options.length; i++) {
          const o = nativeSelect.options[i];
          if (o.value && !o.disabled) {
            nativeSelect.selectedIndex = i;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      updateSelected();
    }

    function updateSelected() {
      const selectedOption = nativeSelect.options[nativeSelect.selectedIndex];
      if (selectedOption && selectedOption.value) {
        const isAvailable = selectedOption.dataset.available === 'true';
        const size = selectedOption.dataset.size || selectedOption.text.split(' - ')[0].trim();
        const price = selectedOption.dataset.price || '';
        const availabilityText = selectedOption.dataset.availabilityText || '';
        
        // Get variant name from current firmness variant
        let variantName = '';
        if (currentFirmnessVariant && currentMattressProduct) {
          const { firmnessIndex } = detectOptionTypes(currentMattressProduct);
          if (currentFirmnessVariant.options && currentFirmnessVariant.options[firmnessIndex] !== undefined) {
            variantName = currentFirmnessVariant.options[firmnessIndex];
          } else if (currentFirmnessVariant.option1 && firmnessIndex === 0) {
            variantName = currentFirmnessVariant.option1;
          } else if (currentFirmnessVariant.option2 && firmnessIndex === 1) {
            variantName = currentFirmnessVariant.option2;
          } else if (currentFirmnessVariant.option3 && firmnessIndex === 2) {
            variantName = currentFirmnessVariant.option3;
          }
        }
        
        if (selectedSize) {
          // Show size variant name with pipe separator and price
          if (isAvailable && price) {
            selectedSize.textContent = `${size} | ${price}`;
          } else {
            selectedSize.textContent = size;
          }
          selectedSize.classList.toggle('out-of-stock', !isAvailable);
        }
        
        if (selectedPrice) {
          if (!isAvailable) {
            selectedPrice.textContent = 'Out of Stock';
            selectedPrice.classList.add('out-of-stock');
          } else if (availabilityText && availabilityText !== 'In Stock') {
            selectedPrice.textContent = availabilityText;
            selectedPrice.classList.remove('out-of-stock');
          } else {
            // Price is now shown in selectedSize with pipe, so hide selectedPrice or show empty
            selectedPrice.textContent = '';
            selectedPrice.classList.remove('out-of-stock');
          }
        }
      } else {
        if (selectedSize) {
          selectedSize.textContent = 'Select Size';
          selectedSize.classList.remove('out-of-stock');
        }
        if (selectedPrice) {
          selectedPrice.textContent = '';
          selectedPrice.classList.remove('out-of-stock');
        }
      }
    }

    function closeDropdown() {
      customDropdown.classList.remove('open');
      customOptions.style.position = '';
      customOptions.style.top = '';
      customOptions.style.left = '';
      customOptions.style.right = '';
      customOptions.style.width = '';
      customOptions.style.zIndex = '';
    }

    function selectOption(index) {
      const option = nativeSelect.options[index];
      if (!option || option.disabled) return;
      
      nativeSelect.selectedIndex = index;
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      
      updateSelected();
      closeDropdown();
    }

    customSelected.addEventListener('click', (e) => {
      e.stopPropagation();
      customDropdown.classList.toggle('open');
      if (customDropdown.classList.contains('open') && window.matchMedia('(max-width: 768px)').matches) {
        var rect = customSelected.getBoundingClientRect();
        customOptions.style.position = 'fixed';
        customOptions.style.top = rect.bottom + 'px';
        customOptions.style.left = rect.left + 'px';
        customOptions.style.right = 'auto';
        customOptions.style.width = rect.width + 'px';
        customOptions.style.zIndex = '9999';
      } else {
        customOptions.style.position = '';
        customOptions.style.top = '';
        customOptions.style.left = '';
        customOptions.style.right = '';
        customOptions.style.width = '';
        customOptions.style.zIndex = '';
      }
    });

    document.addEventListener('click', (e) => {
      if (!customDropdown.contains(e.target)) {
        closeDropdown();
      }
    });

    nativeSelect.addEventListener('change', updateSelected);

    buildCustomOptions();
    window.rebuildCustomDropdown = buildCustomOptions;
  }

  // Get product handle from URL
  const currentPath = window.location.pathname;
  const urlProductHandle = currentPath.replace('/products/', '').replace(/\/$/, '');
  
  let activeTab = null;
  
  // Try to find tab matching URL product handle
  if (urlProductHandle && urlProductHandle !== '') {
    // Find tab by data-tab or data-product-handle attribute
    const allTabs = document.querySelectorAll('#custom-product-container .tab');
    for (let tab of allTabs) {
      const tabHandle = tab.getAttribute('data-tab') || tab.getAttribute('data-product-handle');
      if (tabHandle === urlProductHandle) {
        activeTab = tab;
        break;
      }
    }
  }
  
  // If no matching tab found, use first active tab or first tab
  if (!activeTab) {
    activeTab = document.querySelector('#custom-product-container .tab.active');
    if (!activeTab) {
      activeTab = document.querySelector('#custom-product-container .tab');
    }
  }
  
  if (activeTab) {
    // Remove active class from all tabs first
    document.querySelectorAll('#custom-product-container .tab').forEach(t => {
      t.classList.remove('active');
      // Reset border to default 1px for inactive tabs
      t.style.borderWidth = '1px';
      t.style.borderStyle = 'solid';
    });
    
    // Add active class to selected tab
    activeTab.classList.add('active');
    
    // Set 2px solid border with tab color for active tab
    const tabColor = activeTab.getAttribute('data-tab-color');
    if (tabColor && tabColor.trim() !== '' && tabColor !== 'null' && tabColor !== '#') {
      activeTab.style.borderWidth = '2px';
      activeTab.style.borderStyle = 'solid';
      activeTab.style.borderColor = tabColor.trim();
    }
    
    // Update URL for active tab (in case it was different)
    const initialProductHandle = activeTab.getAttribute('data-tab') || activeTab.getAttribute('data-product-handle');
    if (initialProductHandle) {
      updateProductUrlDisplay(initialProductHandle);
    }
    
    // Set initial indicator color for active tab
    const initialTabColor = activeTab.getAttribute('data-tab-color');
    const initialIndicator = activeTab.querySelector('.tab-indicator');
    if (initialIndicator && initialTabColor && initialTabColor.trim() !== '' && initialTabColor !== 'null' && initialTabColor !== '#') {
      initialIndicator.style.backgroundColor = initialTabColor.trim();
      initialIndicator.style.borderColor = initialTabColor.trim();
    }
    
    setTimeout(() => {
      activeTab.click();
      
      setTimeout(() => {
        initCustomDropdown();
        updateTitleLastWordColor();
        updateButtonStates();
      }, 800);
    }, 100);
  } else {
    if (originalProduct && originalProduct.id) {
      updateMattressProduct(originalProduct);
      setTimeout(() => {
        initCustomDropdown();
        updateTitleLastWordColor();
        updateButtonStates();
      }, 500);
    }
  }



function initPDPColorHandler() {
  // Find all elements that should change colors
  const bgElements = document.querySelectorAll('.pdp-bg-color, [data-section="pdp-color-change"], .pdp-color-enabled');
  const textElements = document.querySelectorAll('.pdp-text-color, [data-pdp-color="text"]');
  const borderElements = document.querySelectorAll('.pdp-border-color, [data-pdp-color="border"]');
  const mattressTabs = document.querySelectorAll('.mattress-type-tabs .tab');
  
  // If no tabs found, exit
  if (mattressTabs.length === 0) {
    return;
  }
  
  // Function to calculate luminance and determine if color is dark
  function isColorDark(color) {
    // Convert color to RGB
    let r, g, b;
    
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else {
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      }
    }
    // Handle rgb/rgba colors
    else if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        r = parseInt(matches[0]);
        g = parseInt(matches[1]);
        b = parseInt(matches[2]);
      }
    }
    // If we couldn't parse, default to light (black text)
    else {
      return false;
    }
    
    // Calculate relative luminance using WCAG formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // If luminance is less than 0.5, it's a dark color
    return luminance < 0.5;
  }

  function syncPromoBannerTabColor(tabElement) {
    const banner = document.querySelector('#custom-product-container [data-promo-banner]');
    if (!banner || !tabElement) return;
    let color = tabElement.getAttribute('data-tab-color');
    if (!color) return;
    color = String(color).trim();
    if (!color || color === 'null' || color === '#') return;

    banner.style.backgroundColor = color;
    banner.style.color = isColorDark(color) ? '#ffffff' : '#1a2744';

    const accent = banner.getAttribute('data-accent-color');
    if (accent) {
      banner.style.setProperty('--pdp-promo-accent', accent);
      banner.querySelectorAll('.pdp-promo-banner__eyebrow').forEach(function (el) {
        el.style.color = accent;
      });
    }
  }
  
  // Function to update colors
  function updatePDPColor(tabElement) {
    // Remove active class from all tabs and reset border
    document.querySelectorAll('.mattress-type-tabs .tab').forEach(tab => {
      tab.classList.remove('active');
      // Reset border to default 1px
      tab.style.borderWidth = '1px';
      tab.style.borderStyle = 'solid';
    });
    
    // Add active class to clicked tab
    tabElement.classList.add('active');
    
    // Get the color from the clicked tab
    let color = tabElement.getAttribute('data-tab-color');
    
    // Set 2px solid border with tab color for active tab
    if (color && color.trim() !== '' && color !== 'null') {
      tabElement.style.borderWidth = '2px';
      tabElement.style.borderStyle = 'solid';
      tabElement.style.borderColor = color.trim();
    }
    
    // Clean and validate color
    if (color) {
      color = color.trim();
      const isValidColor = color && color !== '' && color !== 'null' && color !== '#';
      
      if (isValidColor) {
        // Determine if the background is dark
        const isDark = isColorDark(color);
        const textColor = isDark ? '#ffffff' : '#000000';
        
        // Apply background color AND auto text color to pdp-color-enabled elements
        document.querySelectorAll('.pdp-bg-color, [data-section="pdp-color-change"], .pdp-color-enabled').forEach(element => {
          // Check if element has a gradient background
          const currentBg = window.getComputedStyle(element).backgroundImage;
          
          if (currentBg && currentBg !== 'none' && currentBg.includes('gradient')) {
            // Element has a gradient, preserve it but change the base color
            if (!element.dataset.originalGradient) {
              element.dataset.originalGradient = currentBg;
            }
            element.style.background = `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), ${color}`;
          } else {
            // No gradient, just apply solid color
            element.style.backgroundColor = color;
          }
          
          element.style.setProperty('--pdp-bg-color', color);
          
          // AUTO-APPLY TEXT COLOR to all text within pdp-color-enabled elements
          element.style.color = textColor;
          element.style.setProperty('--pdp-text-color', textColor);
          
          // Also apply to all child text elements
          const allTextElements = element.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, a, li, td, th, div');
          allTextElements.forEach(textEl => {
            // Only apply if the element doesn't have an explicit color override
            const computedColor = window.getComputedStyle(textEl).color;
            const hasInlineColor = textEl.style.color && textEl.style.color !== '';
            
            if (!hasInlineColor) {
              textEl.style.color = textColor;
            }
          });
          
          // Apply to SVG elements (fill and stroke)
          const allSvgElements = element.querySelectorAll('svg, svg path, svg circle, svg rect, svg polygon, svg line, svg polyline, svg ellipse');
          allSvgElements.forEach(svgEl => {
            // Only apply if the element doesn't have an explicit fill/stroke override
            const hasInlineFill = svgEl.style.fill && svgEl.style.fill !== '' && svgEl.style.fill !== 'none';
            const hasInlineStroke = svgEl.style.stroke && svgEl.style.stroke !== '' && svgEl.style.stroke !== 'none';
            const currentFill = svgEl.getAttribute('fill');
            const currentStroke = svgEl.getAttribute('stroke');
            
            // Apply fill color if element has fill (not 'none')
            if (!hasInlineFill && currentFill !== 'none') {
              svgEl.style.fill = textColor;
            }
            
            // Apply stroke color if element has stroke (not 'none')
            if (!hasInlineStroke && currentStroke && currentStroke !== 'none') {
              svgEl.style.stroke = textColor;
            }
          });
        });

        syncPromoBannerTabColor(tabElement);
        
        // Apply text color to explicit text color elements (.pdp-text-color)
        let pdpTextColor = color; // Tab color for title etc.
        const tabIndex = Array.from(document.querySelectorAll('.mattress-type-tabs .tab')).indexOf(tabElement);
        
        document.querySelectorAll('.pdp-text-color, [data-pdp-color="text"]').forEach(element => {
          // Check if this is the last word of the title
          if (element.classList.contains('title-last-word')) {
            // Apply the determined text color directly to the last word
            element.style.color = pdpTextColor;
            element.style.setProperty('--pdp-text-color', pdpTextColor);
          } else {
            // For other text color elements, apply the determined color
            element.style.color = pdpTextColor;
            element.style.setProperty('--pdp-text-color', pdpTextColor);
          }
        });
        
        // Apply border color
        document.querySelectorAll('.pdp-border-color, [data-pdp-color="border"]').forEach(element => {
          element.style.borderColor = color;
          element.style.setProperty('--pdp-border-color', color);
        });

        // Preserve progress bar width from metafield (data-customizer-progress) after color apply
        document.querySelectorAll('.performance-blocks__progress-fill[data-customizer-progress]').forEach(function(el) {
          const w = el.getAttribute('data-customizer-progress');
          if (w) {
            el.style.animation = 'none';
            el.style.setProperty('--progress-width', w, 'important');
            el.style.setProperty('width', w, 'important');
          }
        });

        // Second mattress type tab: only that tab gets text color #647360 when 2nd or 3rd option is selected
        const allTabs = document.querySelectorAll('.mattress-type-tabs .tab');
        const secondTab = allTabs[1];
        if (secondTab) {
          if (tabIndex === 1 || tabIndex === 2) {
            secondTab.style.color = '#647360';
          } else {
            secondTab.style.color = '';
          }
        }
        
      } else {
        clearAllColors();
      }
    } else {
      clearAllColors();
    }
  }
  
  // Function to clear all applied colors
  function clearAllColors() {
    document.querySelectorAll('.pdp-bg-color, [data-section="pdp-color-change"], .pdp-color-enabled').forEach(element => {
      element.style.backgroundColor = '';
      element.style.background = '';
      element.style.color = '';
      element.style.removeProperty('--pdp-bg-color');
      element.style.removeProperty('--pdp-text-color');
      
      // Clear child text elements
      const allTextElements = element.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, a, li, td, th, div');
      allTextElements.forEach(textEl => {
        textEl.style.color = '';
      });
      
      // Clear SVG elements
      const allSvgElements = element.querySelectorAll('svg, svg path, svg circle, svg rect, svg polygon, svg line, svg polyline, svg ellipse');
      allSvgElements.forEach(svgEl => {
        svgEl.style.fill = '';
        svgEl.style.stroke = '';
      });
    });
    
    document.querySelectorAll('.pdp-text-color, [data-pdp-color="text"]').forEach(element => {
      element.style.color = '';
      element.style.removeProperty('--pdp-text-color');
    });
    
    document.querySelectorAll('.pdp-border-color, [data-pdp-color="border"]').forEach(element => {
      element.style.borderColor = '';
      element.style.removeProperty('--pdp-border-color');
    });
    const secondTab = document.querySelector('.mattress-type-tabs .tab:nth-child(2)');
    if (secondTab) secondTab.style.color = '';
  }
  
  // Add click events to tabs
  mattressTabs.forEach(tab => {
    // Remove existing listeners to avoid duplicates
    tab.removeEventListener('click', tab.clickHandler);
    
    // Create new handler
    tab.clickHandler = function(e) {
      e.preventDefault();
      updatePDPColor(this);
    };
    
    tab.addEventListener('click', tab.clickHandler);
  });
  
  // Set initial color
  const activeTab = document.querySelector('.mattress-type-tabs .tab.active');
  if (activeTab) {
    // Set initial indicator color
    const tabColor = activeTab.getAttribute('data-tab-color');
    const indicator = activeTab.querySelector('.tab-indicator');
    if (indicator && tabColor && tabColor.trim() !== '' && tabColor !== 'null' && tabColor !== '#') {
      indicator.style.backgroundColor = tabColor.trim();
      indicator.style.borderColor = tabColor.trim();
    }
    // Set initial border for active tab
    if (tabColor && tabColor.trim() !== '' && tabColor !== 'null') {
      activeTab.style.borderWidth = '2px';
      activeTab.style.borderStyle = 'solid';
      activeTab.style.borderColor = tabColor.trim();
    }
    updatePDPColor(activeTab);
  } else if (mattressTabs.length > 0) {
    mattressTabs[0].classList.add('active');
    // Set initial indicator color for first tab
    const firstTabColor = mattressTabs[0].getAttribute('data-tab-color');
    const firstIndicator = mattressTabs[0].querySelector('.tab-indicator');
    if (firstIndicator && firstTabColor && firstTabColor.trim() !== '' && firstTabColor !== 'null' && firstTabColor !== '#') {
      firstIndicator.style.backgroundColor = firstTabColor.trim();
      firstIndicator.style.borderColor = firstTabColor.trim();
    }
    updatePDPColor(mattressTabs[0]);
  }
  
  mattressTabs.forEach((tab, index) => {
    const color = tab.getAttribute('data-tab-color');
    const hasColor = color && color.trim() && color.trim() !== 'null';
  });
}

function initPromoCountdownBanners() {
  const banners = document.querySelectorAll('#custom-product-container [data-promo-banner]');
  banners.forEach(function (banner) {
    if (banner.dataset.promoCountdownBound === '1') return;

    const daysEl = banner.querySelector('[data-promo-days]');
    const hoursEl = banner.querySelector('[data-promo-hours]');
    const minutesEl = banner.querySelector('[data-promo-minutes]');
    const secondsEl = banner.querySelector('[data-promo-seconds]');
    if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

    let getRemainingMs;

    if (banner.dataset.countdownLoop === 'true') {
      const loopDays = parseInt(banner.dataset.loopDays, 10) || 0;
      const loopHours = parseInt(banner.dataset.loopHours, 10) || 0;
      const cycleMs = loopDays * 86400000 + loopHours * 3600000;
      if (cycleMs <= 0) return;

      const loopStartStr = banner.getAttribute('data-loop-start');
      const syncedAnchor = loopStartStr ? new Date(loopStartStr.replace(' ', 'T')) : null;
      const storageKey =
        'pdp-promo-loop-' +
        (banner.getAttribute('data-promo-banner-id') || banner.getAttribute('data-section') || 'default') +
        '-' +
        cycleMs;

      if (syncedAnchor && !isNaN(syncedAnchor.getTime())) {
        getRemainingMs = function () {
          const elapsed = ((Date.now() - syncedAnchor.getTime()) % cycleMs + cycleMs) % cycleMs;
          return elapsed === 0 ? cycleMs : cycleMs - elapsed;
        };
      } else {
        getRemainingMs = function () {
          let anchor = parseInt(sessionStorage.getItem(storageKey), 10);
          if (!anchor || isNaN(anchor)) {
            anchor = Date.now();
            sessionStorage.setItem(storageKey, String(anchor));
          }
          let elapsed = Date.now() - anchor;
          if (elapsed >= cycleMs) {
            anchor = Date.now();
            sessionStorage.setItem(storageKey, String(anchor));
            elapsed = 0;
          }
          return cycleMs - elapsed;
        };
      }
    } else {
      const endStr = banner.getAttribute('data-countdown-end');
      if (!endStr) return;

      const end = new Date(endStr.replace(' ', 'T'));
      if (isNaN(end.getTime())) return;

      getRemainingMs = function () {
        return Math.max(0, end.getTime() - Date.now());
      };
    }

    banner.dataset.promoCountdownBound = '1';

    function pad2(n) {
      return String(n).padStart(2, '0');
    }

    function tick() {
      let diff = getRemainingMs();
      const days = Math.floor(diff / 86400000);
      diff -= days * 86400000;
      const hours = Math.floor(diff / 3600000);
      diff -= hours * 3600000;
      const minutes = Math.floor(diff / 60000);
      diff -= minutes * 60000;
      const seconds = Math.floor(diff / 1000);

      daysEl.textContent = pad2(days);
      hoursEl.textContent = pad2(hours);
      minutesEl.textContent = pad2(minutes);
      secondsEl.textContent = pad2(seconds);
    }

    tick();
    window.setInterval(tick, 1000);
  });
}

function initBeddingOfferToggle() {
  const wrap = document.getElementById('bedding-offer-toggle-wrap');
  const input = document.getElementById('bedding-offer-toggle-input');
  if (!wrap || !input) return;

  function syncToggleState() {
    const isOn = input.checked;
    wrap.classList.toggle('is-on', isOn);
    wrap.classList.toggle('is-off', !isOn);
    const labelEl = wrap.querySelector('.bedding-offer-toggle-label');
    if (labelEl) {
      const offText = wrap.getAttribute('data-label-off') || '';
      const onText = wrap.getAttribute('data-label-on') || '';
      labelEl.textContent = isOn ? onText : offText;
    }
    if (typeof window.syncBeddingWorthMessageFromToggle === 'function') {
      window.syncBeddingWorthMessageFromToggle();
    }
  }

  if (!input.dataset.beddingToggleBound) {
    input.dataset.beddingToggleBound = '1';
    input.addEventListener('change', syncToggleState);
  }
  syncToggleState();
  if (typeof window.syncBeddingOfferWithMattressSize === 'function') {
    window.syncBeddingOfferWithMattressSize();
  }
}

// Initialize - with multiple attempts for dynamic content
function initializeWithRetry() {
  initPDPColorHandler();
  initBeddingOfferToggle();
  initPromoCountdownBanners();

  // If no tabs found, try again (for dynamically loaded content)
  const mattressTabs = document.querySelectorAll('.mattress-type-tabs .tab');
  if (mattressTabs.length === 0) {
    setTimeout(initPDPColorHandler, 500);
    setTimeout(initPDPColorHandler, 1000);
  }
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWithRetry);
} else {
  initializeWithRetry();
}

// Re-initialize for dynamic content
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', initializeWithRetry);
  document.addEventListener('shopify:section:unload', initializeWithRetry);
}

// Handle variant changes
document.addEventListener('variant:change', function() {
  setTimeout(initPDPColorHandler, 100);
})
});